import type { CandyArt } from './candy-art';

/**
 * Cleanlist's candy drawings, keyed by icon name (the names in @/lib/icons) and
 * by a few narrower names call sites ask for with `candy=` (account, approve,
 * wait, seq-email...). Plain draws Lucide icons instead (lucide-icons.ts).
 */
export const CANDY_ART: CandyArt = {
  account: [
    ['circle', { cx: 23, cy: 23, r: 18.5 }, '#19B6EC'],
    [
      'path',
      {
        d: 'M10.6 36.6C12.6 31 17.4 27.6 23 27.6S33.4 31 35.4 36.6C32.2 39.8 27.8 41.5 23 41.5S13.8 39.8 10.6 36.6z',
      },
      '#FFFFFF',
    ],
    ['circle', { cx: 23, cy: 18.6, r: 6.4 }, '#FFFFFF'],
    ['circle', { cx: 37.5, cy: 37.5, r: 7 }, ['--cl-base', '#252A57']],
    ['path', { d: 'M34.6 37.6l2.1 2.1 3.8-4.2' }, '#7FDBFF', 'line'],
  ],
  'address-book': [
    ['rect', { x: 32, y: 10.5, width: 10, height: 7.5, rx: 2.6 }, '#FFD23F'],
    ['rect', { x: 32, y: 20.25, width: 10, height: 7.5, rx: 2.6 }, '#FF7AB0'],
    ['rect', { x: 32, y: 30, width: 10, height: 7.5, rx: 2.6 }, '#FFD23F'],
    ['rect', { x: 7, y: 6, width: 30, height: 36, rx: 5.5 }, '#27B24A'],
    [
      'path',
      {
        d: 'M12.5 6H15v36h-2.5A5.5 5.5 0 0 1 7 36.5v-25A5.5 5.5 0 0 1 12.5 6z',
      },
      '#178A35',
    ],
    ['circle', { cx: 26, cy: 18.6, r: 4.8 }, ['--cl-oncard', '#FFFFFF']],
    [
      'path',
      {
        d: 'M18 33.6c0-4.7 3.6-7.8 8-7.8s8 3.1 8 7.8a1.4 1.4 0 0 1-1.4 1.4H19.4a1.4 1.4 0 0 1-1.4-1.4z',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
  ],
  'address-card': [
    ['rect', { x: 3, y: 8, width: 36, height: 28, rx: 6 }, '#6FB6FF'],
    ['circle', { cx: 14, cy: 18, r: 4.5 }, '#FFFFFF'],
    [
      'path',
      { d: 'M6.8 31.5c0-4.3 3.2-7.2 7.2-7.2s7.2 2.9 7.2 7.2z' },
      '#FFFFFF',
    ],
    ['rect', { x: 24.5, y: 15, width: 10, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 24.5, y: 21, width: 6.5, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 27, y: 25, width: 18, height: 18, rx: 5 }, '#0A66C2'],
    ['circle', { cx: 32.2, cy: 30, r: 1.6 }, '#FFFFFF'],
    ['path', { d: 'M32.2 33.8v4.8' }, '#FFFFFF', 'line'],
    [
      'path',
      { d: 'M36.2 38.6v-4.8m0 2.2c0-1.4 1-2.3 2.2-2.3s2.2.9 2.2 2.3v2.6' },
      '#FFFFFF',
      'line',
    ],
  ],
  'align-left': [
    ['rect', { x: 5.4, y: 7.9, width: 25.2, height: 5.2, rx: 2.6 }, '#7C5CFF'],
    ['rect', { x: 5.4, y: 16.9, width: 37.2, height: 5.2, rx: 2.6 }, '#A893FF'],
    ['rect', { x: 5.4, y: 25.9, width: 25.2, height: 5.2, rx: 2.6 }, '#7C5CFF'],
    ['rect', { x: 5.4, y: 34.9, width: 37.2, height: 5.2, rx: 2.6 }, '#A893FF'],
    ['path', { d: 'M9 9.4H13', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  'angles-up-down': [
    [
      'rect',
      {
        x: 7.87,
        y: 9.25,
        width: 21.27,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(-43.12 18.5 12.35)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 18.87,
        y: 9.25,
        width: 21.27,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(43.12 29.5 12.35)',
      },
      '#6C63FF',
    ],
    [
      'rect',
      {
        x: 7.87,
        y: 32.55,
        width: 21.27,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(43.12 18.5 35.65)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 18.87,
        y: 32.55,
        width: 21.27,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(-43.12 29.5 35.65)',
      },
      '#6C63FF',
    ],
  ],
  'api-keys': [
    [
      'path',
      {
        d: 'M30 24h11.5M35.5 24v6.5M40.5 24v4.5',
        transform: 'rotate(-45 24 24)',
      },
      '#5B2FD6',
      'thick',
    ],
    [
      'rect',
      {
        x: 18,
        y: 21,
        width: 23,
        height: 6,
        rx: 3,
        transform: 'rotate(-45 24 24)',
      },
      '#B79BFF',
    ],
    [
      'circle',
      { cx: 14.5, cy: 24, r: 10.5, transform: 'rotate(-45 24 24)' },
      '#8B5CF6',
    ],
    [
      'path',
      { d: 'M15.4 27.3l-3.4 3.4 3.4 3.4M19.2 27.3l3.4 3.4-3.4 3.4' },
      '#FFFFFF',
      'line',
    ],
  ],
  appearance: [
    [
      'path',
      {
        d: 'M24 6C34.5 6 43 13.4 43 22.6c0 6.3-4.3 9.4-8.6 8.4-2.9-.7-5 1.5-4.1 4.3 1.1 3.7-1.2 7.2-5.8 7.2C13.6 42.5 5 34.2 5 23.8 5 13.8 13.5 6 24 6z',
      },
      '#D946EF',
    ],
    ['circle', { cx: 14.6, cy: 19.4, r: 3.7 }, '#FFD23F'],
    ['circle', { cx: 23.6, cy: 13.4, r: 3.7 }, '#3FD8FF'],
    ['circle', { cx: 33.2, cy: 16.6, r: 3.7 }, '#FFFFFF'],
    ['circle', { cx: 14.4, cy: 30, r: 3.7 }, '#FFFFFF'],
  ],
  approve: [
    ['rect', { x: 4.5, y: 20, width: 9, height: 23, rx: 3 }, '#0A8A5F'],
    [
      'rect',
      {
        x: 18,
        y: 3.5,
        width: 11,
        height: 23,
        rx: 5.5,
        transform: 'rotate(12 23.5 15)',
      },
      '#14B87F',
    ],
    ['rect', { x: 16, y: 18.5, width: 27.5, height: 24.5, rx: 6.5 }, '#14B87F'],
    ['path', { d: 'M31.5 24.6H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 30.7H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 36.8H41' }, '#FFFFFF', 'line'],
    ['circle', { cx: 9, cy: 37.5, r: 1.7 }, '#8EEBC6'],
  ],
  'arrow-down': [
    ['path', { d: 'M24 9.5L24 36' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M13 28.5L22.23 37.73Q24 39.5 25.77 37.73L35 28.5' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrow-down-a-z': [
    ['path', { d: 'M13.5 7V38' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M6.2 33.7L11.73 39.23Q13.5 41 15.27 39.23L20.8 33.7' },
      '#6C63FF',
      'thick',
    ],
    [
      'path',
      { d: 'M27 19.5L31.5 6.5H36.5L41 19.5M29 14.5H39' },
      '#FF6FAE',
      'thick',
    ],
    ['path', { d: 'M27.5 28.5H40.5L27.5 41.5H40.5' }, '#FFB020', 'thick'],
  ],
  'arrow-down-arrow-up': [
    ['path', { d: 'M15 8.5V37' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M8 33.5L13.23 38.73Q15 40.5 16.77 38.73L22 33.5' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M33 39.5V11' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M26 14.5L31.23 9.27Q33 7.5 34.77 9.27L40 14.5' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrow-left': [
    ['path', { d: 'M38.5 24L12 24' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M19.5 13L10.27 22.23Q8.5 24 10.27 25.77L19.5 35' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrow-left-from-line': [
    ['path', { d: 'M40 9.5V38.5' }, '#6C63FF', 'thick'],
    ['path', { d: 'M31.5 24H11' }, '#A893FF', 'thick'],
    ['path', { d: 'M18 14.5L8.5 24L18 33.5' }, '#6C63FF', 'thick'],
  ],
  'arrow-right': [
    ['path', { d: 'M9.5 24L36 24' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M28.5 13L37.73 22.23Q39.5 24 37.73 25.77L28.5 35' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrow-right-from-line': [
    ['path', { d: 'M8 9.5V38.5' }, '#6C63FF', 'thick'],
    ['path', { d: 'M16.5 24H37' }, '#A893FF', 'thick'],
    ['path', { d: 'M30 14.5L39.5 24L30 33.5' }, '#6C63FF', 'thick'],
  ],
  'arrow-rotate-left': [
    ['path', { d: 'M8.24 26.78A16 16 0 0 0 38.78 30.12' }, '#A893FF', 'thick'],
    [
      'path',
      {
        d: 'M38.78 30.12A16 16 0 0 0 24 8A17.33 17.33 0 0 0 12.02 12.87L8 16.89',
      },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M8 8V16.89H16.89' }, '#6C63FF', 'thick'],
  ],
  'arrow-rotate-right': [
    ['path', { d: 'M39.76 26.78A16 16 0 0 1 9.22 30.12' }, '#A893FF', 'thick'],
    [
      'path',
      {
        d: 'M9.22 30.12A16 16 0 0 1 24 8A17.33 17.33 0 0 1 35.98 12.87L40 16.89',
      },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M40 8V16.89H31.11' }, '#6C63FF', 'thick'],
  ],
  'arrow-trend-up': [
    ['path', { d: 'M6.5 33.5L18.5 21.5L26.5 29.5' }, '#56D68A', 'thick'],
    ['path', { d: 'M26.5 29.5L41 15' }, '#21C45D', 'thick'],
    ['path', { d: 'M31 15H41V25' }, '#21C45D', 'thick'],
  ],
  'arrow-up': [
    ['path', { d: 'M24 40V10M12 22L24 10l12 12' }, 'currentColor', 'thick'],
  ],
  'arrow-up-right': [
    ['path', { d: 'M11.5 36.5L33.5 14.5' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M19.5 11.5L34 11.5Q36.5 11.5 36.5 14L36.5 28.5' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrow-up-right-from-square': [
    [
      'path',
      {
        d: 'M21.5 12.4H12.4a6 6 0 0 0 -6 6V35.6a6 6 0 0 0 6 6H30a6 6 0 0 0 6 -6V26.5',
      },
      '#4D7CFF',
      'thick',
    ],
    ['path', { d: 'M20.5 27.5L39 9' }, '#FF6FAE', 'thick'],
    [
      'path',
      { d: 'M30.4 6.4L39.1 6.4Q41.6 6.4 41.6 8.9L41.6 17.6' },
      '#F0418F',
      'thick',
    ],
  ],
  'arrow-up-z-a': [
    ['path', { d: 'M13.5 41V10' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M6.2 14.3L11.73 8.77Q13.5 7 15.27 8.77L20.8 14.3' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M27.5 6.5H40.5L27.5 19.5H40.5' }, '#FFB020', 'thick'],
    [
      'path',
      { d: 'M27 41.5L31.5 28.5H36.5L41 41.5M29 36.5H39' },
      '#FF6FAE',
      'thick',
    ],
  ],
  'arrows-left-right': [
    ['path', { d: 'M12 24H36' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M16 16L9.77 22.23Q8 24 9.77 25.77L16 32' },
      '#6C63FF',
      'thick',
    ],
    [
      'path',
      { d: 'M32 16L38.23 22.23Q40 24 38.23 25.77L32 32' },
      '#6C63FF',
      'thick',
    ],
  ],
  'arrows-rotate': [
    ['path', { d: 'M8.24 21.22A16 16 0 0 1 24 8' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M24 8A17.33 17.33 0 0 1 35.98 12.87L40 16.89' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M40 8V16.89H31.11' }, '#6C63FF', 'thick'],
    ['path', { d: 'M39.76 26.78A16 16 0 0 1 24 40' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M24 40A17.33 17.33 0 0 1 12.02 35.13L8 31.11' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M16.89 31.11H8V40' }, '#6C63FF', 'thick'],
  ],
  at: [
    ['circle', { cx: 24, cy: 24, r: 4 }, '#FF4D94'],
    ['circle', { cx: 24, cy: 24, r: 6 }, '#F59A0B', 'thick'],
    [
      'path',
      { d: 'M30 17.5V27.5a5.5 5.5 0 0 0 11 0V24A17 17 0 1 0 30.4 39.8' },
      '#F59A0B',
      'thick',
    ],
  ],
  avatar: [
    ['circle', { cx: 24, cy: 24, r: 22 }, '#EEE9FF'],
    [
      'path',
      {
        d: 'M9.6 40.6c2.6-7.6 8-11.6 14.4-11.6s11.8 4 14.4 11.6A21.9 21.9 0 0 1 24 46a21.9 21.9 0 0 1-14.4-5.4z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 24, cy: 19, r: 8.2 }, '#A893FF'],
    ['path', { d: 'M18.6 15.6a6.4 6.4 0 0 1 4.4-3' }, '#FFFFFF', 'thick'],
  ],
  'badge-check': [
    [
      'path',
      {
        d: 'M45 24 44.9 24.68 44.61 25.35 44.14 25.98 43.53 26.57 42.82 27.11 42.05 27.59 41.26 28.03 40.52 28.43 39.85 28.81 39.29 29.19 38.87 29.6 38.6 30.05 38.47 30.56 38.48 31.14 38.61 31.81 38.81 32.55 39.05 33.36 39.3 34.22 39.5 35.11 39.63 35.99 39.64 36.84 39.53 37.62 39.26 38.29 38.85 38.85 38.29 39.26 37.62 39.53 36.84 39.64 35.99 39.63 35.11 39.5 34.22 39.3 33.36 39.05 32.55 38.81 31.81 38.61 31.14 38.48 30.56 38.47 30.05 38.6 29.6 38.87 29.19 39.29 28.81 39.85 28.43 40.52 28.03 41.26 27.59 42.05 27.11 42.82 26.57 43.53 25.98 44.14 25.35 44.61 24.68 44.9 24 45 23.32 44.9 22.65 44.61 22.02 44.14 21.43 43.53 20.89 42.82 20.41 42.05 19.97 41.26 19.57 40.52 19.19 39.85 18.81 39.29 18.4 38.87 17.95 38.6 17.44 38.47 16.86 38.48 16.19 38.61 15.45 38.81 14.64 39.05 13.78 39.3 12.89 39.5 12.01 39.63 11.16 39.64 10.38 39.53 9.71 39.26 9.15 38.85 8.74 38.29 8.47 37.62 8.36 36.84 8.37 35.99 8.5 35.11 8.7 34.22 8.95 33.36 9.19 32.55 9.39 31.81 9.52 31.14 9.53 30.56 9.4 30.05 9.13 29.6 8.71 29.19 8.15 28.81 7.48 28.43 6.74 28.03 5.95 27.59 5.18 27.11 4.47 26.57 3.86 25.98 3.39 25.35 3.1 24.68 3 24 3.1 23.32 3.39 22.65 3.86 22.02 4.47 21.43 5.18 20.89 5.95 20.41 6.74 19.97 7.48 19.57 8.15 19.19 8.71 18.81 9.13 18.4 9.4 17.95 9.53 17.44 9.52 16.86 9.39 16.19 9.19 15.45 8.95 14.64 8.7 13.78 8.5 12.89 8.37 12.01 8.36 11.16 8.47 10.38 8.74 9.71 9.15 9.15 9.71 8.74 10.38 8.47 11.16 8.36 12.01 8.37 12.89 8.5 13.78 8.7 14.64 8.95 15.45 9.19 16.19 9.39 16.86 9.52 17.44 9.53 17.95 9.4 18.4 9.13 18.81 8.71 19.19 8.15 19.57 7.48 19.97 6.74 20.41 5.95 20.89 5.18 21.43 4.47 22.02 3.86 22.65 3.39 23.32 3.1 24 3 24.68 3.1 25.35 3.39 25.98 3.86 26.57 4.47 27.11 5.18 27.59 5.95 28.03 6.74 28.43 7.48 28.81 8.15 29.19 8.71 29.6 9.13 30.05 9.4 30.56 9.53 31.14 9.52 31.81 9.39 32.55 9.19 33.36 8.95 34.22 8.7 35.11 8.5 35.99 8.37 36.84 8.36 37.62 8.47 38.29 8.74 38.85 9.15 39.26 9.71 39.53 10.38 39.64 11.16 39.63 12.01 39.5 12.89 39.3 13.78 39.05 14.64 38.81 15.45 38.61 16.19 38.48 16.86 38.47 17.44 38.6 17.95 38.87 18.4 39.29 18.81 39.85 19.19 40.52 19.57 41.26 19.97 42.05 20.41 42.82 20.89 43.53 21.43 44.14 22.02 44.61 22.65 44.9 23.32Z',
      },
      '#16C7A6',
    ],
    [
      'rect',
      {
        x: 11.38,
        y: 24.35,
        width: 13.94,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 18.35 27.15)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 15.94,
        y: 21.35,
        width: 22.01,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(-46.48 26.95 24.15)',
      },
      '#FFFFFF',
    ],
  ],
  'bag-shopping': [
    [
      'path',
      {
        d: 'M10.6 15h26.8a3 3 0 0 1 3 2.8l1.6 21.8a3.2 3.2 0 0 1-3.2 3.4H9.2A3.2 3.2 0 0 1 6 39.6l1.6-21.8a3 3 0 0 1 3-2.8z',
      },
      '#FF6FAE',
    ],
    [
      'path',
      { d: 'M7.4 20.5h33.2l-.4-2.7a3 3 0 0 0-3-2.8H10.8a3 3 0 0 0-3 2.8z' },
      '#FF9CC8',
    ],
    ['path', { d: 'M17 23v-10a7 7 0 0 1 14 0v10' }, '#C2378A', 'thick'],
  ],
  ban: [
    [
      'path',
      {
        d: 'M24 4A20 20 0 1 1 24 44A20 20 0 1 1 24 4ZM24 10A14 14 0 1 0 24 38A14 14 0 1 0 24 10Z',
        'fill-rule': 'evenodd',
      },
      '#FF4D5E',
    ],
    [
      'rect',
      {
        x: 4.03,
        y: 21,
        width: 39.94,
        height: 6,
        rx: 3,
        transform: 'rotate(45 24 24)',
      },
      '#FF4D5E',
    ],
    [
      'path',
      { d: 'M8.03 18.19A17 17 0 0 1 14.99 9.58', opacity: 0.7 },
      '#FFFFFF',
      'line',
    ],
  ],
  'bars-filter': [
    ['rect', { x: 6, y: 9.2, width: 36, height: 6.6, rx: 3.3 }, '#FF6FAE'],
    ['rect', { x: 12, y: 20.7, width: 24, height: 6.6, rx: 3.3 }, '#FFC23D'],
    ['rect', { x: 18, y: 32.2, width: 12, height: 6.6, rx: 3.3 }, '#4D7CFF'],
    ['path', { d: 'M10 11.1H15', opacity: 0.6 }, '#FFFFFF', 'line'],
    ['path', { d: 'M16 22.6H20', opacity: 0.6 }, '#FFFFFF', 'line'],
    ['path', { d: 'M22 34.1H23.5', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  bed: [
    ['rect', { x: 4, y: 7, width: 6, height: 34, rx: 3 }, '#7C5CFF'],
    ['rect', { x: 38, y: 26, width: 6, height: 15, rx: 3 }, '#7C5CFF'],
    ['rect', { x: 4, y: 27, width: 40, height: 7.5, rx: 2.5 }, '#7C5CFF'],
    ['rect', { x: 11.5, y: 19, width: 10.5, height: 8, rx: 4 }, '#FFD58F'],
    [
      'path',
      {
        d: 'M23.5 30V21a4.5 4.5 0 0 1 4.5-4.5h11.5a4.5 4.5 0 0 1 4.5 4.5v6a3 3 0 0 1-3 3z',
      },
      '#A893FF',
    ],
  ],
  bell: [
    ['circle', { cx: 24, cy: 7.4, r: 3 }, '#F08C00'],
    ['circle', { cx: 24, cy: 37.4, r: 4.6 }, '#F08C00'],
    [
      'path',
      {
        d: 'M24 8.2c-7.4 0-12.4 5.6-12.4 12.8v6.2l-3.9 5.4c-1 1.4 0 3.2 1.7 3.2h29.2c1.7 0 2.7-1.8 1.7-3.2l-3.9-5.4V21c0-7.2-5-12.8-12.4-12.8z',
      },
      '#FFC21A',
    ],
    ['path', { d: 'M16.6 24.4v-3.2c0-3.2 1.5-5.8 4-7.2' }, '#FFFFFF', 'line'],
    ['circle', { cx: 37, cy: 11.5, r: 5.2 }, '#FF3B5C'],
  ],
  'bell-slash': [
    ['circle', { cx: 24, cy: 7.4, r: 3 }, '#F08C00'],
    ['circle', { cx: 24, cy: 37.4, r: 4.6 }, '#F08C00'],
    [
      'path',
      {
        d: 'M11.94 17.88 11.83 18.38 11.7 19.24 11.63 20.11 11.6 21 11.6 27.2 7.7 32.6 7.59 32.78 7.49 32.96 7.42 33.14 7.36 33.32 7.32 33.51 7.3 33.69 7.3 33.87 7.32 34.05 7.35 34.23 7.4 34.4 7.46 34.57 7.54 34.73 7.63 34.88 7.73 35.02 7.85 35.16 7.98 35.28 8.12 35.4 8.28 35.5 8.44 35.59 8.61 35.66 8.8 35.72 8.99 35.76 9.19 35.79 9.4 35.8 29.86 35.8z',
      },
      '#FFC21A',
    ],
    [
      'path',
      {
        d: 'M24 8.2 23.09 8.23 22.2 8.31 21.34 8.45 20.51 8.65 19.71 8.89 18.94 9.19 18.2 9.53 17.5 9.92 16.82 10.35 16.52 10.58 40.53 34.59 40.6 34.4 40.65 34.23 40.68 34.05 40.7 33.87 40.7 33.69 40.68 33.51 40.64 33.32 40.58 33.14 40.51 32.96 40.41 32.78 40.3 32.6 36.4 27.2 36.4 21 36.37 20.11 36.3 19.24 36.17 18.38 36 17.55 35.77 16.74 35.5 15.96 35.19 15.21 34.83 14.48 34.43 13.79 33.98 13.12 33.5 12.49 32.97 11.9 32.41 11.34 31.81 10.83 31.18 10.35 30.5 9.92 29.8 9.53 29.06 9.19 28.29 8.89 27.49 8.65 26.66 8.45 25.8 8.31 24.91 8.23z',
      },
      '#FFC21A',
    ],
    ['path', { d: 'M15.6 30.4V25.6' }, '#FFFFFF', 'line'],
    ['path', { d: 'M7 7L41 41' }, '#F08C00', 'thick'],
  ],
  binary: [
    [
      'path',
      {
        d: 'M14.3 4A6.7 6.7 0 0 1 21 10.7V15.3A6.7 6.7 0 0 1 14.3 22A6.7 6.7 0 0 1 7.6 15.3V10.7A6.7 6.7 0 0 1 14.3 4ZM14.3 8.4A2.3 2.3 0 0 0 12 10.7V15.3A2.3 2.3 0 0 0 14.3 17.6A2.3 2.3 0 0 0 16.6 15.3V10.7A2.3 2.3 0 0 0 14.3 8.4Z',
        'fill-rule': 'evenodd',
      },
      '#4D7CFF',
    ],
    [
      'path',
      { d: 'M28.9 9.8L34.4 6.2V19.8M30.2 19.8H38.6' },
      '#16C7A6',
      'thick',
    ],
    [
      'path',
      { d: 'M9.7 31.8L15.2 28.2V41.8M11 41.8H19.4' },
      '#16C7A6',
      'thick',
    ],
    [
      'path',
      {
        d: 'M33.7 26A6.7 6.7 0 0 1 40.4 32.7V37.3A6.7 6.7 0 0 1 33.7 44A6.7 6.7 0 0 1 27 37.3V32.7A6.7 6.7 0 0 1 33.7 26ZM33.7 30.4A2.3 2.3 0 0 0 31.4 32.7V37.3A2.3 2.3 0 0 0 33.7 39.6A2.3 2.3 0 0 0 36 37.3V32.7A2.3 2.3 0 0 0 33.7 30.4Z',
        'fill-rule': 'evenodd',
      },
      '#4D7CFF',
    ],
  ],
  bold: [
    [
      'path',
      {
        d: 'M17 9.5H28.5A8 8 0 0 1 28.5 25.5H30A9 9 0 0 1 30 43.5H17A3 3 0 0 1 14 40.5V12.5A3 3 0 0 1 17 9.5ZM17.5 13H25A3.5 3.5 0 0 1 25 20H17.5ZM17.5 26H27A4.5 4.5 0 0 1 27 35H17.5Z',
        'fill-rule': 'evenodd',
        transform: 'translate(-1.25 -1.25)',
      },
      '#FF6FAE',
    ],
    [
      'path',
      {
        d: 'M14.5 7H26A8 8 0 0 1 26 23H27.5A9 9 0 0 1 27.5 41H14.5A3 3 0 0 1 11.5 38V10A3 3 0 0 1 14.5 7ZM17.5 13H25A3.5 3.5 0 0 1 25 20H17.5ZM17.5 26H27A4.5 4.5 0 0 1 27 35H17.5Z',
        'fill-rule': 'evenodd',
        transform: 'translate(-1.25 -1.25)',
      },
      '#6C63FF',
    ],
    [
      'path',
      { d: 'M14.4 11.5V16', opacity: 0.6, transform: 'translate(-1.25 -1.25)' },
      '#FFFFFF',
      'line',
    ],
  ],
  bolt: [
    [
      'path',
      {
        d: 'M21.2 4h11.6c1.1 0 1.8 1.1 1.4 2.1L29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16H11.4c-1.2 0-2-1.2-1.5-2.3L19.4 5.2c.3-.7 1-1.2 1.8-1.2z',
      },
      '#FFB020',
    ],
    [
      'path',
      {
        d: 'M29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16z',
      },
      '#FF7A1A',
    ],
    ['path', { d: 'M22.4 8.6l-5 11.2' }, '#FFFFFF', 'line'],
    ['circle', { cx: 41, cy: 8.5, r: 2.4 }, '#FFD966'],
    ['circle', { cx: 7.5, cy: 37, r: 2 }, '#FFD966'],
  ],
  book: [
    ['rect', { x: 8, y: 5, width: 32, height: 39, rx: 6 }, '#3A3DB8'],
    ['rect', { x: 12, y: 32.5, width: 24.5, height: 8, rx: 2.4 }, '#FFF8EC'],
    ['rect', { x: 8, y: 5, width: 32, height: 30.5, rx: 6 }, '#5B5FEF'],
    [
      'path',
      { d: 'M14 5V35.5H14A6 6 0 0 1 8 29.5V11A6 6 0 0 1 14 5Z' },
      '#3A3DB8',
    ],
    ['rect', { x: 19.5, y: 12, width: 14.5, height: 3.6, rx: 1.8 }, '#FFFFFF'],
    ['rect', { x: 19.5, y: 19, width: 9.5, height: 3.6, rx: 1.8 }, '#FFFFFF'],
  ],
  'book-open-lines': [
    [
      'path',
      {
        d: 'M3 11Q3 8 6 8C13 7.6 19 9 24 12C29 9 35 7.6 42 8Q45 8 45 11V37.5Q45 40.5 42 40.5C35 40.3 29.5 41.5 25.6 43.6Q24 44.4 22.4 43.6C18.5 41.5 13 40.3 6 40.5Q3 40.5 3 37.5Z',
      },
      '#5B5FEF',
    ],
    [
      'path',
      {
        d: 'M8.5 12.2C14 11.9 18.6 13.2 22.6 15.8V40C18.6 37.6 14 36.6 8.5 36.8Q7 36.8 7 35.3V13.7Q7 12.2 8.5 12.2Z',
      },
      '#FFF8EC',
    ],
    [
      'path',
      {
        d: 'M39.5 12.2C34 11.9 29.4 13.2 25.4 15.8V40C29.4 37.6 34 36.6 39.5 36.8Q41 36.8 41 35.3V13.7Q41 12.2 39.5 12.2Z',
      },
      '#FFF8EC',
    ],
    [
      'rect',
      {
        x: 10,
        y: 19.5,
        width: 9.5,
        height: 3.6,
        rx: 1.8,
        transform: 'rotate(8 14.75 21.3)',
      },
      '#5B5FEF',
    ],
    [
      'rect',
      {
        x: 28.5,
        y: 19.5,
        width: 9.5,
        height: 3.6,
        rx: 1.8,
        transform: 'rotate(-8 33.25 21.3)',
      },
      '#5B5FEF',
    ],
    [
      'rect',
      {
        x: 10,
        y: 27.5,
        width: 9.5,
        height: 3.6,
        rx: 1.8,
        transform: 'rotate(8 14.75 29.3)',
      },
      '#5B5FEF',
    ],
    [
      'rect',
      {
        x: 28.5,
        y: 27.5,
        width: 9.5,
        height: 3.6,
        rx: 1.8,
        transform: 'rotate(-8 33.25 29.3)',
      },
      '#5B5FEF',
    ],
  ],
  bookmark: [
    [
      'path',
      {
        d: 'M14 4H34a4.5 4.5 0 0 1 4.5 4.5V41.2C38.5 43 36.5 44 35 43L24 35L13 43C11.5 44 9.5 43 9.5 41.2V8.5A4.5 4.5 0 0 1 14 4z',
      },
      '#FF4D94',
    ],
    ['path', { d: 'M14.5 10V20' }, '#FFFFFF', 'line'],
  ],
  box: [
    [
      'path',
      { d: 'M5 16L43 16L43 38.5Q43 43 38.5 43L9.5 43Q5 43 5 38.5Z' },
      '#F29A4A',
    ],
    [
      'path',
      {
        d: 'M8.84 8.33Q10 6 12.6 6L35.4 6Q38 6 39.16 8.33L42.64 15.28Q43 16 42.2 16L5.8 16Q5 16 5.36 15.28Z',
      },
      '#FFC27A',
    ],
    [
      'path',
      {
        d: 'M20.6 6h6.8l.6 10v7.6a1.6 1.6 0 0 1-1.6 1.6h-4.8a1.6 1.6 0 0 1-1.6-1.6V16z',
      },
      '#FFF1DC',
    ],
  ],
  'box-archive': [
    [
      'path',
      { d: 'M7 17L41 17L41 37.5Q41 42 36.5 42L11.5 42Q7 42 7 37.5Z' },
      '#F29A4A',
    ],
    ['rect', { x: 4, y: 6, width: 40, height: 11.5, rx: 3.6 }, '#D2772F'],
    ['rect', { x: 17.5, y: 23.5, width: 13, height: 4.4, rx: 2.2 }, '#8E5526'],
  ],
  'box-isometric': [
    [
      'path',
      {
        d: 'M21.74 5.29Q24 4 26.26 5.29L39.24 12.71Q41.5 14 41.5 16.6L41.5 31.4Q41.5 34 39.24 35.29L26.26 42.71Q24 44 21.74 42.71L8.76 35.29Q6.5 34 6.5 31.4L6.5 16.6Q6.5 14 8.76 12.71Z',
      },
      '#D2772F',
    ],
    [
      'path',
      {
        d: 'M6.5 16.4Q6.5 14 8.58 15.19L24 24L24 41.4Q24 44 21.74 42.71L8.76 35.29Q6.5 34 6.5 31.4Z',
      },
      '#F29A4A',
    ],
    [
      'path',
      {
        d: 'M21.74 5.29Q24 4 26.26 5.29L39.42 12.81Q41.5 14 39.42 15.19L24 24L8.58 15.19Q6.5 14 8.58 12.81Z',
      },
      '#FFC27A',
    ],
    [
      'path',
      {
        d: 'M13.08 10.24L17.42 7.76L34.92 17.76L34.92 26.3Q34.92 27.5 33.88 28.1L31.62 29.4Q30.58 30 30.58 28.8L30.58 20.24Z',
      },
      '#FFF1DC',
    ],
  ],
  'box-open': [
    [
      'path',
      {
        d: 'M7.5 23L24 14L20.32 7.87Q19.5 6.5 18.1 7.27L4.4 14.73Q3 15.5 3.82 16.87Z',
      },
      '#F29A4A',
    ],
    [
      'path',
      {
        d: 'M24 14L40.5 23L44.18 16.87Q45 15.5 43.6 14.73L29.9 7.27Q28.5 6.5 27.68 7.87Z',
      },
      '#D2772F',
    ],
    [
      'path',
      {
        d: 'M22.95 14.57Q24 14 25.05 14.57L39.45 22.43Q40.5 23 39.45 23.57L25.05 31.43Q24 32 22.95 31.43L8.55 23.57Q7.5 23 8.55 22.43Z',
      },
      '#8E5526',
    ],
    [
      'path',
      {
        d: 'M7.5 24.4Q7.5 23 8.73 23.67L24 32L24 41.4Q24 44 21.72 42.75L9.78 36.25Q7.5 35 7.5 32.4Z',
      },
      '#F29A4A',
    ],
    [
      'path',
      {
        d: 'M24 32L39.27 23.67Q40.5 23 40.5 24.4L40.5 32.4Q40.5 35 38.22 36.25L26.28 42.75Q24 44 24 41.4Z',
      },
      '#D2772F',
    ],
    [
      'path',
      {
        d: 'M24 2.5l1.6 2.9 2.9 1.6-2.9 1.6L24 11.5l-1.6-2.9-2.9-1.6 2.9-1.6z',
      },
      '#FFC23D',
    ],
  ],
  'brackets-curly': [
    [
      'path',
      {
        d: 'M16.949 7.627H16.525C14.407 7.627 13.136 8.898 13.136 11.017V16.102C13.136 18.305 11.017 19.746 7.203 20.339C11.017 20.932 13.136 22.373 13.136 24.576V29.661C13.136 31.78 14.407 33.051 16.525 33.051H16.949',
        transform: 'scale(1.18)',
      },
      '#16C7A6',
      'thick',
    ],
    [
      'path',
      {
        d: 'M23.729 7.627H24.153C26.271 7.627 27.542 8.898 27.542 11.017V16.102C27.542 18.305 29.661 19.746 33.475 20.339C29.661 20.932 27.542 22.373 27.542 24.576V29.661C27.542 31.78 26.271 33.051 24.153 33.051H23.729',
        transform: 'scale(1.18)',
      },
      '#16C7A6',
      'thick',
    ],
  ],
  brain: [
    [
      'path',
      {
        d: 'M22.9 8.6A5.43 5.43 0 0 0 14.2 9.6A6.51 6.51 0 0 0 7.4 17.6A6.22 6.22 0 0 0 6.6 27.6A6.28 6.28 0 0 0 11.6 36.4A7.35 7.35 0 0 0 22.9 40Z',
      },
      '#9640EC',
    ],
    [
      'path',
      {
        d: 'M25.1 8.6A5.43 5.43 0 0 1 33.8 9.6A6.51 6.51 0 0 1 40.6 17.6A6.22 6.22 0 0 1 41.4 27.6A6.28 6.28 0 0 1 36.4 36.4A7.35 7.35 0 0 1 25.1 40Z',
      },
      '#BC76FF',
    ],
    [
      'path',
      { d: 'M18.5 15.5c-3.4.2-5.6 2.6-5.2 5.6M12.5 29c2.4-.4 4.6.7 5.6 3' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      { d: 'M29.5 15.5c3.4.2 5.6 2.6 5.2 5.6M35.5 29c-2.4-.4-4.6.7-5.6 3' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      {
        d: 'M41 3.2l2.02 2.78 2.78 2.02-2.78 2.02-2.02 2.78-2.02-2.78-2.78-2.02 2.78-2.02z',
      },
      '#FF6FC8',
    ],
  ],
  branch: [
    ['path', { d: 'M24 38V29c0-6-11-6.5-11-15' }, '#7CC522', 'thick'],
    ['path', { d: 'M24 29c0-6 11-6.5 11-15' }, '#3FB6F5', 'thick'],
    ['circle', { cx: 13, cy: 11, r: 6 }, '#7CC522'],
    ['circle', { cx: 35, cy: 11, r: 6 }, '#3FB6F5'],
    ['circle', { cx: 24, cy: 38.5, r: 6.5 }, '#4E9A1E'],
    ['circle', { cx: 24, cy: 38.5, r: 2.4 }, '#FFFFFF'],
  ],
  brief: [
    [
      'path',
      {
        d: 'M11 4h14.6c1 0 2 .4 2.8 1.2l7.4 7.4c.8.8 1.2 1.8 1.2 2.8V37a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z',
      },
      '#5B5FEF',
    ],
    ['path', { d: 'M26 4.2V11a3 3 0 0 0 3 3h6.8z' }, '#A5A8FF'],
    ['rect', { x: 12, y: 18, width: 14, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 12, y: 24, width: 18, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 12, y: 30, width: 10, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 27, y: 27.5, width: 18, height: 16.5, rx: 4 }, '#FFFFFF'],
    [
      'path',
      { d: 'M31 27.5h10a4 4 0 0 1 4 4V34H27v-2.5a4 4 0 0 1 4-4z' },
      '#FF7A45',
    ],
    ['rect', { x: 30.6, y: 24.5, width: 2.8, height: 6, rx: 1.4 }, '#3A3DB8'],
    ['rect', { x: 38.6, y: 24.5, width: 2.8, height: 6, rx: 1.4 }, '#3A3DB8'],
    ['rect', { x: 30.5, y: 36.5, width: 4.5, height: 4.5, rx: 1.3 }, '#FFC23D'],
  ],
  briefcase: [
    [
      'path',
      { d: 'M17 15v-4.5a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4V15' },
      '#8E5526',
      'thick',
    ],
    ['rect', { x: 4, y: 14, width: 40, height: 28, rx: 6 }, '#D08A45'],
    ['rect', { x: 4, y: 25.5, width: 40, height: 3.4 }, '#B8743A'],
    ['rect', { x: 19.5, y: 22.7, width: 9, height: 9, rx: 2.4 }, '#FFE3BF'],
  ],
  browser: [
    ['rect', { x: 4, y: 7, width: 40, height: 34, rx: 7 }, '#2F80FF'],
    ['rect', { x: 8, y: 17.5, width: 32, height: 19.5, rx: 3.5 }, '#FFFFFF'],
    ['circle', { cx: 11.5, cy: 12.25, r: 2.2 }, '#FFFFFF'],
    ['rect', { x: 16.5, y: 10.5, width: 20, height: 3.5, rx: 1.75 }, '#8FD3FF'],
  ],
  building: [
    ['rect', { x: 25, y: 18, width: 17, height: 24.5, rx: 3.5 }, '#8D97FF'],
    ['rect', { x: 7, y: 6, width: 22, height: 36.5, rx: 4 }, '#4F5BEA'],
    [
      'path',
      {
        d: 'M11.5 11.5h4.4v4.4h-4.4zM20.1 11.5h4.4v4.4h-4.4zM11.5 19.5h4.4v4.4h-4.4zM20.1 19.5h4.4v4.4h-4.4zM11.5 27.5h4.4v4.4h-4.4zM20.1 27.5h4.4v4.4h-4.4z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M32.4 23.5h4.4v4.4h-4.4zM32.4 31.5h4.4v4.4h-4.4z' },
      '#FFFFFF',
    ],
    ['rect', { x: 15.4, y: 35, width: 5.2, height: 7.5, rx: 1.6 }, '#FFC93C'],
  ],
  'building-columns': [
    [
      'path',
      {
        d: 'M22 5.7a3.8 3.8 0 0 1 4 0l15.6 9.7c1.7 1.1 1 3.6-1 3.6H7.4c-2 0-2.7-2.5-1-3.6z',
      },
      '#16C7A6',
    ],
    ['circle', { cx: 24, cy: 13.2, r: 2.7 }, '#FFFFFF'],
    ['rect', { x: 9, y: 19, width: 6.5, height: 19 }, '#8FE8D6'],
    ['rect', { x: 20.75, y: 19, width: 6.5, height: 19 }, '#8FE8D6'],
    ['rect', { x: 32.5, y: 19, width: 6.5, height: 19 }, '#8FE8D6'],
    ['rect', { x: 5, y: 37, width: 38, height: 6, rx: 3 }, '#16C7A6'],
  ],
  buildings: [
    ['rect', { x: 7, y: 5, width: 19, height: 37, rx: 4 }, '#B44CE6'],
    ['rect', { x: 23, y: 17, width: 18, height: 25, rx: 4 }, '#F27BC9'],
    [
      'path',
      { d: 'M12 11.5h6M12 17.5h6M12 23.5h6M12 29.5h6' },
      ['--cl-oncard', '#FFFFFF'],
      'line',
    ],
    [
      'path',
      {
        d: 'M28.3 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM28.3 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1z',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
    ['rect', { x: 29.5, y: 35, width: 5, height: 7, rx: 1.6 }, '#FFD23F'],
  ],
  bullseye: [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#FF6FAE'],
    ['circle', { cx: 24, cy: 24, r: 14.5 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 24, r: 9.5 }, '#FF6FAE'],
    ['circle', { cx: 24, cy: 24, r: 4.5 }, '#FFC23D'],
  ],
  'bullseye-arrow': [
    ['circle', { cx: 23, cy: 25, r: 18 }, '#FF6FAE'],
    ['circle', { cx: 23, cy: 25, r: 13 }, '#FFFFFF'],
    ['circle', { cx: 23, cy: 25, r: 8.2 }, '#FF6FAE'],
    ['circle', { cx: 23, cy: 25, r: 3.8 }, '#FFC23D'],
    ['path', { d: 'M23 25L37.85 10.15' }, ['--cl-base', '#2D2A5C'], 'thick'],
    [
      'path',
      {
        d: 'M32.19 15.81L31.13 8.38L35.37 4.14L36.44 11.56L43.86 12.63L39.62 16.87Z',
      },
      '#FFC23D',
    ],
  ],
  calendar: [
    ['rect', { x: 5, y: 8.5, width: 38, height: 35.5, rx: 7 }, '#FF8A1F'],
    ['rect', { x: 9.5, y: 19, width: 29, height: 20.5, rx: 3.5 }, '#FFF4E8'],
    [
      'rect',
      { x: 12.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 30.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  'calendar-check': [
    ['rect', { x: 5, y: 8.5, width: 38, height: 35.5, rx: 7 }, '#FF8A1F'],
    ['rect', { x: 9.5, y: 19, width: 29, height: 20.5, rx: 3.5 }, '#FFF4E8'],
    [
      'rect',
      { x: 12.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 30.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['path', { d: 'M16.5 29.4l5.2 5 10-10' }, '#21C45D', 'thick'],
  ],
  'calendar-clock': [
    ['rect', { x: 4, y: 8, width: 29, height: 28, rx: 6 }, '#FF8A1F'],
    ['rect', { x: 8, y: 16.5, width: 21, height: 15.5, rx: 3 }, '#FFF4E8'],
    [
      'rect',
      { x: 10, y: 4, width: 4.6, height: 8.5, rx: 2.3 },
      ['--cl-base', '#252A57'],
    ],
    [
      'rect',
      { x: 22.4, y: 4, width: 4.6, height: 8.5, rx: 2.3 },
      ['--cl-base', '#252A57'],
    ],
    ['circle', { cx: 32.5, cy: 32.5, r: 12.5 }, ['--cl-base', '#252A57']],
    ['circle', { cx: 32.5, cy: 32.5, r: 9.3 }, '#FFF4E8'],
    ['path', { d: 'M32.5 27.2v5.3l3.6 2.6' }, '#FF7A12', 'line'],
  ],
  'calendar-day': [
    ['rect', { x: 5, y: 8.5, width: 38, height: 35.5, rx: 7 }, '#FF8A1F'],
    ['rect', { x: 9.5, y: 19, width: 29, height: 20.5, rx: 3.5 }, '#FFF4E8'],
    [
      'rect',
      { x: 12.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 30.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 14, y: 23.5, width: 9.5, height: 9, rx: 2.4 }, '#FF8A1F'],
  ],
  'calendar-days': [
    ['rect', { x: 5, y: 8.5, width: 38, height: 35.5, rx: 7 }, '#FF8A1F'],
    ['rect', { x: 9.5, y: 19, width: 29, height: 20.5, rx: 3.5 }, '#FFF4E8'],
    [
      'rect',
      { x: 12.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 30.5, y: 4, width: 5, height: 10, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 13.7, y: 23.2, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
    ['rect', { x: 21.7, y: 23.2, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
    ['rect', { x: 29.7, y: 23.2, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
    ['rect', { x: 13.7, y: 30.8, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
    ['rect', { x: 21.7, y: 30.8, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
    ['rect', { x: 29.7, y: 30.8, width: 4.6, height: 4.6, rx: 1.4 }, '#FF8A1F'],
  ],
  'call-statuses': [
    [
      'path',
      {
        d: 'M9.4 10h6.2a2.6 2.6 0 0 1 2.5 1.9l1.9 6.8a2.6 2.6 0 0 1-.7 2.6l-3.6 3.4c2.3 4.8 6.2 8.7 11 11l3.4-3.6a2.6 2.6 0 0 1 2.6-.7l6.8 1.9a2.6 2.6 0 0 1 1.9 2.5v6.2a3 3 0 0 1-3 3C20.6 45 6.4 30.8 6.4 13a3 3 0 0 1 3-3z',
      },
      '#22C55E',
    ],
    ['rect', { x: 23.5, y: 4, width: 21, height: 10, rx: 5 }, '#FFFFFF'],
    ['circle', { cx: 28.7, cy: 9, r: 2.3 }, '#22C55E'],
    ['circle', { cx: 34, cy: 9, r: 2.3 }, '#FFB020'],
    ['circle', { cx: 39.3, cy: 9, r: 2.3 }, '#FF4D5E'],
  ],
  'chart-bar': [
    [
      'rect',
      { x: 6.5, y: 7, width: 3, height: 34, rx: 1.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 9.5, y: 10, width: 24, height: 7.5, rx: 2.6 }, '#FF6FAE'],
    ['rect', { x: 9.5, y: 20.25, width: 16, height: 7.5, rx: 2.6 }, '#FFC23D'],
    ['rect', { x: 9.5, y: 30.5, width: 32, height: 7.5, rx: 2.6 }, '#4D7CFF'],
  ],
  'chart-line': [
    [
      'rect',
      { x: 7, y: 38.5, width: 34, height: 3, rx: 1.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['path', { d: 'M9 31.5L18 21l9 7L39 13v25.5H9z' }, '#8FD3FF'],
    ['path', { d: 'M9 31.5L18 21l9 7L39 13' }, '#4D7CFF', 'thick'],
    ['circle', { cx: 39, cy: 13, r: 3.8 }, '#FFC23D'],
  ],
  'chart-line-up': [
    [
      'rect',
      { x: 7, y: 38.5, width: 34, height: 3, rx: 1.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['path', { d: 'M9 32l8.5-8.5 7 6L38 16' }, '#FF6FAE', 'thick'],
    [
      'path',
      {
        d: 'M32.6 11.5h8.4a1.5 1.5 0 0 1 1.5 1.5v8.4c0 1.3-1.6 2-2.6 1.1l-8.4-8.4c-.9-1-.2-2.6 1.1-2.6z',
      },
      '#FF6FAE',
    ],
    ['circle', { cx: 17.5, cy: 23.5, r: 3.4 }, '#FFC23D'],
    ['circle', { cx: 24.5, cy: 29.5, r: 3.4 }, '#FFC23D'],
  ],
  'chart-simple': [
    [
      'rect',
      { x: 7, y: 38.5, width: 34, height: 3, rx: 1.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 10, y: 26, width: 7.5, height: 12.5, rx: 2.6 }, '#FF6FAE'],
    ['rect', { x: 20.25, y: 8, width: 7.5, height: 30.5, rx: 2.6 }, '#FFC23D'],
    ['rect', { x: 30.5, y: 17, width: 7.5, height: 21.5, rx: 2.6 }, '#4D7CFF'],
  ],
  'chart-user': [
    [
      'rect',
      { x: 7, y: 38.5, width: 34, height: 3, rx: 1.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 10, y: 25, width: 7.5, height: 13.5, rx: 2.6 }, '#FF6FAE'],
    ['rect', { x: 20.25, y: 16, width: 7.5, height: 22.5, rx: 2.6 }, '#FFC23D'],
    ['rect', { x: 30.5, y: 8.5, width: 7.5, height: 30, rx: 2.6 }, '#4D7CFF'],
    [
      'path',
      { d: 'M42 2.5l1.1 2.6 2.6 1.1-2.6 1.1L42 9.9l-1.1-2.6-2.6-1.1 2.6-1.1z' },
      '#FFC23D',
    ],
  ],
  check: [
    ['path', { d: 'M9.5 25.5L19 35' }, '#16A34A', 'thick'],
    ['path', { d: 'M19 35L38.5 14' }, '#21C45D', 'thick'],
  ],
  'chess-rook': [
    ['path', { d: 'M15 16h18l2 21H13z' }, '#C77DEB'],
    [
      'path',
      {
        d: 'M9 6.5A2.5 2.5 0 0 1 11.5 4h3A1.5 1.5 0 0 1 16 5.5V10h4.5V5.5A1.5 1.5 0 0 1 22 4h4a1.5 1.5 0 0 1 1.5 1.5V10H32V5.5A1.5 1.5 0 0 1 33.5 4h3A2.5 2.5 0 0 1 39 6.5V15a3 3 0 0 1-3 3H12a3 3 0 0 1-3-3z',
      },
      '#A33BD8',
    ],
    ['rect', { x: 17.5, y: 20.5, width: 3, height: 12, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 7, y: 35.5, width: 34, height: 8, rx: 3.5 }, '#A33BD8'],
  ],
  'chevron-down': [
    [
      'path',
      { d: 'M10 17.5L22.23 29.73Q23.115 30.615 24 30.615' },
      '#A893FF',
      'thick',
    ],
    [
      'path',
      { d: 'M24 30.615Q24.885 30.615 25.77 29.73L38 17.5' },
      '#6C63FF',
      'thick',
    ],
  ],
  'chevron-left': [
    [
      'path',
      { d: 'M30.5 10L18.27 22.23Q17.385 23.115 17.385 24' },
      '#A893FF',
      'thick',
    ],
    [
      'path',
      { d: 'M17.385 24Q17.385 24.885 18.27 25.77L30.5 38' },
      '#6C63FF',
      'thick',
    ],
  ],
  'chevron-right': [
    [
      'rect',
      {
        x: 11.44,
        y: 13.9,
        width: 25.72,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(45.83 24.3 17)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 11.44,
        y: 27.9,
        width: 25.72,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(134.17 24.3 31)',
      },
      '#6C63FF',
    ],
  ],
  'chevron-up': [
    [
      'rect',
      {
        x: 4.31,
        y: 20.85,
        width: 25.37,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(-43.1 17 23.95)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 18.31,
        y: 20.85,
        width: 25.37,
        height: 6.2,
        rx: 3.1,
        transform: 'rotate(43.1 31 23.95)',
      },
      '#6C63FF',
    ],
  ],
  'chevrons-left': [
    [
      'rect',
      {
        x: 19.12,
        y: 14.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(131.76 31.5 17.0)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 19.12,
        y: 28.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(48.24 31.5 31.0)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 4.12,
        y: 14.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(131.76 16.5 17.0)',
      },
      '#6C63FF',
    ],
    [
      'rect',
      {
        x: 4.12,
        y: 28.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(48.24 16.5 31.0)',
      },
      '#6C63FF',
    ],
  ],
  'chevrons-right': [
    [
      'rect',
      {
        x: 4.12,
        y: 14.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(48.24 16.5 17.0)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 4.12,
        y: 28.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(131.76 16.5 31.0)',
      },
      '#9580FF',
    ],
    [
      'rect',
      {
        x: 19.12,
        y: 14.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(48.24 31.5 17.0)',
      },
      '#6C63FF',
    ],
    [
      'rect',
      {
        x: 19.12,
        y: 28.0,
        width: 24.77,
        height: 6.0,
        rx: 3.0,
        transform: 'rotate(131.76 31.5 31.0)',
      },
      '#6C63FF',
    ],
  ],
  church: [
    ['path', { d: 'M24 4.3v8M20.1 7.8h7.8' }, '#E58A00', 'thick'],
    [
      'path',
      {
        d: 'M8 30h9v13H8a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3zM40 30h-9v13h9a3 3 0 0 0 3-3v-7a3 3 0 0 0-3-3z',
      },
      '#4E9A1E',
    ],
    [
      'path',
      {
        d: 'M22.6 12.6a1.9 1.9 0 0 1 2.8 0l7.2 8.2c.7.8.1 2-1 2H16.4c-1.1 0-1.7-1.2-1-2z',
      },
      '#4E9A1E',
    ],
    ['rect', { x: 17, y: 22, width: 14, height: 21 }, '#7CC522'],
    ['circle', { cx: 24, cy: 28.3, r: 2.7 }, '#FFFFFF'],
    ['path', { d: 'M20.5 43v-5.2a3.5 3.5 0 0 1 7 0V43z' }, '#4E9A1E'],
  ],
  circle: [
    [
      'path',
      {
        d: 'M24 4A20 20 0 1 1 24 44A20 20 0 1 1 24 4ZM24 10.2A13.8 13.8 0 1 0 24 37.8A13.8 13.8 0 1 0 24 10.2Z',
        'fill-rule': 'evenodd',
      },
      '#A893FF',
    ],
    [
      'path',
      { d: 'M9.28 15.5A17 17 0 0 1 15.5 9.28', opacity: 0.6 },
      '#FFFFFF',
      'line',
    ],
  ],
  'circle-arrow-up': [
    ['circle', { cx: 24, cy: 24, r: 19.5 }, '#FFB020'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'rect',
      {
        x: 12.3,
        y: 21.3,
        width: 23.4,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(90 24 24.1)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 12.27,
        y: 15.9,
        width: 16.07,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(135 20.3 18.7)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 19.67,
        y: 15.9,
        width: 16.07,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 27.7 18.7)',
      },
      '#FFFFFF',
    ],
  ],
  'circle-check': [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#21C45D'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'rect',
      {
        x: 10.57,
        y: 24.4,
        width: 14.65,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 17.9 27.2)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 15.63,
        y: 21.3,
        width: 23.14,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(-45.92 27.2 24.1)',
      },
      '#FFFFFF',
    ],
  ],
  'circle-chevron-down': [
    ['circle', { cx: 24, cy: 24, r: 19.5 }, '#6C63FF'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'rect',
      {
        x: 10.94,
        y: 21.45,
        width: 17.62,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45.0 19.75 24.25)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 19.44,
        y: 21.45,
        width: 17.62,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(-45.0 28.25 24.25)',
      },
      '#FFFFFF',
    ],
  ],
  'circle-exclamation': [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#FF4D5E'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    ['rect', { x: 21.2, y: 10.65, width: 5.6, height: 17, rx: 2.8 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 34.05, r: 3.3 }, '#FFFFFF'],
  ],
  'circle-info': [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#4D7CFF'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    ['circle', { cx: 24, cy: 14.25, r: 3.3 }, '#FFFFFF'],
    [
      'rect',
      { x: 21.2, y: 21.25, width: 5.6, height: 15.8, rx: 2.8 },
      '#FFFFFF',
    ],
  ],
  'circle-question': [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#2F80FF'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      {
        d: 'M15.82 14.28A8.7 8.7 0 1 1 30.15 23.41A2.8 2.8 0 0 1 26.19 19.45A3.1 3.1 0 1 0 21.09 16.2A2.8 2.8 0 0 1 15.82 14.28Z',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 20.34,
        y: 20.72,
        width: 11.5,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(135 26.09 23.52)',
      },
      '#FFFFFF',
    ],
    ['rect', { x: 21.2, y: 22.8, width: 5.6, height: 6.8, rx: 2.8 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 36.22, r: 3.22 }, '#FFFFFF'],
  ],
  'circle-xmark': [
    ['circle', { cx: 24, cy: 24, r: 20 }, '#FF4D5E'],
    [
      'path',
      { d: 'M9.16 19.18A15.6 15.6 0 0 1 16.68 10.23', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'rect',
      {
        x: 11.72,
        y: 21.2,
        width: 24.55,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 24 24)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 11.72,
        y: 21.2,
        width: 24.55,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(135 24 24)',
      },
      '#FFFFFF',
    ],
  ],
  'clipboard-list': [
    [
      'path',
      {
        d: 'M11 4h14.6c1 0 2 .4 2.8 1.2l7.4 7.4c.8.8 1.2 1.8 1.2 2.8V37a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z',
      },
      '#5B5FEF',
    ],
    ['path', { d: 'M26 4.2V11a3 3 0 0 0 3 3h6.8z' }, '#A5A8FF'],
    ['rect', { x: 12, y: 18, width: 14, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 12, y: 24, width: 18, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 12, y: 30, width: 10, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 27, y: 27.5, width: 18, height: 16.5, rx: 4 }, '#FFFFFF'],
    [
      'path',
      { d: 'M31 27.5h10a4 4 0 0 1 4 4V34H27v-2.5a4 4 0 0 1 4-4z' },
      '#FF7A45',
    ],
    ['rect', { x: 30.6, y: 24.5, width: 2.8, height: 6, rx: 1.4 }, '#3A3DB8'],
    ['rect', { x: 38.6, y: 24.5, width: 2.8, height: 6, rx: 1.4 }, '#3A3DB8'],
    ['rect', { x: 30.5, y: 36.5, width: 4.5, height: 4.5, rx: 1.3 }, '#FFC23D'],
  ],
  clock: [
    ['circle', { cx: 24, cy: 24, r: 19 }, '#FF8A1F'],
    ['circle', { cx: 24, cy: 24, r: 14 }, '#FFF4E8'],
    [
      'path',
      { d: 'M24 12.8v2.2M35.2 24H33M24 35.2V33M12.8 24H15' },
      '#FFB070',
      'line',
    ],
    ['path', { d: 'M24 24v-7.2M24 24l5.2 3.4' }, '#252A57', 'thick'],
    ['circle', { cx: 24, cy: 24, r: 2.3 }, '#FF8A1F'],
  ],
  'clock-rotate-left': [
    [
      'circle',
      { cx: 25, cy: 24.5, r: 14.400000000000002, transform: 'translate(-1 0)' },
      '#FFF4E8',
    ],
    [
      'path',
      {
        d: 'M11.24 15.22A16.6 16.6 0 1 1 10.62 32.8',
        transform: 'translate(-1 0)',
      },
      '#FF8A1F',
      'thick',
    ],
    [
      'path',
      {
        d: 'M6.68 13.68L7.01 19.75Q7.1 21.35 8.61 20.83L14.37 18.87Q15.88 18.35 14.55 17.45L7.92 12.98Q6.6 12.09 6.68 13.68Z',
        transform: 'translate(-1 0)',
      },
      '#FF8A1F',
    ],
    [
      'path',
      { d: 'M25 24.5v-7M25 24.5l4.8 3.2', transform: 'translate(-1 0)' },
      '#2D2A5C',
      'thick',
    ],
    [
      'circle',
      { cx: 25, cy: 24.5, r: 2.2, transform: 'translate(-1 0)' },
      '#FF8A1F',
    ],
  ],
  'cloud-arrow-up': [
    ['circle', { cx: 13.5, cy: 27.5, r: 8.5 }, '#4D7CFF'],
    ['circle', { cx: 25.5, cy: 20.5, r: 11.5 }, '#4D7CFF'],
    ['circle', { cx: 34.5, cy: 29, r: 8.5 }, '#4D7CFF'],
    ['rect', { x: 5, y: 27, width: 38, height: 13, rx: 6.5 }, '#4D7CFF'],
    ['path', { d: 'M17.5 17.5a8 8 0 0 1 6-5' }, '#8FD3FF', 'thick'],
    [
      'path',
      { d: 'M24 35V24.5M18.8 29.3l5.2-5.2 5.2 5.2' },
      '#FFFFFF',
      'thick',
    ],
  ],
  code: [
    ['rect', { x: 4, y: 7, width: 40, height: 34, rx: 7 }, '#FFD43B'],
    ['path', { d: 'M11 7h26a7 7 0 0 1 7 7v1H4v-1a7 7 0 0 1 7-7z' }, '#F2A900'],
    ['circle', { cx: 10, cy: 11, r: 1.5 }, '#FFFFFF'],
    ['circle', { cx: 15, cy: 11, r: 1.5 }, '#FFFFFF'],
    ['path', { d: 'M17.5 21.5L11.5 28l6 6.5' }, '#3A2F12', 'thick'],
    ['path', { d: 'M30.5 21.5l6 6.5-6 6.5' }, '#3A2F12', 'thick'],
    ['path', { d: 'M26.4 20.5l-4.8 15' }, '#3A2F12', 'thick'],
  ],
  'code-branch': [
    ['path', { d: 'M24 38V29c0-6-11-6.5-11-15' }, '#7CC522', 'thick'],
    ['path', { d: 'M24 29c0-6 11-6.5 11-15' }, '#3FB6F5', 'thick'],
    ['circle', { cx: 13, cy: 11, r: 6 }, '#7CC522'],
    ['circle', { cx: 35, cy: 11, r: 6 }, '#3FB6F5'],
    ['circle', { cx: 24, cy: 38.5, r: 6.5 }, '#4E9A1E'],
    ['circle', { cx: 24, cy: 38.5, r: 2.4 }, '#FFFFFF'],
  ],
  coin: [
    ['circle', { cx: 24, cy: 26.189999999999998, r: 19 }, '#0E9F55'],
    ['circle', { cx: 24, cy: 22.2, r: 19 }, '#2BD46F'],
    ['circle', { cx: 24, cy: 22.2, r: 12.540000000000001 }, '#9DF0BF'],
    ['path', { d: 'M24 16.12v12.16' }, '#0E9F55', 'thick'],
    ['path', { d: 'M9.15 16.8A15.8 15.8 0 0 1 16.1 8.52' }, '#FFFFFF', 'thick'],
  ],
  coins: [
    [
      'path',
      {
        d: 'M27.9 12.19A12.5 12.5 0 1 0 12.21 30.45A18.29 18.29 0 0 1 27.9 12.19Z',
      },
      '#0E9F55',
    ],
    [
      'path',
      {
        d: 'M29.18 12.05A12.5 12.5 0 1 0 12.37 27.9A18.29 18.29 0 0 1 29.18 12.05Z',
      },
      '#2BD46F',
    ],
    [
      'path',
      {
        d: 'M24.43 13.04A7.75 7.75 0 1 0 13.63 23.22A18.29 18.29 0 0 1 24.43 13.04Z',
      },
      '#9DF0BF',
    ],
    ['circle', { cx: 30.5, cy: 31.782, r: 14.2 }, '#0E9F55'],
    ['circle', { cx: 30.5, cy: 28.8, r: 14.2 }, '#2BD46F'],
    ['circle', { cx: 30.5, cy: 28.8, r: 9.372 }, '#9DF0BF'],
    ['path', { d: 'M30.5 24.26v9.09' }, '#0E9F55', 'thick'],
  ],
  command: [
    ['rect', { x: 18, y: 18, width: 12, height: 12 }, '#FFC23D'],
    [
      'path',
      {
        d: 'M18 18V13A5 5 0 1 0 13 18H35A5 5 0 1 0 30 13V35A5 5 0 1 0 35 30H13A5 5 0 1 0 18 35Z',
      },
      '#9580FF',
      'thick',
    ],
  ],
  companies: [
    ['rect', { x: 7, y: 5, width: 19, height: 37, rx: 4 }, '#B44CE6'],
    ['rect', { x: 23, y: 17, width: 18, height: 25, rx: 4 }, '#F27BC9'],
    [
      'path',
      { d: 'M12 11.5h6M12 17.5h6M12 23.5h6M12 29.5h6' },
      ['--cl-oncard', '#FFFFFF'],
      'line',
    ],
    [
      'path',
      {
        d: 'M28.3 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM28.3 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1z',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
    ['rect', { x: 29.5, y: 35, width: 5, height: 7, rx: 1.6 }, '#FFD23F'],
  ],
  'company-address': [
    ['ellipse', { cx: 24, cy: 41.5, rx: 11, ry: 3.2 }, '#FFB3C0'],
    [
      'path',
      {
        d: 'M24 4c8.2 0 14.4 6.2 14.4 14.2 0 9.8-9.6 18.4-12.6 21.8a2.4 2.4 0 0 1-3.6 0C19.2 36.6 9.6 28 9.6 18.2 9.6 10.2 15.8 4 24 4z',
      },
      '#FF3D5A',
    ],
    ['circle', { cx: 24, cy: 18.2, r: 7 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 18.2, r: 3 }, '#C81E3A'],
  ],
  copy: [
    [
      'path',
      {
        d: 'M10.5 4H27.5A4.5 4.5 0 0 1 32 8.5V12H20.5A6.5 6.5 0 0 0 14 18.5V34H10.5A4.5 4.5 0 0 1 6 29.5V8.5A4.5 4.5 0 0 1 10.5 4Z',
      },
      '#8FD3FF',
    ],
    [
      'path',
      {
        d: 'M16 18.5Q16 14 20.5 14L31.4 14Q33 14 34.13 15.13L40.87 21.87Q42 23 42 24.6L42 39.5Q42 44 37.5 44L20.5 44Q16 44 16 39.5Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M19 18.5Q19 17 20.5 17L31.26 17Q31.76 17 32.11 17.35L38.65 23.89Q39 24.24 39 24.74L39 39.5Q39 41 37.5 41L20.5 41Q19 41 19 39.5Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M31.76 16L31.76 21.64Q31.76 24.24 34.36 24.24L40 24.24Z' },
      '#4D7CFF',
    ],
  ],
  'credit-card': [
    [
      'rect',
      { x: 4, y: 10, width: 38, height: 26, rx: 5 },
      ['--cl-base', '#252A57'],
    ],
    ['rect', { x: 9, y: 15.5, width: 8.5, height: 6.6, rx: 1.8 }, '#FFC93C'],
    ['path', { d: 'M9.6 28.6h5.6M18.6 28.6h5.6' }, '#FFC93C', 'line'],
    ['circle', { cx: 36.5, cy: 35.5, r: 8.5 }, '#FFC93C'],
    ['circle', { cx: 36.5, cy: 35.5, r: 5.4 }, '#FFE08A'],
    ['path', { d: 'M36.5 32.6v5.8' }, '#E09A00', 'line'],
  ],
  'crm-contact-lookup': [
    [
      'path',
      {
        d: 'M4 9.6V31.4A13 5.2 0 0 0 23.11 35.99A11.4 11.4 0 0 1 30 23.88V9.6Z',
      },
      '#7C5CFF',
    ],
    [
      'path',
      { d: 'M4 18.6A13 5.2 0 0 0 30 18.6v3.4A13 5.2 0 0 1 4 22Z' },
      '#A893FF',
    ],
    ['ellipse', { cx: 17, cy: 9.6, rx: 13, ry: 5.2 }, '#A893FF'],
    ['path', { d: 'M40 40L43.4 43.4' }, ['--cl-base', '#2D2A5C'], 'thick'],
    ['circle', { cx: 34.4, cy: 34.4, r: 9 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 34.4, cy: 34.4, r: 5.6 }, '#7DF1E5'],
  ],
  'csv-logo': [
    ['rect', { x: 4, y: 5, width: 40, height: 38, rx: 8 }, '#21C45D'],
    ['rect', { x: 9, y: 10.4, width: 12.6, height: 6.6, rx: 2 }, '#FFFFFF'],
    ['rect', { x: 26.4, y: 10.4, width: 12.6, height: 6.6, rx: 2 }, '#BDF3D0'],
    ['rect', { x: 9, y: 20.7, width: 12.6, height: 6.6, rx: 2 }, '#BDF3D0'],
    ['rect', { x: 26.4, y: 20.7, width: 12.6, height: 6.6, rx: 2 }, '#FFFFFF'],
    ['rect', { x: 9, y: 31, width: 12.6, height: 6.6, rx: 2 }, '#FFFFFF'],
    ['rect', { x: 26.4, y: 31, width: 12.6, height: 6.6, rx: 2 }, '#BDF3D0'],
  ],
  cube: [
    [
      'path',
      {
        d: 'M24 24.5L24 43.6Q22.44 43.6 20.88 42.7L9.82 36.3Q6.7 34.5 6.7 30.9L6.7 18.1Q6.7 16.3 7.48 14.95Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M24 24.5L40.52 14.95Q41.3 16.3 41.3 18.1L41.3 30.9Q41.3 34.5 38.18 36.3L27.12 42.7Q25.56 43.6 24 43.6Z',
      },
      '#F59A0B',
    ],
    [
      'path',
      {
        d: 'M24 24.5L7.48 14.95Q8.26 13.6 9.82 12.7L20.88 6.3Q24 4.5 27.12 6.3L38.18 12.7Q39.74 13.6 40.52 14.95Z',
      },
      '#FFD58F',
    ],
    ['path', { d: 'M18 12.6l6-3.4' }, '#FFFFFF', 'line'],
  ],
  database: [
    ['path', { d: 'M9 11.5V36A15 6 0 0 0 39 36V11.5z' }, '#4D7CFF'],
    [
      'path',
      {
        d: 'M9 18.6A15 6 0 0 0 39 18.6v3.2A15 6 0 0 1 9 21.8zM9 27.4A15 6 0 0 0 39 27.4v3.2A15 6 0 0 1 9 30.6z',
      },
      '#8FD3FF',
    ],
    ['ellipse', { cx: 24, cy: 11.5, rx: 15, ry: 6 }, '#8FD3FF'],
  ],
  desktop: [
    [
      'rect',
      { x: 4, y: 5.5, width: 40, height: 29, rx: 5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 7.5, y: 9, width: 33, height: 20, rx: 2.2 }, '#4D7CFF'],
    ['path', { d: 'M10.5 11.5h8.5l-8.5 8.5z' }, '#8FD3FF'],
    ['path', { d: 'M20.5 34.5h7l.8 5h-8.6z' }, ['--cl-base', '#2D2A5C']],
    [
      'rect',
      { x: 14, y: 38.5, width: 20, height: 4, rx: 2 },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  'dollar-sign': [
    ['path', { d: 'M24 4.3v4M24 39.7v4' }, '#16A34A', 'thick'],
    [
      'path',
      {
        d: 'M34.31 12A11.9 10.2 0 1 0 24 27.3A5.3 3.6 0 1 1 19.41 32.7A3.3 3.3 0 0 0 13.69 36A11.9 10.2 0 1 0 24 20.7A5.3 3.6 0 1 1 28.59 15.3A3.3 3.3 0 0 0 34.31 12Z',
      },
      '#21C45D',
    ],
  ],
  'down-left-and-up-right-to-center': [
    [
      'path',
      {
        d: 'M36.97 15.97L41.61 11.34Q43.87 9.07 41.61 6.81L41.19 6.39Q38.93 4.13 36.66 6.39L32.03 11.03L36.97 15.97Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M28 17L28 7.6Q28 5 29.84 6.84L41.16 18.16Q43 20 40.4 20L31 20Q28 20 28 17Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M11.03 32.03L6.39 36.66Q4.13 38.93 6.39 41.19L6.81 41.61Q9.07 43.87 11.34 41.61L15.97 36.97L11.03 32.03Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M20 31L20 40.4Q20 43 18.16 41.16L6.84 29.84Q5 28 7.6 28L17 28Q20 28 20 31Z',
      },
      '#4D7CFF',
    ],
    ['circle', { cx: 24, cy: 24, r: 3 }, '#FFC23D'],
  ],
  'down-to-line': [
    ['rect', { x: 18.5, y: 4, width: 11, height: 20, rx: 4 }, '#2F80FF'],
    [
      'path',
      {
        d: 'M10.5 20.5L37.5 20.5Q40.5 20.5 38.35 22.59L26.3 34.27Q24 36.5 21.7 34.27L9.65 22.59Q7.5 20.5 10.5 20.5Z',
      },
      '#2F80FF',
    ],
    [
      'rect',
      { x: 6, y: 39.5, width: 36, height: 5, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['path', { d: 'M22.4 8.6V15' }, '#FFFFFF', 'line'],
  ],
  download: [
    [
      'path',
      { d: 'M7 29V34A6 6 0 0 0 13 40H35A6 6 0 0 0 41 34V29' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M24 8V26.5' }, '#21C45D', 'thick'],
    [
      'path',
      { d: 'M15 20L22.23 27.23Q24 29 25.77 27.23L33 20' },
      '#16A34A',
      'thick',
    ],
  ],
  droplet: [
    [
      'path',
      {
        d: 'M22.3 5.9c.9-1.2 2.5-1.2 3.4 0C30.2 11.6 38 22 38 29.5a14 14 0 0 1-28 0c0-7.5 7.8-17.9 12.3-23.6z',
      },
      '#2F80FF',
    ],
    ['path', { d: 'M16.6 30a7.4 7.4 0 0 0 7.4 7.4' }, '#FFFFFF', 'thick'],
  ],
  ellipsis: [
    ['circle', { cx: 10, cy: 24, r: 5.2 }, '#FF6FAE'],
    ['circle', { cx: 24, cy: 24, r: 5.2 }, '#FFC23D'],
    ['circle', { cx: 38, cy: 24, r: 5.2 }, '#4D7CFF'],
    ['circle', { cx: 8.3, cy: 22.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 22.3, cy: 22.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 36.3, cy: 22.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
  ],
  'ellipsis-vertical': [
    ['circle', { cx: 24, cy: 10, r: 5.2 }, '#FF6FAE'],
    ['circle', { cx: 24, cy: 24, r: 5.2 }, '#FFC23D'],
    ['circle', { cx: 24, cy: 38, r: 5.2 }, '#4D7CFF'],
    ['circle', { cx: 22.3, cy: 8.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 22.3, cy: 22.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 22.3, cy: 36.3, r: 1.3, opacity: 0.55 }, '#FFFFFF'],
  ],
  envelope: [
    ['rect', { x: 5, y: 10, width: 38, height: 28, rx: 5.5 }, '#FFBE2E'],
    [
      'path',
      {
        d: 'M5 15.5A5.5 5.5 0 0 1 10.5 10h27a5.5 5.5 0 0 1 5.5 5.5L27.3 26.4a5.6 5.6 0 0 1-6.6 0z',
      },
      '#F59A0B',
    ],
    ['circle', { cx: 24, cy: 26, r: 4.6 }, '#FF4D94'],
    ['circle', { cx: 22.6, cy: 24.6, r: 1.2 }, '#FFFFFF'],
  ],
  'envelope-circle-check': [
    ['rect', { x: 4, y: 8, width: 34, height: 25, rx: 5 }, '#FFBE2E'],
    [
      'path',
      { d: 'M4 13A5 5 0 0 1 9 8h24a5 5 0 0 1 5 5L24.2 23.1a5 5 0 0 1-6.4 0z' },
      '#F59A0B',
    ],
    ['circle', { cx: 35, cy: 34.5, r: 10 }, '#21C45D'],
    ['path', { d: 'M30.4 34.8l3.1 3.1 5.6-6' }, '#FFFFFF', 'thick'],
  ],
  'envelope-open': [
    [
      'path',
      {
        d: 'M5 20.1L20.8 8.4a5.4 5.4 0 0 1 6.4 0L43 20.1V35.5a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5z',
      },
      '#F59A0B',
    ],
    ['rect', { x: 13.5, y: 14.5, width: 21, height: 20, rx: 2.5 }, '#FFFFFF'],
    ['path', { d: 'M18.5 20.5H29.5' }, '#FF4D94', 'thick'],
    [
      'path',
      {
        d: 'M5 23L21 32.9a5.6 5.6 0 0 0 6 0L43 23V35.5a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5z',
      },
      '#FFBE2E',
    ],
  ],
  expand: [
    ['path', { d: 'M8.5 18V12.5A4 4 0 0 1 12.5 8.5H18' }, '#6C63FF', 'thick'],
    ['path', { d: 'M30 8.5H35.5A4 4 0 0 1 39.5 12.5V18' }, '#6C63FF', 'thick'],
    ['path', { d: 'M39.5 30V35.5A4 4 0 0 1 35.5 39.5H30' }, '#6C63FF', 'thick'],
    ['path', { d: 'M18 39.5H12.5A4 4 0 0 1 8.5 35.5V30' }, '#6C63FF', 'thick'],
    ['rect', { x: 18, y: 18, width: 12, height: 12, rx: 3.5 }, '#FFC23D'],
    ['path', { d: 'M21 21.5V24', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  'export-csv': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M22.6 37.1Q24 38.5 25.4 37.1L31.6 30.9Q33 29.5 31 29.5H17Q15 29.5 16.4 30.9Z',
      },
      '#21C45D',
    ],
    ['rect', { x: 21, y: 20, width: 6, height: 12, rx: 2.2 }, '#21C45D'],
  ],
  eye: [
    [
      'path',
      {
        d: 'M3.5 24C9 14.5 16 10 24 10C32 10 39 14.5 44.5 24C39 33.5 32 38 24 38C16 38 9 33.5 3.5 24Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M33 24C33 28.97 28.97 33 24 33C19.03 33 15 28.97 15 24C15 19.03 19.03 15 24 15C28.97 15 33 19.03 33 24Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      {
        d: 'M28.6 24C28.6 26.54 26.54 28.6 24 28.6C21.46 28.6 19.4 26.54 19.4 24C19.4 21.46 21.46 19.4 24 19.4C26.54 19.4 28.6 21.46 28.6 24Z',
      },
      '#2D2A5C',
    ],
    [
      'path',
      {
        d: 'M27.8 21.8C27.8 22.68 27.08 23.4 26.2 23.4C25.32 23.4 24.6 22.68 24.6 21.8C24.6 20.92 25.32 20.2 26.2 20.2C27.08 20.2 27.8 20.92 27.8 21.8Z',
      },
      '#FFFFFF',
    ],
  ],
  'eye-slash': [
    [
      'path',
      {
        d: 'M17.15 11.21C19.33 10.4 21.62 10 24 10C32 10 39 14.5 44.5 24C42.56 27.35 40.44 30.08 38.15 32.21Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M30.85 36.79C28.67 37.6 26.38 38 24 38C16 38 9 33.5 3.5 24C5.44 20.65 7.56 17.92 9.85 15.79Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M21.34 15.4C22.18 15.14 23.07 15 24 15C28.97 15 33 19.03 33 24C33 24.93 32.86 25.82 32.6 26.66Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      {
        d: 'M26.66 32.6C25.82 32.86 24.93 33 24 33C19.03 33 15 28.97 15 24C15 23.07 15.14 22.18 15.4 21.34Z',
      },
      '#FFFFFF',
    ],
    ['path', { d: 'M7.5 7.5L40.5 40.5' }, ['--cl-base', '#2D2A5C'], 'thick'],
  ],
  file: [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#4D7CFF',
    ],
  ],
  'file-arrow-up': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#4D7CFF',
    ],
    ['path', { d: 'M24 37V24.5M18.6 29.4L24 24l5.4 5.4' }, '#4D7CFF', 'thick'],
  ],
  'file-csv': [
    [
      'path',
      {
        d: 'M12 9Q12 4 17 4L29.4 4Q31 4 32.13 5.13L40.87 13.87Q42 15 42 16.6L42 39Q42 44 37 44L17 44Q12 44 12 39Z',
      },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M15 9Q15 7 17 7L29.26 7Q29.76 7 30.11 7.35L38.65 15.89Q39 16.24 39 16.74L39 39Q39 41 37 41L17 41Q15 41 15 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M29.76 6L29.76 13.64Q29.76 16.24 32.36 16.24L40 16.24Z' },
      '#21C45D',
    ],
    ['rect', { x: 5, y: 25.5, width: 30, height: 14, rx: 3.5 }, '#159A47'],
    [
      'path',
      {
        d: 'M14.2 29.6A3.6 3.6 0 1 0 14.2 35.4M22.6 29.9c-.5-.6-1.3-.9-2.2-.9-1.3 0-2.3.8-2.3 1.8 0 2.6 4.8 1.4 4.8 4.1 0 1.1-1 1.9-2.4 1.9-1 0-1.9-.4-2.4-1.1M25.6 29l2.6 7 2.6-7',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'file-export': [
    [
      'path',
      {
        d: 'M12.5 4L25 4Q26.5 4 27.56 5.06L34.94 12.44Q36 13.5 36 15L36 39.5Q36 44 31.5 44L12.5 44Q8 44 8 39.5L8 8.5Q8 4 12.5 4Z',
      },
      '#16C7A6',
    ],
    ['path', { d: 'M26.5 4.6V10.5A3 3 0 0 0 29.5 13.5H35.4Z' }, '#A7F0E1'],
    ['rect', { x: 13, y: 17.5, width: 9, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['rect', { x: 13, y: 23.5, width: 13, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    [
      'rect',
      { x: 17, y: 30.6, width: 19, height: 5.4, rx: 2.7 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M35.43 26.23L43.07 31.87Q45 33.3 43.07 34.73L35.43 40.37Q33.5 41.8 33.5 39.4L33.5 27.2Q33.5 24.8 35.43 26.23Z',
      },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  'file-lines': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#4D7CFF',
    ],
    ['rect', { x: 15, y: 20, width: 18, height: 3.4, rx: 1.7 }, '#4D7CFF'],
    ['rect', { x: 15, y: 26.6, width: 18, height: 3.4, rx: 1.7 }, '#4D7CFF'],
    ['rect', { x: 15, y: 33.2, width: 11, height: 3.4, rx: 1.7 }, '#4D7CFF'],
  ],
  'file-magnifying-glass': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#4D7CFF',
    ],
    ['rect', { x: 15, y: 12, width: 9, height: 3.4, rx: 1.7 }, '#4D7CFF'],
    ['rect', { x: 15, y: 18.6, width: 8, height: 3.4, rx: 1.7 }, '#4D7CFF'],
    [
      'rect',
      {
        x: 31.6,
        y: 35.86,
        width: 12.6,
        height: 4.6,
        rx: 2.3,
        transform: 'rotate(45 37.9 38.16)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    ['circle', { cx: 28, cy: 29, r: 9 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 28, cy: 29, r: 6.2 }, '#7DF1E5'],
    ['path', { d: 'M24.4 28.2A4 4 0 0 1 27.2 25.4' }, '#FFFFFF', 'line'],
  ],
  'file-spreadsheet': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#21C45D',
    ],
    ['rect', { x: 12, y: 19.5, width: 24, height: 3 }, '#21C45D'],
    ['rect', { x: 12, y: 29.5, width: 24, height: 3 }, '#21C45D'],
    ['rect', { x: 21.5, y: 19.5, width: 3, height: 22 }, '#21C45D'],
  ],
  filter: [
    [
      'path',
      {
        d: 'M18.4 23H29.6V35.6C29.6 36.8 29 37.8 28 38.4L22 41.8C20.4 42.7 18.4 41.6 18.4 39.8Z',
      },
      '#0E9384',
    ],
    [
      'path',
      {
        d: 'M10.6 7.5H37.4C40.5 7.5 42.1 11.2 40 13.5L29.6 25H18.4L8 13.5C5.9 11.2 7.5 7.5 10.6 7.5Z',
      },
      '#20C9B5',
    ],
    ['path', { d: 'M12 11.4H18.5', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  fingerprint: [
    ['path', { d: 'M8 32.5V23.5a16 16 0 0 1 32 0V28' }, '#7C5CFF', 'thick'],
    [
      'path',
      { d: 'M16 40.5V23.5a8 8 0 0 1 16 0v5.5c0 4-.6 7.6-1.8 10.6' },
      '#A893FF',
      'thick',
    ],
    ['path', { d: 'M24 23.5v7c0 4-.9 7.6-2.6 10.6' }, '#7C5CFF', 'thick'],
  ],
  flask: [
    [
      'path',
      {
        d: 'M19.5 7h9v11.4l10.7 19.4c1.5 2.7-.5 6.2-3.6 6.2H12.4c-3.1 0-5.1-3.5-3.6-6.2L19.5 18.4z',
      },
      '#C6F07F',
    ],
    [
      'path',
      {
        d: 'M14.3 28h19.4l5.5 9.8c1.5 2.7-.5 6.2-3.6 6.2H12.4c-3.1 0-5.1-3.5-3.6-6.2z',
      },
      '#7CC71E',
    ],
    ['rect', { x: 16, y: 4, width: 16, height: 5, rx: 2.5 }, '#3F9416'],
    ['circle', { cx: 20, cy: 36.5, r: 2.4 }, '#FFFFFF'],
    ['circle', { cx: 27.5, cy: 32.5, r: 1.6 }, '#FFFFFF'],
    ['circle', { cx: 25.5, cy: 21.5, r: 1.7 }, '#7CC71E'],
    ['circle', { cx: 22.5, cy: 14.5, r: 1.2 }, '#7CC71E'],
  ],
  'floppy-disk': [
    [
      'path',
      {
        d: 'M10 5L32 5Q34.5 5 36.27 6.77L41.23 11.73Q43 13.5 43 16L43 38Q43 43 38 43L10 43Q5 43 5 38L5 10Q5 5 10 5Z',
      },
      '#21C45D',
    ],
    [
      'path',
      { d: 'M13 5H31V15A3 3 0 0 1 28 18H16A3 3 0 0 1 13 15Z' },
      '#0E8F45',
    ],
    ['rect', { x: 23.8, y: 8.3, width: 4.4, height: 6.4, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 11, y: 25, width: 26, height: 14, rx: 3.5 }, '#FFFFFF'],
    ['rect', { x: 15.5, y: 30.5, width: 17, height: 3, rx: 1.5 }, '#A6EBC0'],
  ],
  folder: [
    [
      'path',
      {
        d: 'M4 12Q4 7 9 7L18.8 7Q21 7 22.56 8.56L23.94 9.94Q25.5 11.5 27.7 11.5L39 11.5Q44 11.5 44 16.5L44 37Q44 42 39 42L9 42Q4 42 4 37Z',
      },
      '#F08E1E',
    ],
    ['rect', { x: 4, y: 16, width: 40, height: 26, rx: 5 }, '#FFB547'],
    ['rect', { x: 9, y: 21, width: 8, height: 3.2, rx: 1.6 }, '#FFFFFF'],
  ],
  'folder-open': [
    [
      'path',
      {
        d: 'M4 12Q4 7 9 7L18.8 7Q21 7 22.56 8.56L23.94 9.94Q25.5 11.5 27.7 11.5L35 11.5Q40 11.5 40 16.5L40 37Q40 42 35 42L9 42Q4 42 4 37Z',
      },
      '#F08E1E',
    ],
    ['rect', { x: 9, y: 13.5, width: 25, height: 14, rx: 2.2 }, '#FFFFFF'],
    [
      'path',
      {
        d: 'M11.46 21.81Q12.5 19 15.5 19L43 19Q46 19 45.24 21.9L41.14 37.65Q40 42 35.5 42L8.5 42Q4 42 5.56 37.78Z',
      },
      '#FFB547',
    ],
  ],
  'folder-plus': [
    [
      'path',
      {
        d: 'M3 12Q3 7 8 7L17.8 7Q20 7 21.56 8.56L22.94 9.94Q24.5 11.5 26.7 11.5L36 11.5Q41 11.5 41 16.5L41 35Q41 40 36 40L8 40Q3 40 3 35Z',
      },
      '#F08E1E',
    ],
    ['rect', { x: 3, y: 14, width: 38, height: 26, rx: 5 }, '#FFB547'],
    ['rect', { x: 8, y: 19, width: 8, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['circle', { cx: 36, cy: 35, r: 10 }, '#21C45D'],
    ['path', { d: 'M36 30.2v9.6M31.2 35h9.6' }, '#FFFFFF', 'thick'],
  ],
  gauge: [
    [
      'path',
      {
        d: 'M6.78 37.03A19 19 0 0 1 10.98 15.16L15.78 20.26A12 12 0 0 0 13.12 34.07A3.5 3.5 0 0 1 6.78 37.03Z',
      },
      '#FF6FAE',
    ],
    [
      'path',
      {
        d: 'M12.87 13.6A19 19 0 0 1 35.13 13.6L31.03 19.28A12 12 0 0 0 16.97 19.28Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M37.02 15.16A19 19 0 0 1 41.22 37.03A3.5 3.5 0 0 1 34.88 34.07A12 12 0 0 0 32.22 20.26Z',
      },
      '#4D7CFF',
    ],
    ['path', { d: 'M24 29L32.27 22.54' }, ['--cl-base', '#2D2A5C'], 'thick'],
    ['circle', { cx: 24, cy: 29, r: 4.6 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 24, cy: 29, r: 1.6 }, '#FFFFFF'],
  ],
  gear: [
    [
      'path',
      {
        d: 'M26.45 8.28L27.64 6.09Q28.5 4.51 30.16 5.2L32.94 6.35Q34.6 7.04 34.09 8.77L33.38 11.15Q32.99 12.5 33.98 13.48L34.52 14.02Q35.5 15.01 36.85 14.62L39.23 13.91Q40.96 13.4 41.65 15.06L42.8 17.84Q43.49 19.5 41.91 20.36L39.72 21.55Q38.49 22.22 38.49 23.62L38.49 24.38Q38.49 25.78 39.72 26.45L41.91 27.64Q43.49 28.5 42.8 30.16L41.65 32.94Q40.96 34.6 39.23 34.09L36.85 33.38Q35.5 32.99 34.52 33.98L33.98 34.52Q32.99 35.5 33.38 36.85L34.09 39.23Q34.6 40.96 32.94 41.65L30.16 42.8Q28.5 43.49 27.64 41.91L26.45 39.72Q25.78 38.49 24.38 38.49L23.62 38.49Q22.22 38.49 21.55 39.72L20.36 41.91Q19.5 43.49 17.84 42.8L15.06 41.65Q13.4 40.96 13.91 39.23L14.62 36.85Q15.01 35.5 14.02 34.52L13.48 33.98Q12.5 32.99 11.15 33.38L8.77 34.09Q7.04 34.6 6.35 32.94L5.2 30.16Q4.51 28.5 6.09 27.64L8.28 26.45Q9.51 25.78 9.51 24.38L9.51 23.62Q9.51 22.22 8.28 21.55L6.09 20.36Q4.51 19.5 5.2 17.84L6.35 15.06Q7.04 13.4 8.77 13.91L11.15 14.62Q12.5 15.01 13.48 14.02L14.02 13.48Q15.01 12.5 14.62 11.15L13.91 8.77Q13.4 7.04 15.06 6.35L17.84 5.2Q19.5 4.51 20.36 6.09L21.55 8.28Q22.22 9.51 23.62 9.51L24.38 9.51Q25.78 9.51 26.45 8.28z',
      },
      '#7C8DB5',
    ],
    ['circle', { cx: 24, cy: 24, r: 8.6 }, '#E9EDF7'],
    ['circle', { cx: 24, cy: 24, r: 4.2 }, '#46557A'],
    ['path', { d: 'M15.5 15.2A12.6 12.6 0 0 1 21 12.1' }, '#B8C3DD', 'thick'],
  ],
  gem: [
    ['path', { d: 'M14 8h20l9 11H5z' }, '#B79CFF'],
    ['path', { d: 'M19.5 8h9L31 19H17z' }, '#E1D6FF'],
    ['path', { d: 'M5 19h38L24 42z' }, '#7C5CFF'],
    ['path', { d: 'M17 19h14L24 42z' }, '#9D84FF'],
    ['path', { d: 'M10.4 16.6l4.4-5.4' }, '#FFFFFF', 'line'],
  ],
  gift: [
    [
      'path',
      {
        d: 'M24 15.5C21.5 9.5 17 6.5 14 8.5C11.5 10.3 12.5 14 16 15C18.5 15.7 21.5 15.5 24 15.5z',
        transform: 'translate(0 -1.4)',
      },
      '#FF4D94',
    ],
    [
      'path',
      {
        d: 'M24 15.5C26.5 9.5 31 6.5 34 8.5C36.5 10.3 35.5 14 32 15C29.5 15.7 26.5 15.5 24 15.5z',
        transform: 'translate(0 -1.4)',
      },
      '#FF4D94',
    ],
    [
      'rect',
      {
        x: 9,
        y: 22,
        width: 30,
        height: 21,
        rx: 3.5,
        transform: 'translate(0 -1.4)',
      },
      '#FFC23D',
    ],
    [
      'rect',
      {
        x: 6,
        y: 15,
        width: 36,
        height: 9,
        rx: 3,
        transform: 'translate(0 -1.4)',
      },
      '#FFB547',
    ],
    [
      'rect',
      { x: 20.5, y: 15, width: 7, height: 28, transform: 'translate(0 -1.4)' },
      '#FF4D94',
    ],
    [
      'path',
      { d: 'M13.5 28.5V37', transform: 'translate(0 -1.4)' },
      '#FFFFFF',
      'line',
    ],
  ],
  globe: [
    ['circle', { cx: 24, cy: 24, r: 19.5 }, '#4D7CFF'],
    ['ellipse', { cx: 24, cy: 24, rx: 7.5, ry: 17 }, '#FFFFFF', 'thick'],
    ['path', { d: 'M7.5 24H40.5' }, '#FFFFFF', 'thick'],
  ],
  'grid-2': [
    ['rect', { x: 7, y: 7, width: 15, height: 15, rx: 4.5 }, '#FF6FAE'],
    ['rect', { x: 26, y: 7, width: 15, height: 15, rx: 4.5 }, '#FFC23D'],
    ['rect', { x: 7, y: 26, width: 15, height: 15, rx: 4.5 }, '#4D7CFF'],
    ['rect', { x: 26, y: 26, width: 15, height: 15, rx: 4.5 }, '#21C45D'],
  ],
  'grip-vertical': [
    ['circle', { cx: 18, cy: 12, r: 4 }, '#9580FF'],
    ['circle', { cx: 30, cy: 12, r: 4 }, '#9580FF'],
    ['circle', { cx: 18, cy: 24, r: 4 }, '#9580FF'],
    ['circle', { cx: 30, cy: 24, r: 4 }, '#9580FF'],
    ['circle', { cx: 18, cy: 36, r: 4 }, '#9580FF'],
    ['circle', { cx: 30, cy: 36, r: 4 }, '#9580FF'],
    ['circle', { cx: 16.6, cy: 10.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 28.6, cy: 10.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 16.6, cy: 22.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 28.6, cy: 22.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 16.6, cy: 34.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 28.6, cy: 34.6, r: 1.1, opacity: 0.55 }, '#FFFFFF'],
  ],
  hand: [
    [
      'rect',
      {
        x: 8.75,
        y: 19,
        width: 6.5,
        height: 17,
        rx: 3.25,
        transform: 'rotate(-38 12.0 27.5)',
      },
      '#FFB547',
    ],
    ['rect', { x: 14.0, y: 9, width: 6, height: 20, rx: 3.0 }, '#FFB547'],
    ['rect', { x: 22.0, y: 5.5, width: 6, height: 22, rx: 3.0 }, '#FFB547'],
    ['rect', { x: 30.0, y: 7.5, width: 6, height: 20, rx: 3.0 }, '#FFB547'],
    ['rect', { x: 38.0, y: 12, width: 5.5, height: 16, rx: 2.75 }, '#FFB547'],
    ['rect', { x: 14.0, y: 19, width: 29.5, height: 24, rx: 9.5 }, '#FFB547'],
    ['path', { d: 'M25.0 9.5v5' }, '#FFFFFF', 'line'],
  ],
  'hand-off': [
    ['rect', { x: 4, y: 25, width: 17, height: 17, rx: 5 }, '#8BE3F5'],
    ['rect', { x: 27, y: 25, width: 17, height: 17, rx: 5 }, '#0BAFD3'],
    ['path', { d: 'M12.5 20.5C12.5 9 35.5 9 35.5 15.5' }, '#0784A8', 'thick'],
    [
      'path',
      {
        d: 'M30.6 15.4h9.8c.9 0 1.4 1 .9 1.7l-4.9 5.9c-.4.5-1.2.5-1.6 0l-4.9-5.9c-.5-.7 0-1.7.7-1.7z',
      },
      '#0784A8',
    ],
    ['rect', { x: 8.5, y: 30, width: 8, height: 2.8, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 8.5, y: 34.6, width: 5.5, height: 2.8, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 31.5, y: 30, width: 8, height: 2.8, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 31.5, y: 34.6, width: 5.5, height: 2.8, rx: 1.4 }, '#FFFFFF'],
  ],
  handshake: [
    ['rect', { x: 2.5, y: 18.0, width: 7.5, height: 18.5, rx: 2.6 }, '#7C5CFF'],
    ['rect', { x: 38, y: 12.0, width: 7.5, height: 18.5, rx: 2.6 }, '#4D7CFF'],
    [
      'path',
      {
        d: 'M17.09 34L15.25 34A6.75 6.75 0 0 1 8.5 27.25L8.5 27.25A6.75 6.75 0 0 1 15.25 20.5L15.3 20.5A8.7 8.7 0 0 0 15.3 20.5L15.3 20.5A8.7 8.7 0 0 0 15.3 20.5L15.3 29.1A7.6 7.6 0 0 0 17.09 34Z',
      },
      '#FFD58F',
    ],
    [
      'path',
      {
        d: 'M22.87 13.29A3.3 3.3 0 0 1 26.33 18.91L14.33 26.31A3.3 3.3 0 0 1 10.87 20.69L22.87 13.29Z',
      },
      '#FFD58F',
    ],
    [
      'path',
      {
        d: 'M29.68 14L33 14A6.5 6.5 0 0 1 39.5 20.5L39.5 20.5A6.5 6.5 0 0 1 33 27L24 27A6.5 6.5 0 0 1 19.84 25.5L27.49 20.78A5.5 5.5 0 0 0 29.68 14Z',
      },
      '#FFB547',
    ],
    [
      'path',
      {
        d: 'M29.93 17.44A5.4 5.4 0 0 1 30.3 19.4L30.3 29.1A5.4 5.4 0 0 1 24.9 34.5L22.9 34.5A5.4 5.4 0 0 1 17.5 29.1L17.5 26.94L27.49 20.78A5.5 5.5 0 0 0 29.93 17.44Z',
      },
      '#FFB547',
    ],
  ],
  hashtag: [
    ['rect', { x: 5.7, y: 13.7, width: 36.6, height: 5.6, rx: 2.8 }, '#FFB547'],
    ['rect', { x: 5.7, y: 28.7, width: 36.6, height: 5.6, rx: 2.8 }, '#FFB547'],
    [
      'rect',
      {
        x: -0.98,
        y: 21.2,
        width: 36.97,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(98.8 17.5 24)',
      },
      '#FF8A3D',
    ],
    [
      'rect',
      {
        x: 12.02,
        y: 21.2,
        width: 36.97,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(98.8 30.5 24)',
      },
      '#FF8A3D',
    ],
  ],
  heading: [
    ['rect', { x: 13.5, y: 20.8, width: 21, height: 6.4 }, '#FF6FAE'],
    ['rect', { x: 10.3, y: 6.8, width: 6.4, height: 34.4, rx: 3.2 }, '#6C63FF'],
    ['rect', { x: 31.3, y: 6.8, width: 6.4, height: 34.4, rx: 3.2 }, '#6C63FF'],
    ['path', { d: 'M12.6 11V16', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  headset: [
    [
      'path',
      { d: 'M9.5 28v-5a14.5 14.5 0 0 1 29 0v5' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    ['rect', { x: 5, y: 23, width: 10, height: 15, rx: 5 }, '#FF4D94'],
    ['rect', { x: 33, y: 23, width: 10, height: 15, rx: 5 }, '#FF4D94'],
    [
      'path',
      { d: 'M38 37.5c0 3.6-2.6 5.8-6.4 5.8H27' },
      ['--cl-base', '#2D2A5C'],
      'line',
    ],
    ['rect', { x: 19, y: 40, width: 9, height: 6, rx: 3 }, '#7C5CFF'],
    ['path', { d: 'M8 27v4' }, '#FFFFFF', 'line'],
  ],
  hospital: [
    ['rect', { x: 4, y: 18, width: 40, height: 25, rx: 4 }, '#8FD3FF'],
    ['rect', { x: 13, y: 5, width: 22, height: 38, rx: 4.5 }, '#4D7CFF'],
    ['path', { d: 'M24 11.5v13M17.5 18h13' }, '#FFFFFF', 'thick'],
    ['rect', { x: 20, y: 32, width: 8, height: 11, rx: 2 }, '#8FD3FF'],
  ],
  house: [
    ['rect', { x: 31, y: 9, width: 5, height: 10, rx: 1.5 }, '#E2553A'],
    ['rect', { x: 11, y: 21, width: 26, height: 19, rx: 3.5 }, '#FFD58F'],
    [
      'path',
      {
        d: 'M21.4 8.6a4 4 0 0 1 5.2 0l15 12.8c1.4 1.2.6 3.6-1.3 3.6H7.7c-1.9 0-2.7-2.4-1.3-3.6z',
      },
      '#FF6B4A',
    ],
    ['circle', { cx: 24, cy: 17.8, r: 2.6 }, '#62D2F5'],
    ['rect', { x: 20.5, y: 29, width: 7, height: 11, rx: 2 }, '#FF9A3C'],
    ['circle', { cx: 25.4, cy: 35, r: 0.9 }, '#A0520F'],
  ],
  'house-user': [
    ['rect', { x: 31, y: 9, width: 5, height: 10, rx: 1.5 }, '#E2553A'],
    ['rect', { x: 11, y: 21, width: 26, height: 19, rx: 3.5 }, '#FFD58F'],
    [
      'path',
      {
        d: 'M21.4 8.6a4 4 0 0 1 5.2 0l15 12.8c1.4 1.2.6 3.6-1.3 3.6H7.7c-1.9 0-2.7-2.4-1.3-3.6z',
      },
      '#FF6B4A',
    ],
    ['circle', { cx: 24, cy: 17.8, r: 2.6 }, '#62D2F5'],
    ['rect', { x: 20.5, y: 29, width: 7, height: 11, rx: 2 }, '#FF9A3C'],
    ['circle', { cx: 25.4, cy: 35, r: 0.9 }, '#A0520F'],
  ],
  'id-card': [
    ['rect', { x: 3, y: 8, width: 36, height: 28, rx: 6 }, '#6FB6FF'],
    ['circle', { cx: 14, cy: 18, r: 4.5 }, '#FFFFFF'],
    [
      'path',
      { d: 'M6.8 31.5c0-4.3 3.2-7.2 7.2-7.2s7.2 2.9 7.2 7.2z' },
      '#FFFFFF',
    ],
    ['rect', { x: 24.5, y: 15, width: 10, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 24.5, y: 21, width: 6.5, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 27, y: 25, width: 18, height: 18, rx: 5 }, '#0A66C2'],
    ['circle', { cx: 32.2, cy: 30, r: 1.6 }, '#FFFFFF'],
    ['path', { d: 'M32.2 33.8v4.8' }, '#FFFFFF', 'line'],
    [
      'path',
      { d: 'M36.2 38.6v-4.8m0 2.2c0-1.4 1-2.3 2.2-2.3s2.2.9 2.2 2.3v2.6' },
      '#FFFFFF',
      'line',
    ],
  ],
  'identify-person': [
    ['rect', { x: 3, y: 8, width: 36, height: 28, rx: 6 }, '#6FB6FF'],
    ['circle', { cx: 14, cy: 18, r: 4.5 }, '#FFFFFF'],
    [
      'path',
      { d: 'M6.8 31.5c0-4.3 3.2-7.2 7.2-7.2s7.2 2.9 7.2 7.2z' },
      '#FFFFFF',
    ],
    ['rect', { x: 24.5, y: 15, width: 10, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 24.5, y: 21, width: 6.5, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['rect', { x: 27, y: 25, width: 18, height: 18, rx: 5 }, '#0A66C2'],
    ['circle', { cx: 32.2, cy: 30, r: 1.6 }, '#FFFFFF'],
    ['path', { d: 'M32.2 33.8v4.8' }, '#FFFFFF', 'line'],
    [
      'path',
      { d: 'M36.2 38.6v-4.8m0 2.2c0-1.4 1-2.3 2.2-2.3s2.2.9 2.2 2.3v2.6' },
      '#FFFFFF',
      'line',
    ],
  ],
  image: [
    ['rect', { x: 4, y: 8, width: 40, height: 32, rx: 6 }, '#8FD3FF'],
    ['circle', { cx: 33, cy: 17.5, r: 4.2 }, '#FFC23D'],
    [
      'path',
      { d: 'M19 40L31 25.8c1.2-1.4 3.4-1.4 4.6 0L44 34a6 6 0 0 1-6 6z' },
      '#16C7A6',
    ],
    [
      'path',
      { d: 'M4 30.5L13.9 21.3c1.2-1.1 3-1.1 4.2 0L36.5 40H10a6 6 0 0 1-6-6z' },
      '#21C45D',
    ],
  ],
  'import-crm': [
    [
      'path',
      {
        d: 'M4 9.6V31.4A13 5.2 0 0 0 23.61 35.88A11.8 11.8 0 0 1 30 25.01V9.6Z',
      },
      '#7C5CFF',
    ],
    [
      'path',
      { d: 'M4 18.6A13 5.2 0 0 0 30 18.6v3.4A13 5.2 0 0 1 4 22Z' },
      '#A893FF',
    ],
    ['ellipse', { cx: 17, cy: 9.6, rx: 13, ry: 5.2 }, '#A893FF'],
    ['rect', { x: 26.5, y: 32.5, width: 11, height: 6, rx: 2.2 }, '#21C45D'],
    [
      'path',
      {
        d: 'M42.6 34.1Q44 35.5 42.6 36.9L36.4 43.1Q35 44.5 35 42.5V28.5Q35 26.5 36.4 27.9Z',
      },
      '#21C45D',
    ],
  ],
  'import-csv': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#21C45D',
    ],
    [
      'path',
      {
        d: 'M22.6 20.9Q24 19.5 25.4 20.9L31.6 27.1Q33 28.5 31 28.5H17Q15 28.5 16.4 27.1Z',
      },
      '#21C45D',
    ],
    ['rect', { x: 21, y: 26, width: 6, height: 12, rx: 2.2 }, '#21C45D'],
  ],
  inbox: [
    ['path', { d: 'M10.5 18.5h27l4.5 8.5H6z' }, '#0D9F87'],
    ['rect', { x: 13, y: 7, width: 22, height: 16, rx: 3 }, '#FF7EB6'],
    ['path', { d: 'M14.2 9l9.8 7 9.8-7' }, '#FFFFFF', 'line'],
    [
      'path',
      {
        d: 'M6 27h9.6a3 3 0 0 1 2.7 1.7l.8 1.6a3 3 0 0 0 2.7 1.7h4.4a3 3 0 0 0 2.7-1.7l.8-1.6a3 3 0 0 1 2.7-1.7H42v9.5a5.5 5.5 0 0 1-5.5 5.5h-25A5.5 5.5 0 0 1 6 36.5z',
      },
      '#16C7A6',
    ],
    ['circle', { cx: 38.5, cy: 9.5, r: 4 }, '#FF4D5E'],
  ],
  industry: [
    ['rect', { x: 5, y: 4.5, width: 10, height: 24, rx: 3 }, '#D9531A'],
    [
      'path',
      {
        d: 'M5 24h10l11.6-7.2c1.3-.8 2.9.1 2.9 1.6V24l11.6-7.2c1.3-.8 2.9.1 2.9 1.6V40a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3z',
      },
      '#FF7A2A',
    ],
    ['rect', { x: 10, y: 30.5, width: 5.5, height: 5.5, rx: 1.5 }, '#FFE3BF'],
    [
      'rect',
      { x: 21.25, y: 30.5, width: 5.5, height: 5.5, rx: 1.5 },
      '#FFE3BF',
    ],
    ['rect', { x: 32.5, y: 30.5, width: 5.5, height: 5.5, rx: 1.5 }, '#FFE3BF'],
  ],
  italic: [
    [
      'rect',
      {
        x: 8.48,
        y: 21,
        width: 31.05,
        height: 6,
        rx: 3,
        transform: 'rotate(104.93 24 24)',
      },
      '#6C63FF',
    ],
    ['rect', { x: 19, y: 6, width: 18, height: 6, rx: 3 }, '#FF6FAE'],
    ['rect', { x: 11, y: 36, width: 18, height: 6, rx: 3 }, '#FF6FAE'],
  ],
  'job-history': [
    [
      'path',
      { d: 'M17.5 14v-3a3.5 3.5 0 0 1 3.5-3.5h6a3.5 3.5 0 0 1 3.5 3.5v3' },
      '#8E5526',
      'thick',
    ],
    ['rect', { x: 4, y: 13, width: 36, height: 26, rx: 6 }, '#D08A45'],
    ['rect', { x: 4, y: 22.5, width: 36, height: 3, rx: 0 }, '#B8743A'],
    ['rect', { x: 18.5, y: 20.5, width: 7, height: 7, rx: 2 }, '#FFE3BF'],
    ['circle', { cx: 36, cy: 35, r: 10 }, '#3FB6F5'],
    ['path', { d: 'M36 30.5V35l3 2' }, '#FFFFFF', 'line'],
  ],
  key: [
    [
      'path',
      {
        d: 'M30 24h11.5M35.5 24v6.5M40.5 24v4.5',
        transform: 'rotate(-45 24 24)',
      },
      '#5B2FD6',
      'thick',
    ],
    [
      'rect',
      {
        x: 18,
        y: 21,
        width: 23,
        height: 6,
        rx: 3,
        transform: 'rotate(-45 24 24)',
      },
      '#B79BFF',
    ],
    [
      'circle',
      { cx: 14.5, cy: 24, r: 10.5, transform: 'rotate(-45 24 24)' },
      '#8B5CF6',
    ],
    [
      'path',
      { d: 'M15.4 27.3l-3.4 3.4 3.4 3.4M19.2 27.3l3.4 3.4-3.4 3.4' },
      '#FFFFFF',
      'line',
    ],
  ],
  keyboard: [
    ['rect', { x: 3, y: 10, width: 42, height: 28, rx: 6.5 }, '#8A73FF'],
    ['rect', { x: 8.9, y: 14.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 17.3, y: 14.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 25.7, y: 14.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 34.1, y: 14.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 13.1, y: 21.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 21.5, y: 21.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 29.9, y: 21.5, width: 5, height: 5, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 13, y: 28.5, width: 22, height: 5, rx: 2.5 }, '#FFC23D'],
  ],
  language: [
    [
      'path',
      {
        d: 'M19.8 27L9 27A6 6 0 0 1 3 21L3 10A6 6 0 0 1 9 4L20 4A6 6 0 0 1 26 10L26 19.05A8.2 8.2 0 0 0 19.8 27Z',
      },
      '#8FD3FF',
    ],
    ['path', { d: 'M8 11.5h13V20H8zM14.5 7.2v17.2' }, '#2F80FF', 'line'],
    ['rect', { x: 22, y: 21, width: 23, height: 23, rx: 6 }, '#4D7CFF'],
    [
      'path',
      { d: 'M27.7 38.8L33.5 25.6l5.8 13.2M29.9 34.2h7.2' },
      '#FFFFFF',
      'thick',
    ],
  ],
  'layer-group': [
    [
      'path',
      {
        d: 'M6.97 32.98L21.32 40.16Q24 41.5 26.68 40.16L41.03 32.98Q43 32 43 29.8L43 28.4Q43 26.8 41.57 27.52L25.79 35.41Q24 36.3 22.21 35.41L6.43 27.52Q5 26.8 5 28.4L5 29.8Q5 32 6.97 32.98Z',
      },
      '#2F80FF',
    ],
    [
      'path',
      {
        d: 'M6.97 24.98L21.32 32.16Q24 33.5 26.68 32.16L41.03 24.98Q43 24 43 21.8L43 20.4Q43 18.8 41.57 19.52L25.79 27.41Q24 28.3 22.21 27.41L6.43 19.52Q5 18.8 5 20.4L5 21.8Q5 24 6.97 24.98Z',
      },
      '#8FD3FF',
    ],
    [
      'path',
      {
        d: 'M26.68 7.84L40.32 14.66Q43 16 40.32 17.34L26.68 24.16Q24 25.5 21.32 24.16L7.68 17.34Q5 16 7.68 14.66L21.32 7.84Q24 6.5 26.68 7.84Z',
      },
      '#4D7CFF',
    ],
    ['path', { d: 'M13.5 15.2l6.5-3.2' }, '#FFFFFF', 'line'],
  ],
  'life-ring': [
    [
      'path',
      {
        d: 'M4 24a20 20 0 1 0 40 0a20 20 0 1 0 -40 0zM14.5 24a9.5 9.5 0 1 1 19.0 0a9.5 9.5 0 1 1 -19.0 0z',
        'fill-rule': 'evenodd',
      },
      '#FFD3D9',
    ],
    [
      'path',
      {
        d: 'M42.54 31.49A20 20 0 0 1 31.49 42.54L27.56 32.81A9.5 9.5 0 0 0 32.81 27.56Z',
      },
      '#FF4D5E',
    ],
    [
      'path',
      {
        d: 'M16.51 42.54A20 20 0 0 1 5.46 31.49L15.19 27.56A9.5 9.5 0 0 0 20.44 32.81Z',
      },
      '#FF4D5E',
    ],
    [
      'path',
      {
        d: 'M5.46 16.51A20 20 0 0 1 16.51 5.46L20.44 15.19A9.5 9.5 0 0 0 15.19 20.44Z',
      },
      '#FF4D5E',
    ],
    [
      'path',
      {
        d: 'M31.49 5.46A20 20 0 0 1 42.54 16.51L32.81 20.44A9.5 9.5 0 0 0 27.56 15.19Z',
      },
      '#FF4D5E',
    ],
    [
      'path',
      { d: 'M10.77 15.73A15.6 15.6 0 0 1 15.73 10.77' },
      '#FFFFFF',
      'line',
    ],
  ],
  lightbulb: [
    [
      'path',
      {
        d: 'M24 4.5C31.5 4.5 37.5 10.4 37.5 17.8C37.5 22 35.6 25.1 33.2 27.8C31.6 29.6 30.6 31.4 30.4 33.5H17.6C17.4 31.4 16.4 29.6 14.8 27.8C12.4 25.1 10.5 22 10.5 17.8C10.5 10.4 16.5 4.5 24 4.5Z',
      },
      '#FFB547',
    ],
    [
      'path',
      {
        d: 'M24 4.5 25.39 4.57 26.73 4.77 28.03 5.09 29.27 5.54 30.45 6.09 31.57 6.76 32.61 7.52 33.56 8.38 34.43 9.32 35.21 10.34 35.88 11.44 36.45 12.6 36.49 12.73 36.68 13.81 36.8 15.14 36.8 16.46 36.68 17.79 36.45 19.09 36.11 20.38 35.66 21.62 35.1 22.83 34.43 23.97 33.67 25.06 32.82 26.08 31.88 27.02 30.86 27.87 29.77 28.63 28.63 29.3 27.42 29.86 26.18 30.31 24.89 30.65 23.59 30.88 22.26 31 20.94 31 19.61 30.88 18.31 30.65 17.02 30.31 16.5 30.12 16.34 29.84 16.12 29.5 15.88 29.15 15.64 28.81 15.37 28.48 15.09 28.14 14.8 27.8 14.36 27.29 13.93 26.77 13.51 26.23 13.12 25.69 12.74 25.13 12.38 24.56 12.05 23.97 11.75 23.36 11.47 22.74 11.23 22.1 11.01 21.44 10.83 20.75 10.69 20.05 10.59 19.32 10.52 18.57 10.5 17.8 10.57 16.43 10.77 15.1 11.1 13.82 11.55 12.6 12.12 11.44 12.79 10.34 13.57 9.32 14.44 8.38 15.39 7.52 16.43 6.76 17.55 6.09 18.73 5.54 19.97 5.09 21.27 4.77 22.61 4.57Z',
      },
      '#FFC23D',
    ],
    ['path', { d: 'M15.12 14.74A8.2 8.2 0 0 1 20.47 9.2' }, '#FFFFFF', 'line'],
    [
      'rect',
      { x: 17.4, y: 35, width: 13.2, height: 4, rx: 2 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 20, y: 40, width: 8, height: 3.6, rx: 1.8 },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  'lightbulb-slash': [
    [
      'path',
      {
        d: 'M29.56 33.5 17.6 33.5 17.55 33.11 17.49 32.73 17.4 32.35 17.3 31.98 17.18 31.61 17.05 31.25 16.89 30.89 16.73 30.54 16.54 30.19 16.34 29.84 16.12 29.5 15.88 29.15 15.64 28.81 15.37 28.48 15.09 28.14 14.8 27.8 14.36 27.29 13.93 26.77 13.51 26.23 13.12 25.69 12.74 25.13 12.38 24.56 12.05 23.97 11.75 23.36 11.47 22.74 11.23 22.1 11.01 21.44 10.83 20.75 10.69 20.05 10.59 19.32 10.52 18.57 10.5 17.8 10.57 16.43 10.77 15.1 10.85 14.79Z',
      },
      '#FFB547',
    ],
    [
      'path',
      {
        d: 'M24 4.5 25.39 4.57 26.73 4.77 28.03 5.09 29.27 5.54 30.45 6.09 31.57 6.76 32.61 7.52 33.56 8.38 34.43 9.32 35.21 10.34 35.88 11.44 36.45 12.6 36.9 13.82 37.23 15.1 37.43 16.43 37.5 17.8 37.48 18.57 37.41 19.32 37.31 20.05 37.17 20.75 36.99 21.44 36.77 22.1 36.53 22.74 36.25 23.36 35.95 23.97 35.62 24.56 35.26 25.13 34.88 25.69 34.49 26.23 34.35 26.41 15.43 7.49 16.43 6.76 17.55 6.09 18.73 5.54 19.97 5.09 21.27 4.77 22.61 4.57Z',
      },
      '#FFB547',
    ],
    [
      'path',
      {
        d: 'M26.32 30.26 26.18 30.31 24.89 30.65 23.59 30.88 22.26 31 20.94 31 19.61 30.88 18.31 30.65 17.02 30.31 16.5 30.12 16.34 29.84 16.12 29.5 15.88 29.15 15.64 28.81 15.37 28.48 15.09 28.14 14.8 27.8 14.36 27.29 13.93 26.77 13.51 26.23 13.12 25.69 12.74 25.13 12.38 24.56 12.05 23.97 11.75 23.36 11.47 22.74 11.23 22.1 11.01 21.44 10.83 20.75 10.69 20.05 10.59 19.32 10.52 18.57 10.5 17.8 10.57 16.43 10.77 15.1 10.85 14.79Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M24 4.5 25.39 4.57 26.73 4.77 28.03 5.09 29.27 5.54 30.45 6.09 31.57 6.76 32.61 7.52 33.56 8.38 34.43 9.32 35.21 10.34 35.88 11.44 36.45 12.6 36.49 12.73 36.68 13.81 36.8 15.14 36.8 16.46 36.68 17.79 36.45 19.09 36.11 20.38 35.66 21.62 35.1 22.83 34.43 23.97 33.67 25.06 33.37 25.43 15.43 7.49 16.43 6.76 17.55 6.09 18.73 5.54 19.97 5.09 21.27 4.77 22.61 4.57Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M28.6 35 28.99 35.04 29.37 35.15 29.71 35.34 30.01 35.59 30.26 35.89 30.45 36.23 30.56 36.61 30.6 37 30.56 37.39 30.45 37.77 30.26 38.11 30.01 38.41 29.71 38.66 29.37 38.85 28.99 38.96 28.6 39 19.4 39 19.01 38.96 18.63 38.85 18.29 38.66 17.99 38.41 17.74 38.11 17.55 37.77 17.44 37.39 17.4 37 17.44 36.61 17.55 36.23 17.74 35.89 17.99 35.59 18.29 35.34 18.63 35.15 19.01 35.04 19.4 35Z',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M26.2 40 26.55 40.03 26.89 40.14 27.2 40.3 27.47 40.53 27.7 40.8 27.86 41.11 27.97 41.45 28 41.8 27.97 42.15 27.86 42.49 27.7 42.8 27.47 43.07 27.2 43.3 26.89 43.46 26.55 43.57 26.2 43.6 21.8 43.6 21.45 43.57 21.11 43.46 20.8 43.3 20.53 43.07 20.3 42.8 20.14 42.49 20.03 42.15 20 41.8 20.03 41.45 20.14 41.11 20.3 40.8 20.53 40.53 20.8 40.3 21.11 40.14 21.45 40.03 21.8 40Z',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    ['path', { d: 'M9.5 7.5L38.5 36.5' }, ['--cl-base', '#2D2A5C'], 'thick'],
  ],
  link: [
    [
      'path',
      {
        d: 'M17.942 12.042L22.058 7.917A7.083 7.083 0 0 1 32.083 17.942L27.958 22.058',
        transform: 'scale(1.2)',
      },
      '#4D7CFF',
      'thick',
    ],
    [
      'path',
      { d: 'M20 20L24.417 15.583', transform: 'scale(1.2)' },
      '#4D7CFF',
      'thick',
    ],
    [
      'path',
      {
        d: 'M22.058 27.958L17.942 32.083A7.083 7.083 0 0 1 7.917 22.058L12.042 17.942',
        transform: 'scale(1.2)',
      },
      '#7C5CFF',
      'thick',
    ],
    [
      'path',
      { d: 'M15.583 24.417L20 20', transform: 'scale(1.2)' },
      '#7C5CFF',
      'thick',
    ],
  ],
  linkedin: [
    ['rect', { x: 4, y: 4, width: 40, height: 40, rx: 7 }, '#0A66C2'],
    [
      'circle',
      {
        cx: 15.2,
        cy: 14.6,
        r: 3.3,
        transform: 'translate(24 24) scale(0.94) translate(-26.2 -24.65)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 12.3,
        y: 20.2,
        width: 5.8,
        height: 17.8,
        rx: 1,
        transform: 'translate(24 24) scale(0.94) translate(-26.2 -24.65)',
      },
      '#FFFFFF',
    ],
    [
      'path',
      {
        d: 'M22.3 20.2h5.5v2.5c.9-1.6 3-3.1 6.1-3.1 5.6 0 6.6 3.6 6.6 8.4V38h-5.8v-9c0-2.2 0-5-3-5-3.1 0-3.6 2.4-3.6 4.8V38h-5.8z',
        transform: 'translate(24 24) scale(0.94) translate(-26.2 -24.65)',
      },
      '#FFFFFF',
    ],
  ],
  list: [
    [
      'rect',
      {
        x: 8,
        y: 10,
        width: 25,
        height: 30,
        rx: 5,
        transform: 'rotate(-14 20.5 25)',
      },
      '#8FD3FF',
    ],
    [
      'rect',
      {
        x: 11,
        y: 9,
        width: 25,
        height: 30,
        rx: 5,
        transform: 'rotate(-5 23.5 24)',
      },
      '#FFC93C',
    ],
    [
      'rect',
      {
        x: 14.5,
        y: 9,
        width: 25,
        height: 31,
        rx: 5,
        transform: 'rotate(6 27 24.5)',
      },
      '#FF5D9E',
    ],
    [
      'circle',
      { cx: 27, cy: 19, r: 4.6, transform: 'rotate(6 27 24.5)' },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 20.5,
        y: 27,
        width: 13,
        height: 3.2,
        rx: 1.6,
        transform: 'rotate(6 27 24.5)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 22.5,
        y: 32.2,
        width: 9,
        height: 3.2,
        rx: 1.6,
        transform: 'rotate(6 27 24.5)',
      },
      '#FFFFFF',
    ],
  ],
  'list-bullets': [
    [
      'rect',
      {
        x: 8,
        y: 10,
        width: 25,
        height: 30,
        rx: 5,
        transform: 'rotate(-14 20.5 25)',
      },
      '#8FD3FF',
    ],
    [
      'rect',
      {
        x: 11,
        y: 9,
        width: 25,
        height: 30,
        rx: 5,
        transform: 'rotate(-5 23.5 24)',
      },
      '#FFC93C',
    ],
    [
      'rect',
      {
        x: 14.5,
        y: 9,
        width: 25,
        height: 31,
        rx: 5,
        transform: 'rotate(6 27 24.5)',
      },
      '#FF5D9E',
    ],
    [
      'circle',
      { cx: 27, cy: 19, r: 4.6, transform: 'rotate(6 27 24.5)' },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 20.5,
        y: 27,
        width: 13,
        height: 3.2,
        rx: 1.6,
        transform: 'rotate(6 27 24.5)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 22.5,
        y: 32.2,
        width: 9,
        height: 3.2,
        rx: 1.6,
        transform: 'rotate(6 27 24.5)',
      },
      '#FFFFFF',
    ],
  ],
  'list-check': [
    ['rect', { x: 9, y: 9.5, width: 30, height: 33, rx: 6 }, '#FFB547'],
    ['rect', { x: 13, y: 14, width: 22, height: 25, rx: 3.5 }, '#FFF8EC'],
    ['rect', { x: 17.5, y: 5.5, width: 13, height: 7.5, rx: 3.2 }, '#7A5AF8'],
    ['path', { d: 'M16.6 21.2l2.1 2.1 4.1-4.4' }, '#21C45D', 'line'],
    ['rect', { x: 25, y: 19.6, width: 7, height: 3, rx: 1.5 }, '#E3CFAE'],
    ['path', { d: 'M16.6 28.8l2.1 2.1 4.1-4.4' }, '#21C45D', 'line'],
    ['rect', { x: 25, y: 27.2, width: 7, height: 3, rx: 1.5 }, '#E3CFAE'],
    [
      'rect',
      { x: 16.8, y: 33.6, width: 5, height: 4.4, rx: 1.4 },
      '#21C45D',
      'line',
    ],
    ['rect', { x: 25, y: 34.3, width: 5, height: 3, rx: 1.5 }, '#E3CFAE'],
  ],
  'list-ol': [
    ['rect', { x: 20, y: 10.2, width: 22, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['rect', { x: 20, y: 21.2, width: 22, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['rect', { x: 20, y: 32.2, width: 22, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['path', { d: 'M7.5 9.5L11 7.5V19M7.5 19H14.5' }, '#FF6FAE', 'thick'],
    [
      'path',
      {
        d: 'M7 31.5C7 29.5 8.7 28 11 28S15 29.5 15 31.5C15 33.5 13.5 34.5 11.5 36L7 40H15',
      },
      '#4D7CFF',
      'thick',
    ],
  ],
  'list-ul': [
    ['rect', { x: 18, y: 10.2, width: 24, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['rect', { x: 18, y: 21.2, width: 24, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['rect', { x: 18, y: 32.2, width: 24, height: 5.6, rx: 2.8 }, '#A893FF'],
    ['circle', { cx: 9.5, cy: 13, r: 4.4 }, '#FF6FAE'],
    ['circle', { cx: 9.5, cy: 24, r: 4.4 }, '#FFC23D'],
    ['circle', { cx: 9.5, cy: 35, r: 4.4 }, '#4D7CFF'],
    ['circle', { cx: 8.1, cy: 11.6, r: 1.2, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 8.1, cy: 22.6, r: 1.2, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 8.1, cy: 33.6, r: 1.2, opacity: 0.55 }, '#FFFFFF'],
  ],
  'location-dot': [
    ['ellipse', { cx: 24, cy: 41.5, rx: 11, ry: 3.2 }, '#FFB3C0'],
    [
      'path',
      {
        d: 'M24 4c8.2 0 14.4 6.2 14.4 14.2 0 9.8-9.6 18.4-12.6 21.8a2.4 2.4 0 0 1-3.6 0C19.2 36.6 9.6 28 9.6 18.2 9.6 10.2 15.8 4 24 4z',
      },
      '#FF3D5A',
    ],
    ['circle', { cx: 24, cy: 18.2, r: 7 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 18.2, r: 3 }, '#C81E3A'],
  ],
  lock: [
    [
      'path',
      { d: 'M15.5 22V16a8.5 8.5 0 0 1 17 0v6' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    ['rect', { x: 8, y: 19.5, width: 32, height: 23.5, rx: 6.5 }, '#FFC23D'],
    ['circle', { cx: 24, cy: 29, r: 3.4 }, '#2D2A5C'],
    ['rect', { x: 22.5, y: 29.5, width: 3, height: 7.5, rx: 1.5 }, '#2D2A5C'],
    ['path', { d: 'M13 24.5v5' }, '#FFFFFF', 'line'],
  ],
  'lock-keyhole': [
    [
      'path',
      { d: 'M15.5 22V16a8.5 8.5 0 0 1 17 0v6' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    ['rect', { x: 8, y: 19.5, width: 32, height: 23.5, rx: 6.5 }, '#FFC23D'],
    ['circle', { cx: 24, cy: 29, r: 3.4 }, '#2D2A5C'],
    ['rect', { x: 22.5, y: 29.5, width: 3, height: 7.5, rx: 1.5 }, '#2D2A5C'],
    ['path', { d: 'M13 24.5v5' }, '#FFFFFF', 'line'],
  ],
  'magnifying-glass': [
    [
      'rect',
      {
        x: 25.76,
        y: 31.7,
        width: 19.28,
        height: 7.4,
        rx: 3.7,
        transform: 'rotate(45 35.4 35.4)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    ['circle', { cx: 21, cy: 21, r: 15.6 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 21, cy: 21, r: 11 }, '#7DF1E5'],
    ['path', { d: 'M14.5 19.5A7 7 0 0 1 19.5 14.5' }, '#FFFFFF', 'thick'],
  ],
  'magnifying-glass-chart': [
    [
      'rect',
      {
        x: 29.5,
        y: 33.5,
        width: 15,
        height: 7,
        rx: 3.5,
        transform: 'rotate(45 37 37)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    ['circle', { cx: 20, cy: 20, r: 16 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 20, cy: 20, r: 12 }, '#FFFFFF'],
    ['rect', { x: 12.6, y: 20, width: 4.2, height: 6.5, rx: 1.4 }, '#FF6FAE'],
    ['rect', { x: 17.9, y: 13.5, width: 4.2, height: 13, rx: 1.4 }, '#FFC23D'],
    ['rect', { x: 23.2, y: 17, width: 4.2, height: 9.5, rx: 1.4 }, '#4D7CFF'],
  ],
  'magnifying-glass-minus': [
    [
      'rect',
      {
        x: 25.76,
        y: 31.7,
        width: 19.28,
        height: 7.4,
        rx: 3.7,
        transform: 'rotate(45 35.4 35.4)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    ['circle', { cx: 21, cy: 21, r: 15.6 }, ['--cl-base', '#2D2A5C']],
    ['circle', { cx: 21, cy: 21, r: 11 }, '#7DF1E5'],
    ['path', { d: 'M14.86 16.7A7.5 7.5 0 0 1 16.7 14.86' }, '#FFFFFF', 'thick'],
    ['rect', { x: 15, y: 18.4, width: 12, height: 5.2, rx: 2.6 }, '#FF4D5E'],
  ],
  mailbox: [
    [
      'rect',
      { x: 22.5, y: 30, width: 6, height: 14, rx: 1.8 },
      ['--cl-base', '#252A57'],
    ],
    ['path', { d: 'M12 13h21a9 9 0 0 1 9 9v12a2 2 0 0 1-2 2H12z' }, '#A3E635'],
    [
      'path',
      { d: 'M12 13a9 9 0 0 1 9 9v14H5a2 2 0 0 1-2-2V22a9 9 0 0 1 9-9z' },
      '#65A30D',
    ],
    ['circle', { cx: 12, cy: 25.5, r: 1.8 }, '#FFFFFF'],
    [
      'path',
      {
        d: 'M33 7.5a1.5 1.5 0 0 1 1.5-1.5H42a1.5 1.5 0 0 1 1.5 1.5v4.5A1.5 1.5 0 0 1 42 13.5h-5.6V27a1.7 1.7 0 0 1-3.4 0z',
      },
      '#FF4D5E',
    ],
  ],
  'manual-entry': [
    ['rect', { x: 4, y: 9, width: 34, height: 34, rx: 7 }, '#4D7CFF'],
    ['rect', { x: 9.5, y: 33, width: 9, height: 4, rx: 2 }, '#FFFFFF'],
    [
      'rect',
      {
        x: 32,
        y: 18,
        width: 11,
        height: 12,
        rx: 4,
        transform:
          'translate(11.96 2.37) scale(0.7) translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FF6F91',
    ],
    [
      'rect',
      {
        x: 11,
        y: 18,
        width: 23.5,
        height: 12,
        rx: 1,
        transform:
          'translate(11.96 2.37) scale(0.7) translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M11.5 18L-1 24 11.5 30Z',
        transform:
          'translate(11.96 2.37) scale(0.7) translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FFD58F',
    ],
    [
      'path',
      {
        d: 'M3.8 21.7L0 23.5Q-1 24 0 24.5L3.8 26.3Z',
        transform:
          'translate(11.96 2.37) scale(0.7) translate(0.9 -0.8) rotate(-45 24 24)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  'match-company': [
    ['rect', { x: 6, y: 5, width: 24, height: 38, rx: 4.5 }, '#D63FE6'],
    ['rect', { x: 11, y: 11, width: 5, height: 5, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 20, y: 11, width: 5, height: 5, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 11, y: 20, width: 5, height: 5, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 20, y: 20, width: 5, height: 5, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 14.5, y: 32, width: 7, height: 11, rx: 1.6 }, '#F5B8FA'],
    ['circle', { cx: 35, cy: 34, r: 10 }, '#7E2BD8'],
    ['path', { d: 'M30.6 34.2l3 3 5.8-6.2' }, '#FFFFFF', 'line'],
  ],
  medal: [
    [
      'rect',
      {
        x: 11.25,
        y: 3.65,
        width: 8.5,
        height: 23.7,
        rx: 2.4,
        transform: 'rotate(-27.6 15.5 15.5)',
      },
      '#7C5CFF',
    ],
    [
      'rect',
      {
        x: 28.25,
        y: 3.65,
        width: 8.5,
        height: 23.7,
        rx: 2.4,
        transform: 'rotate(27.6 32.5 15.5)',
      },
      '#A893FF',
    ],
    ['circle', { cx: 24, cy: 32, r: 12 }, '#FFC23D'],
    [
      'path',
      {
        d: 'M24 26.4L25.47 29.98L29.33 30.27L26.38 32.77L27.29 36.53L24 34.5L20.71 36.53L21.62 32.77L18.67 30.27L22.53 29.98Z',
      },
      '#FFFFFF',
    ],
  ],
  memory: [
    [
      'path',
      {
        d: 'M22.9 8.6A5.43 5.43 0 0 0 14.2 9.6A6.51 6.51 0 0 0 7.4 17.6A6.22 6.22 0 0 0 6.6 27.6A6.28 6.28 0 0 0 11.6 36.4A7.35 7.35 0 0 0 22.9 40Z',
      },
      '#9640EC',
    ],
    [
      'path',
      {
        d: 'M25.1 8.6A5.43 5.43 0 0 1 33.8 9.6A6.51 6.51 0 0 1 40.6 17.6A6.22 6.22 0 0 1 41.4 27.6A6.28 6.28 0 0 1 36.4 36.4A7.35 7.35 0 0 1 25.1 40Z',
      },
      '#BC76FF',
    ],
    [
      'path',
      { d: 'M18.5 15.5c-3.4.2-5.6 2.6-5.2 5.6M12.5 29c2.4-.4 4.6.7 5.6 3' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      { d: 'M29.5 15.5c3.4.2 5.6 2.6 5.2 5.6M35.5 29c-2.4-.4-4.6.7-5.6 3' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      {
        d: 'M41 3.2l2.02 2.78 2.78 2.02-2.78 2.02-2.02 2.78-2.02-2.78-2.78-2.02 2.78-2.02z',
      },
      '#FF6FC8',
    ],
  ],
  message: [
    [
      'path',
      {
        d: 'M27 4h12a5 5 0 0 1 5 5v7a5 5 0 0 1-5 5v3.2c0 1-1.1 1.6-1.9 1L32.4 21H27a5 5 0 0 1-5-5V9a5 5 0 0 1 5-5z',
      },
      '#FF4D94',
    ],
    [
      'path',
      {
        d: 'M10 13h20a6 6 0 0 1 6 6v11a6 6 0 0 1-6 6H18.5l-6.3 5.2c-1.1.9-2.7.1-2.7-1.3V36A5.6 5.6 0 0 1 4 30.4V19a6 6 0 0 1 6-6z',
      },
      '#0FB2C4',
    ],
    ['circle', { cx: 12.6, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
    ['circle', { cx: 20, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
    ['circle', { cx: 27.4, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
  ],
  'message-dots': [
    [
      'path',
      {
        d: 'M11 7H37a7 7 0 0 1 7 7V29a7 7 0 0 1-7 7H21.5l-7.2 5.8c-1.3 1-3.1.1-3.1-1.5V36A7 7 0 0 1 4 29V14a7 7 0 0 1 7-7z',
      },
      '#0FB2C4',
    ],
    ['circle', { cx: 14.5, cy: 21.5, r: 3 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 21.5, r: 3 }, '#FFFFFF'],
    ['circle', { cx: 33.5, cy: 21.5, r: 3 }, '#FFFFFF'],
  ],
  minus: [
    ['rect', { x: 6, y: 20.5, width: 36, height: 7, rx: 3.5 }, '#FFB547'],
    ['path', { d: 'M12 22.4H17', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  'mobile-screen-button': [
    [
      'rect',
      { x: 12, y: 3.5, width: 24, height: 41, rx: 6 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 15.5, y: 8, width: 17, height: 26.5, rx: 2.5 }, '#4D7CFF'],
    ['path', { d: 'M19 17.5l5.5-5.5' }, '#FFFFFF', 'line'],
    ['circle', { cx: 24, cy: 39.3, r: 2.4 }, '#8FD3FF'],
  ],
  moon: [
    [
      'path',
      {
        d: 'M17.95 8.98A17.5 17.5 0 1 0 39.02 30.05A15.5 15.5 0 0 1 17.95 8.98Z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M37.73 6.91Q38.68 10.82 42.59 11.77Q43.9 12 42.59 12.23Q38.68 13.18 37.73 17.09Q37.5 18.4 37.27 17.09Q36.32 13.18 32.41 12.23Q31.1 12 32.41 11.77Q36.32 10.82 37.27 6.91Q37.5 5.6 37.73 6.91Z',
      },
      '#FFC23D',
    ],
  ],
  notifications: [
    ['circle', { cx: 24, cy: 7.4, r: 3 }, '#F08C00'],
    ['circle', { cx: 24, cy: 37.4, r: 4.6 }, '#F08C00'],
    [
      'path',
      {
        d: 'M24 8.2c-7.4 0-12.4 5.6-12.4 12.8v6.2l-3.9 5.4c-1 1.4 0 3.2 1.7 3.2h29.2c1.7 0 2.7-1.8 1.7-3.2l-3.9-5.4V21c0-7.2-5-12.8-12.4-12.8z',
      },
      '#FFC21A',
    ],
    ['path', { d: 'M16.6 24.4v-3.2c0-3.2 1.5-5.8 4-7.2' }, '#FFFFFF', 'line'],
    ['circle', { cx: 37, cy: 11.5, r: 5.2 }, '#FF3B5C'],
  ],
  organization: [
    ['rect', { x: 25, y: 18, width: 17, height: 24.5, rx: 3.5 }, '#8D97FF'],
    ['rect', { x: 7, y: 6, width: 22, height: 36.5, rx: 4 }, '#4F5BEA'],
    [
      'path',
      {
        d: 'M11.5 11.5h4.4v4.4h-4.4zM20.1 11.5h4.4v4.4h-4.4zM11.5 19.5h4.4v4.4h-4.4zM20.1 19.5h4.4v4.4h-4.4zM11.5 27.5h4.4v4.4h-4.4zM20.1 27.5h4.4v4.4h-4.4z',
      },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M32.4 23.5h4.4v4.4h-4.4zM32.4 31.5h4.4v4.4h-4.4z' },
      '#FFFFFF',
    ],
    ['rect', { x: 15.4, y: 35, width: 5.2, height: 7.5, rx: 1.6 }, '#FFC93C'],
  ],
  palette: [
    [
      'path',
      {
        d: 'M24 6C34.5 6 43 13.4 43 22.6c0 6.3-4.3 9.4-8.6 8.4-2.9-.7-5 1.5-4.1 4.3 1.1 3.7-1.2 7.2-5.8 7.2C13.6 42.5 5 34.2 5 23.8 5 13.8 13.5 6 24 6z',
      },
      '#D946EF',
    ],
    ['circle', { cx: 14.6, cy: 19.4, r: 3.7 }, '#FFD23F'],
    ['circle', { cx: 23.6, cy: 13.4, r: 3.7 }, '#3FD8FF'],
    ['circle', { cx: 33.2, cy: 16.6, r: 3.7 }, '#FFFFFF'],
    ['circle', { cx: 14.4, cy: 30, r: 3.7 }, '#FFFFFF'],
  ],
  'paper-plane': [
    ['circle', { cx: 7.5, cy: 39.5, r: 1.7 }, '#A7D3FF'],
    ['circle', { cx: 12, cy: 37, r: 1.9 }, '#A7D3FF'],
    ['circle', { cx: 16, cy: 33.8, r: 2.1 }, '#A7D3FF'],
    ['path', { d: 'M42.5 6.5L8 21.2l12.3 5z' }, '#6CB6FF'],
    ['path', { d: 'M42.5 6.5L20.3 26.2 25.6 40z' }, '#2F80FF'],
    ['path', { d: 'M20.3 26.2l5.3 13.8-2-10.6z' }, '#1D4FD8'],
  ],
  'paper-plane-top': [
    ['circle', { cx: 7.5, cy: 39.5, r: 1.7 }, '#A7D3FF'],
    ['circle', { cx: 12, cy: 37, r: 1.9 }, '#A7D3FF'],
    ['circle', { cx: 16, cy: 33.8, r: 2.1 }, '#A7D3FF'],
    ['path', { d: 'M42.5 6.5L8 21.2l12.3 5z' }, '#6CB6FF'],
    ['path', { d: 'M42.5 6.5L20.3 26.2 25.6 40z' }, '#2F80FF'],
    ['path', { d: 'M20.3 26.2l5.3 13.8-2-10.6z' }, '#1D4FD8'],
  ],
  paperclip: [
    [
      'path',
      {
        d: 'M15.7 14.2A4.5 4.5 0 0 1 24.7 14.2V29.7',
        transform: 'translate(24 24) rotate(45) scale(1.12) translate(-24 -24)',
      },
      '#93A9DC',
      'thick',
    ],
    [
      'path',
      {
        d: 'M33.7 15.7V30.7A9 9 0 0 1 15.7 30.7V14.2',
        transform: 'translate(24 24) rotate(45) scale(1.12) translate(-24 -24)',
      },
      '#5F7FC4',
      'thick',
    ],
  ],
  'party-horn': [
    [
      'path',
      {
        d: 'M13.64 37.33A2.2 2.2 0 0 1 10.67 34.36L19.76 16.79A1.2 1.2 0 0 1 21.67 16.49L31.51 26.33A1.2 1.2 0 0 1 31.21 28.24z',
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#FF4D94',
    ],
    [
      'path',
      {
        d: 'M12.32 31.18 14.34 27.26 20.74 33.66 16.82 35.68z',
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M16.41 23.25 18.44 19.34 28.66 29.56 24.75 31.59z',
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#FFC23D',
    ],
    [
      'ellipse',
      {
        cx: 26.6,
        cy: 21.4,
        rx: 8.6,
        ry: 2.8,
        transform:
          'translate(24 24) scale(1.1) translate(-27.6 -20.7) rotate(45 26.6 21.4)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M31 14L35 10L38.5 12.5L42.5 8.5',
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#21C45D',
      'thick',
    ],
    [
      'circle',
      {
        cx: 40.5,
        cy: 25.5,
        r: 2.7,
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#4D7CFF',
    ],
    [
      'circle',
      {
        cx: 22,
        cy: 6.5,
        r: 2.7,
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#4D7CFF',
    ],
    [
      'circle',
      {
        cx: 36,
        cy: 34,
        r: 2.2,
        transform: 'translate(24 24) scale(1.1) translate(-27.6 -20.7)',
      },
      '#21C45D',
    ],
  ],
  pause: [
    ['rect', { x: 11.5, y: 9.5, width: 9, height: 29, rx: 3.5 }, '#6C63FF'],
    ['rect', { x: 27.5, y: 9.5, width: 9, height: 29, rx: 3.5 }, '#6C63FF'],
    ['path', { d: 'M14.6 14V19', opacity: 0.6 }, '#FFFFFF', 'line'],
    ['path', { d: 'M30.6 14V19', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  pen: [
    [
      'rect',
      {
        x: 13,
        y: 17.5,
        width: 30,
        height: 13,
        rx: 6.5,
        transform: 'translate(1.1 -0.9) rotate(-45 24 24)',
      },
      '#4D7CFF',
    ],
    [
      'rect',
      {
        x: 10.5,
        y: 18.5,
        width: 8,
        height: 11,
        rx: 2,
        transform: 'translate(1.1 -0.9) rotate(-45 24 24)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M9.88 19.92L0.12 23.58Q-1 24 0.12 24.42L9.88 28.08Q11 28.5 11 27.3L11 20.7Q11 19.5 9.88 19.92Z',
        transform: 'translate(1.1 -0.9) rotate(-45 24 24)',
      },
      '#FFC23D',
    ],
    [
      'path',
      { d: 'M25 21.2H38', transform: 'translate(1.1 -0.9) rotate(-45 24 24)' },
      '#FFFFFF',
      'line',
    ],
  ],
  pencil: [
    [
      'rect',
      {
        x: 32,
        y: 18,
        width: 11,
        height: 12,
        rx: 4,
        transform: 'translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FF6F91',
    ],
    [
      'rect',
      {
        x: 11,
        y: 18,
        width: 23.5,
        height: 12,
        rx: 1,
        transform: 'translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M11.5 18L-1 24 11.5 30Z',
        transform: 'translate(0.9 -0.8) rotate(-45 24 24)',
      },
      '#FFD58F',
    ],
    [
      'path',
      {
        d: 'M3.8 21.7L0 23.5Q-1 24 0 24.5L3.8 26.3Z',
        transform: 'translate(0.9 -0.8) rotate(-45 24 24)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  people: [
    ['rect', { x: 32, y: 10.5, width: 10, height: 7.5, rx: 2.6 }, '#FFD23F'],
    ['rect', { x: 32, y: 20.25, width: 10, height: 7.5, rx: 2.6 }, '#FF7AB0'],
    ['rect', { x: 32, y: 30, width: 10, height: 7.5, rx: 2.6 }, '#FFD23F'],
    ['rect', { x: 7, y: 6, width: 30, height: 36, rx: 5.5 }, '#27B24A'],
    [
      'path',
      {
        d: 'M12.5 6H15v36h-2.5A5.5 5.5 0 0 1 7 36.5v-25A5.5 5.5 0 0 1 12.5 6z',
      },
      '#178A35',
    ],
    ['circle', { cx: 26, cy: 18.6, r: 4.8 }, ['--cl-oncard', '#FFFFFF']],
    [
      'path',
      {
        d: 'M18 33.6c0-4.7 3.6-7.8 8-7.8s8 3.1 8 7.8a1.4 1.4 0 0 1-1.4 1.4H19.4a1.4 1.4 0 0 1-1.4-1.4z',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
  ],
  'phone-hangup': [
    [
      'path',
      {
        d: 'M5 25.5c10.6-8.7 27.4-8.7 38 0 1.2 1 1.4 2.8.4 4l-2.6 3.1c-1 1.2-2.7 1.4-3.9.6l-4.6-3.2c-.9-.6-1.3-1.7-1.1-2.7l.5-2.6c-3.4-1.3-9-1.3-12.4 0l.5 2.6c.2 1-.2 2.1-1.1 2.7l-4.6 3.2c-1.2.8-2.9.6-3.9-.6L4.6 29.5c-1-1.2-.8-3 .4-4z',
      },
      '#FF5A5F',
    ],
  ],
  microphone: [
    ['rect', { x: 16.5, y: 5, width: 15, height: 24, rx: 7.5 }, '#19B6EC'],
    [
      'path',
      { d: 'M11 22.5c0 7.2 5.8 13 13 13s13-5.8 13-13' },
      '#252A57',
      'thick',
    ],
    ['path', { d: 'M24 35.5V42' }, '#252A57', 'thick'],
  ],
  'microphone-slash': [
    ['rect', { x: 16.5, y: 5, width: 15, height: 24, rx: 7.5 }, '#A7B0C0'],
    [
      'path',
      { d: 'M11 22.5c0 7.2 5.8 13 13 13s13-5.8 13-13' },
      '#6B7280',
      'thick',
    ],
    ['path', { d: 'M24 35.5V42' }, '#6B7280', 'thick'],
    ['path', { d: 'M8 8l32 32' }, '#FF5A5F', 'thick'],
  ],
  dialpad: [
    ['circle', { cx: 12, cy: 10, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 24, cy: 10, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 36, cy: 10, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 12, cy: 22, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 24, cy: 22, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 36, cy: 22, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 12, cy: 34, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 24, cy: 34, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 36, cy: 34, r: 4.5 }, '#19B6EC'],
    ['circle', { cx: 24, cy: 44, r: 3 }, '#252A57'],
  ],
  phone: [
    [
      'path',
      {
        d: 'M13 6.5c2 0 3.5 1 4.2 2.9l2.2 5.9c.6 1.7.1 3.3-1.2 4.3L16 21.3c2 4.7 6 8.7 10.7 10.7l1.7-2.2c1-1.3 2.6-1.8 4.3-1.2l5.9 2.2c1.9.7 2.9 2.2 2.9 4.2v3.5c0 1.9-1.5 3.5-3.5 3.5C21 42 6 27 6 10c0-1.9 1.6-3.5 3.5-3.5z',
      },
      '#6CCB2B',
    ],
    ['path', { d: 'M27.5 13.5a7 7 0 0 1 7 7' }, '#FF4D94', 'line'],
    ['path', { d: 'M27.5 6.5a14 14 0 0 1 14 14' }, '#FF4D94', 'line'],
  ],
  'phone-volume': [
    [
      'path',
      {
        d: 'M13 6.5c2 0 3.5 1 4.2 2.9l2.2 5.9c.6 1.7.1 3.3-1.2 4.3L16 21.3c2 4.7 6 8.7 10.7 10.7l1.7-2.2c1-1.3 2.6-1.8 4.3-1.2l5.9 2.2c1.9.7 2.9 2.2 2.9 4.2v3.5c0 1.9-1.5 3.5-3.5 3.5C21 42 6 27 6 10c0-1.9 1.6-3.5 3.5-3.5z',
        transform:
          'translate(-0.8 0.8) translate(4 44) scale(0.8) translate(-6 -42)',
      },
      '#6CCB2B',
    ],
    [
      'circle',
      { cx: 27, cy: 21, r: 3.2, transform: 'translate(-0.8 0.8)' },
      '#FF4D94',
    ],
    [
      'path',
      { d: 'M27 12.2A8.8 8.8 0 0 1 35.8 21', transform: 'translate(-0.8 0.8)' },
      '#FF4D94',
      'thick',
    ],
    [
      'path',
      {
        d: 'M27 4.6A16.4 16.4 0 0 1 43.4 21',
        transform: 'translate(-0.8 0.8)',
      },
      '#FF4D94',
      'thick',
    ],
  ],
  'plans-billing': [
    [
      'rect',
      { x: 4, y: 10, width: 38, height: 26, rx: 5 },
      ['--cl-base', '#252A57'],
    ],
    ['rect', { x: 9, y: 15.5, width: 8.5, height: 6.6, rx: 1.8 }, '#FFC93C'],
    ['path', { d: 'M9.6 28.6h5.6M18.6 28.6h5.6' }, '#FFC93C', 'line'],
    ['circle', { cx: 36.5, cy: 35.5, r: 8.5 }, '#FFC93C'],
    ['circle', { cx: 36.5, cy: 35.5, r: 5.4 }, '#FFE08A'],
    ['path', { d: 'M36.5 32.6v5.8' }, '#E09A00', 'line'],
  ],
  play: [
    [
      'path',
      {
        d: 'M12.5 13L12.5 35Q12.5 40.5 17.24 37.71L35.76 26.79Q40.5 24 35.76 21.21L17.24 10.29Q12.5 7.5 12.5 13Z',
      },
      '#2F6BFF',
    ],
    ['path', { d: 'M18 15.5v5' }, '#FFFFFF', 'line'],
  ],
  plug: [
    [
      'rect',
      { x: 15.5, y: 3, width: 5, height: 12, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 27.5, y: 3, width: 5, height: 12, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 21, y: 33, width: 6, height: 11.5, rx: 3 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M10.5 13h27a2.5 2.5 0 0 1 2.5 2.5V21c0 8.3-6.7 15-15 15h-2C14.7 36 8 29.3 8 21v-5.5A2.5 2.5 0 0 1 10.5 13z',
      },
      '#8B5CF6',
    ],
    ['path', { d: 'M13.5 18.5v3.5' }, '#FFFFFF', 'thick'],
  ],
  'plug-circle-xmark': [
    [
      'rect',
      {
        x: 15.5,
        y: 3,
        width: 5,
        height: 12,
        rx: 2.5,
        transform: 'translate(-1 1) scale(0.74)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      {
        x: 27.5,
        y: 3,
        width: 5,
        height: 12,
        rx: 2.5,
        transform: 'translate(-1 1) scale(0.74)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      {
        x: 21,
        y: 33,
        width: 6,
        height: 11.5,
        rx: 3,
        transform: 'translate(-1 1) scale(0.74)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M10.5 13h27a2.5 2.5 0 0 1 2.5 2.5V21c0 8.3-6.7 15-15 15h-2C14.7 36 8 29.3 8 21v-5.5A2.5 2.5 0 0 1 10.5 13z',
        transform: 'translate(-1 1) scale(0.74)',
      },
      '#8B5CF6',
    ],
    ['circle', { cx: 34.5, cy: 34.5, r: 11 }, '#FF4D5E'],
    [
      'path',
      { d: 'M30.2 30.2l8.6 8.6M38.8 30.2l-8.6 8.6' },
      '#FFFFFF',
      'thick',
    ],
  ],
  plus: [['path', { d: 'M24 8v32M8 24h32' }, 'currentColor', 'thick']],
  'presentation-screen': [
    [
      'path',
      { d: 'M16.5 43L24 37.5L31.5 43M24 32V37.5' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    ['rect', { x: 6, y: 8, width: 36, height: 25.5, rx: 4 }, '#4D7CFF'],
    [
      'rect',
      { x: 4, y: 5, width: 40, height: 5.5, rx: 2.75 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['rect', { x: 10, y: 14, width: 28, height: 15.5, rx: 2.5 }, '#FFFFFF'],
    ['rect', { x: 13.5, y: 21, width: 6, height: 5.5, rx: 1.5 }, '#FF6FAE'],
    ['rect', { x: 21, y: 18.5, width: 6, height: 8, rx: 1.5 }, '#FFC23D'],
    ['rect', { x: 28.5, y: 16.5, width: 6, height: 10, rx: 1.5 }, '#4D7CFF'],
  ],
  'puzzle-piece': [
    [
      'path',
      {
        d: 'M10 14h10a3 3 0 0 1 3 3v4a3 3 0 1 1 0 6v4a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V17a3 3 0 0 1 3-3z',
      },
      '#8B5CF6',
    ],
    [
      'path',
      {
        d: 'M27 14h11a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H27a3 3 0 0 1-3-3v-4a3 3 0 1 0 0-6v-4a3 3 0 0 1 3-3z',
      },
      '#FF8A3D',
    ],
    ['circle', { cx: 11.5, cy: 18.5, r: 1.6 }, '#C4B2FF'],
    ['circle', { cx: 37, cy: 18.5, r: 1.6 }, '#FFC59B'],
  ],
  question: [
    [
      'path',
      {
        d: 'M12.25 11.04A12.5 12.5 0 1 1 32.84 24.16A4.5 4.5 0 0 1 26.47 17.79A3.5 3.5 0 1 0 20.71 14.12A4.5 4.5 0 0 1 12.25 11.04Z',
      },
      '#2F80FF',
    ],
    [
      'rect',
      {
        x: 18.33,
        y: 19.3,
        width: 17,
        height: 9,
        rx: 4.5,
        transform: 'rotate(135 26.83 23.8)',
      },
      '#2F80FF',
    ],
    ['rect', { x: 19.5, y: 22.13, width: 9, height: 10.2, rx: 4.5 }, '#2F80FF'],
    ['circle', { cx: 24, cy: 40.46, r: 4.73 }, '#2F80FF'],
  ],
  'quote-left': [
    [
      'path',
      {
        d: 'M8.5 24.583V21.667C8.5 16.25 11.5 12.667 16.667 11.667',
        transform: 'scale(1.2)',
      },
      '#FF6FAE',
      'thick',
    ],
    ['circle', { cx: 15, cy: 29.5, r: 7 }, '#FF6FAE'],
    [
      'path',
      {
        d: 'M23.5 24.583V21.667C23.5 16.25 26.5 12.667 31.667 11.667',
        transform: 'scale(1.2)',
      },
      '#F0418F',
      'thick',
    ],
    ['circle', { cx: 33, cy: 29.5, r: 7 }, '#F0418F'],
    ['circle', { cx: 17.8, cy: 27, r: 1.5, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 35.8, cy: 27, r: 1.5, opacity: 0.55 }, '#FFFFFF'],
  ],
  receipt: [
    [
      'path',
      {
        d: 'M9 8Q9 4 13 4h22q4 0 4 4v36l-5-3.2-5 3.2-5-3.2-5 3.2-5-3.2-5 3.2z',
      },
      '#2FBF8F',
    ],
    ['rect', { x: 15, y: 11, width: 18, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['rect', { x: 15, y: 18, width: 18, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['rect', { x: 15, y: 25, width: 10, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['rect', { x: 27, y: 30.5, width: 6, height: 5, rx: 2.5 }, '#FFD23F'],
  ],
  'record-skill': [
    ['rect', { x: 4.5, y: 13, width: 30, height: 30, rx: 6 }, '#F03A55'],
    ['circle', { cx: 11.5, cy: 21.5, r: 2.6 }, '#FFFFFF'],
    ['rect', { x: 16, y: 19.9, width: 9, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['circle', { cx: 11.5, cy: 30, r: 2.6 }, '#FFFFFF'],
    ['rect', { x: 16, y: 28.4, width: 13, height: 3.2, rx: 1.6 }, '#FFFFFF'],
    ['circle', { cx: 11.5, cy: 38.5, r: 2.6 }, '#FF8A9B'],
    ['rect', { x: 16, y: 36.9, width: 13, height: 3.2, rx: 1.6 }, '#FF8A9B'],
    [
      'path',
      {
        d: 'M35.54 4.49Q36.72 2.64 37.27 4.76L38.3 8.73Q38.61 9.89 39.77 10.2L43.74 11.23Q45.86 11.78 44.01 12.96L40.56 15.17Q39.54 15.82 39.62 17.01L39.86 21.11Q39.99 23.3 38.29 21.9L35.13 19.3Q34.2 18.54 33.09 18.98L29.27 20.47Q27.22 21.28 28.03 19.23L29.52 15.41Q29.96 14.3 29.2 13.37L26.6 10.21Q25.2 8.51 27.39 8.64L31.49 8.88Q32.68 8.96 33.33 7.94Z',
      },
      '#FFC63A',
    ],
  ],
  'rectangle-list': [
    ['rect', { x: 4, y: 9, width: 40, height: 30, rx: 6.5 }, '#4D7CFF'],
    ['circle', { cx: 13, cy: 19, r: 3.6 }, '#FFC23D'],
    ['circle', { cx: 13, cy: 29, r: 3.6 }, '#FFC23D'],
    ['rect', { x: 20, y: 17.2, width: 17, height: 3.6, rx: 1.8 }, '#FFFFFF'],
    ['rect', { x: 20, y: 27.2, width: 12, height: 3.6, rx: 1.8 }, '#FFFFFF'],
  ],
  reply: [
    [
      'path',
      {
        d: 'M18 13.5H27C35.8 13.5 43 20.7 43 29.5V39C43 41.6 39.6 42.5 38.6 40.2C36.4 32.7 32.4 24.5 26 24.5H18z',
      },
      '#0FB2C4',
    ],
    [
      'path',
      {
        d: 'M6.6 17.1L16.3 8.4C17.9 7 20.3 8.1 20.3 10.2V28.8C20.3 30.9 17.9 32 16.3 30.6L6.6 21.9C5.2 20.6 5.2 18.4 6.6 17.1z',
      },
      '#0A8E9D',
    ],
    ['path', { d: 'M24.5 17.4H27c5 0 9.4 3.4 10.7 8.2' }, '#FFFFFF', 'line'],
  ],
  'right-from-bracket': [
    ['rect', { x: 5, y: 5, width: 22, height: 38, rx: 5 }, '#FF6F91'],
    ['circle', { cx: 21.5, cy: 33, r: 2 }, '#FFFFFF'],
    [
      'rect',
      { x: 13, y: 20.2, width: 18, height: 7.6, rx: 2.2 },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'path',
      {
        d: 'M31.38 14.33L41.62 22.17Q44 24 41.62 25.83L31.38 33.67Q29 35.5 29 32.5L29 15.5Q29 12.5 31.38 14.33Z',
      },
      ['--cl-base', '#2D2A5C'],
    ],
  ],
  robot: [
    [
      'path',
      {
        d: 'M292,64 C370,72 425,138 425,222 C425,290 418,330 412,380 C404,440 360,488 300,494 C250,498 200,492 170,482 C140,492 112,502 82,508 C118,478 132,452 136,422 C104,372 92,300 93,228 C93,170 112,128 148,100 C128,72 108,48 80,30 C124,34 160,52 186,78 C186,52 178,26 160,4 C202,18 228,40 240,66 C246,44 256,24 270,8 C282,26 292,44 292,64 Z',
        transform: 'translate(1 1) scale(0.09)',
      },
      '#3B5AF7',
    ],
    [
      'path',
      {
        d: 'M146,318 C176,306 200,312 204,326 C206,388 220,436 254,478 C196,460 156,410 146,318 Z',
        transform: 'translate(1 1) scale(0.09)',
      },
      '#2A43DE',
    ],
    [
      'ellipse',
      {
        cx: 258,
        cy: 219.5,
        rx: 119,
        ry: 96.5,
        transform: 'translate(1 1) scale(0.09)',
      },
      '#FFFFFF',
    ],
    [
      'circle',
      { cx: 212, cy: 214.5, r: 25, transform: 'translate(1 1) scale(0.09)' },
      '#0E1668',
    ],
    [
      'circle',
      { cx: 322, cy: 199, r: 25, transform: 'translate(1 1) scale(0.09)' },
      '#0E1668',
    ],
    [
      'path',
      {
        d: 'M259,225 C272,217 301,218 311,233 C322,251 311,280 279,298 C276,275 270,248 259,225 Z',
        transform: 'translate(1 1) scale(0.09)',
      },
      '#2A43DE',
    ],
  ],
  rocket: [
    [
      'path',
      {
        d: 'M24 45.5c-3.4-2.4-5.2-5.1-5.2-7.8 0-1 .6-1.7 1.4-1.7h7.6c.8 0 1.4.7 1.4 1.7 0 2.7-1.8 5.4-5.2 7.8z',
        transform: 'rotate(45 24 24)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M17 24.5l-6.2 6.9a2 2 0 0 0 1.5 3.3H17z',
        transform: 'rotate(45 24 24)',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M31 24.5l6.2 6.9a2 2 0 0 1-1.5 3.3H31z',
        transform: 'rotate(45 24 24)',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M24 3.5c5.4 3.9 8 10.6 8 18.5v10.5a3 3 0 0 1-3 3H19a3 3 0 0 1-3-3V22c0-7.9 2.6-14.6 8-18.5z',
        transform: 'rotate(45 24 24)',
      },
      '#FF6FAE',
    ],
    [
      'circle',
      { cx: 24, cy: 18, r: 4.6, transform: 'rotate(45 24 24)' },
      '#FFFFFF',
    ],
    [
      'circle',
      { cx: 24, cy: 18, r: 2.4, transform: 'rotate(45 24 24)' },
      '#4D7CFF',
    ],
  ],
  'rocket-launch': [
    [
      'path',
      {
        d: 'M24 45.5c-3.4-2.4-5.2-5.1-5.2-7.8 0-1 .6-1.7 1.4-1.7h7.6c.8 0 1.4.7 1.4 1.7 0 2.7-1.8 5.4-5.2 7.8z',
        transform: 'rotate(45 24 24)',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M17 24.5l-6.2 6.9a2 2 0 0 0 1.5 3.3H17z',
        transform: 'rotate(45 24 24)',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M31 24.5l6.2 6.9a2 2 0 0 1-1.5 3.3H31z',
        transform: 'rotate(45 24 24)',
      },
      '#4D7CFF',
    ],
    [
      'path',
      {
        d: 'M24 3.5c5.4 3.9 8 10.6 8 18.5v10.5a3 3 0 0 1-3 3H19a3 3 0 0 1-3-3V22c0-7.9 2.6-14.6 8-18.5z',
        transform: 'rotate(45 24 24)',
      },
      '#FF6FAE',
    ],
    [
      'circle',
      { cx: 24, cy: 18, r: 4.6, transform: 'rotate(45 24 24)' },
      '#FFFFFF',
    ],
    [
      'circle',
      { cx: 24, cy: 18, r: 2.4, transform: 'rotate(45 24 24)' },
      '#4D7CFF',
    ],
  ],
  rotate: [
    ['path', { d: 'M8.24 21.22A16 16 0 0 1 24 8' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M24 8A17.33 17.33 0 0 1 35.98 12.87L40 16.89' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M40 8V16.89H31.11' }, '#6C63FF', 'thick'],
    ['path', { d: 'M39.76 26.78A16 16 0 0 1 24 40' }, '#A893FF', 'thick'],
    [
      'path',
      { d: 'M24 40A17.33 17.33 0 0 1 12.02 35.13L8 31.11' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M16.89 31.11H8V40' }, '#6C63FF', 'thick'],
  ],
  'sack-dollar': [
    [
      'path',
      {
        d: 'M18.4 5.4c1.2-.9 2.9-.8 4 .2L24 7l1.6-1.4c1.1-1 2.8-1.1 4-.2l.3.2c1.3 1 1.5 2.9.5 4.1L28 13.5h-8l-2.4-3.8c-1-1.2-.8-3.1.5-4.1z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M20 14.5C12.6 18.2 6 25.4 6 33.4 6 39.8 10.4 44 17 44h14c6.6 0 11-4.2 11-10.6 0-8-6.6-15.2-14-18.9z',
      },
      '#FFC23D',
    ],
    ['path', { d: 'M19.5 14.5h9' }, '#21C45D', 'thick'],
    [
      'path',
      {
        d: 'M27.81 25.25A4.4 3.5 0 1 0 24 30.5A4.4 3.5 0 1 1 20.19 35.75M24 20.6v1.2M24 39.2v1.2',
      },
      '#16A34A',
      'thick',
    ],
  ],
  schedule: [
    ['rect', { x: 4, y: 8, width: 29, height: 28, rx: 6 }, '#FF8A1F'],
    ['rect', { x: 8, y: 16.5, width: 21, height: 15.5, rx: 3 }, '#FFF4E8'],
    [
      'rect',
      { x: 10, y: 4, width: 4.6, height: 8.5, rx: 2.3 },
      ['--cl-base', '#252A57'],
    ],
    [
      'rect',
      { x: 22.4, y: 4, width: 4.6, height: 8.5, rx: 2.3 },
      ['--cl-base', '#252A57'],
    ],
    ['circle', { cx: 32.5, cy: 32.5, r: 12.5 }, ['--cl-base', '#252A57']],
    ['circle', { cx: 32.5, cy: 32.5, r: 9.3 }, '#FFF4E8'],
    ['path', { d: 'M32.5 27.2v5.3l3.6 2.6' }, '#FF7A12', 'line'],
  ],
  school: [
    ['rect', { x: 4, y: 19, width: 40, height: 23.5, rx: 4 }, '#8FD3FF'],
    [
      'path',
      {
        d: 'M14.5 42.5V16.5q0-1.2.9-2l7-6.2q1.6-1.4 3.2 0l7 6.2q.9.8.9 2V42.5z',
      },
      '#4D7CFF',
    ],
    ['circle', { cx: 24, cy: 20.5, r: 3.4 }, '#FFFFFF'],
    ['path', { d: 'M20.5 40V34.5a3.5 3.5 0 0 1 7 0V40z' }, '#FFFFFF'],
    ['rect', { x: 7.3, y: 25.5, width: 4.6, height: 8, rx: 1.4 }, '#FFFFFF'],
    ['rect', { x: 36.1, y: 25.5, width: 4.6, height: 8, rx: 1.4 }, '#FFFFFF'],
  ],
  seedling: [
    ['path', { d: 'M24 40V25' }, '#21C45D', 'thick'],
    [
      'path',
      { d: 'M23.5 31C13.5 31.5 6.5 25 6.5 15.5 16.5 15 23.5 21 23.5 31z' },
      '#16C7A6',
    ],
    [
      'path',
      { d: 'M24.5 25.5C24.5 14 31.5 6 42 6 42 17.5 35 25.5 24.5 25.5z' },
      '#21C45D',
    ],
    ['path', { d: 'M12 43.5a12 6.5 0 0 1 24 0z' }, ['--cl-base', '#2D2A5C']],
  ],
  senders: [
    [
      'rect',
      { x: 22.5, y: 30, width: 6, height: 14, rx: 1.8 },
      ['--cl-base', '#252A57'],
    ],
    ['path', { d: 'M12 13h21a9 9 0 0 1 9 9v12a2 2 0 0 1-2 2H12z' }, '#A3E635'],
    [
      'path',
      { d: 'M12 13a9 9 0 0 1 9 9v14H5a2 2 0 0 1-2-2V22a9 9 0 0 1 9-9z' },
      '#65A30D',
    ],
    ['circle', { cx: 12, cy: 25.5, r: 1.8 }, '#FFFFFF'],
    [
      'path',
      {
        d: 'M33 7.5a1.5 1.5 0 0 1 1.5-1.5H42a1.5 1.5 0 0 1 1.5 1.5v4.5A1.5 1.5 0 0 1 42 13.5h-5.6V27a1.7 1.7 0 0 1-3.4 0z',
      },
      '#FF4D5E',
    ],
  ],
  'seq-call-task': [
    [
      'path',
      {
        d: 'M13 6.5c2 0 3.5 1 4.2 2.9l2.2 5.9c.6 1.7.1 3.3-1.2 4.3L16 21.3c2 4.7 6 8.7 10.7 10.7l1.7-2.2c1-1.3 2.6-1.8 4.3-1.2l5.9 2.2c1.9.7 2.9 2.2 2.9 4.2v3.5c0 1.9-1.5 3.5-3.5 3.5C21 42 6 27 6 10c0-1.9 1.6-3.5 3.5-3.5z',
      },
      '#6CCB2B',
    ],
    ['path', { d: 'M27.5 13.5a7 7 0 0 1 7 7' }, '#FF4D94', 'line'],
    ['path', { d: 'M27.5 6.5a14 14 0 0 1 14 14' }, '#FF4D94', 'line'],
  ],
  'seq-email': [
    ['rect', { x: 5, y: 10, width: 38, height: 28, rx: 5.5 }, '#FFBE2E'],
    [
      'path',
      {
        d: 'M5 15.5A5.5 5.5 0 0 1 10.5 10h27a5.5 5.5 0 0 1 5.5 5.5L27.3 26.4a5.6 5.6 0 0 1-6.6 0z',
      },
      '#F59A0B',
    ],
    ['circle', { cx: 24, cy: 26, r: 4.6 }, '#FF4D94'],
    ['circle', { cx: 22.6, cy: 24.6, r: 1.2 }, '#FFFFFF'],
  ],
  'seq-send-invitation': [
    [
      'path',
      {
        d: 'M5 40.5c0-7.4 6-12.5 14-12.5s14 5.1 14 12.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z',
      },
      '#FF7A2A',
    ],
    ['circle', { cx: 19, cy: 17, r: 7.5 }, '#FFB070'],
    ['path', { d: 'M37 10v12M31 16h12' }, '#FF4D94', 'thick'],
  ],
  'seq-send-message': [
    [
      'path',
      {
        d: 'M27 4h12a5 5 0 0 1 5 5v7a5 5 0 0 1-5 5v3.2c0 1-1.1 1.6-1.9 1L32.4 21H27a5 5 0 0 1-5-5V9a5 5 0 0 1 5-5z',
      },
      '#FF4D94',
    ],
    [
      'path',
      {
        d: 'M10 13h20a6 6 0 0 1 6 6v11a6 6 0 0 1-6 6H18.5l-6.3 5.2c-1.1.9-2.7.1-2.7-1.3V36A5.6 5.6 0 0 1 4 30.4V19a6 6 0 0 1 6-6z',
      },
      '#0FB2C4',
    ],
    ['circle', { cx: 12.6, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
    ['circle', { cx: 20, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
    ['circle', { cx: 27.4, cy: 24.6, r: 2.5 }, ['--cl-oncard', '#FFFFFF']],
  ],
  'seq-visit-profile': [
    ['rect', { x: 4, y: 7, width: 34, height: 27, rx: 5 }, '#5562F0'],
    ['circle', { cx: 14.5, cy: 17, r: 4.4 }, ['--cl-oncard', '#FFFFFF']],
    [
      'path',
      { d: 'M7.8 28.6c0-3.6 3-6 6.7-6s6.7 2.4 6.7 6z' },
      ['--cl-oncard', '#FFFFFF'],
    ],
    ['path', { d: 'M25 15h8M25 21h5' }, ['--cl-oncard', '#FFFFFF'], 'line'],
    [
      'path',
      {
        d: 'M22 35.5c3.2-5.4 7.7-8.1 11.7-8.1s8.5 2.7 11.7 8.1c-3.2 5.4-7.7 8.1-11.7 8.1S25.2 40.9 22 35.5z',
      },
      '#FF4D94',
    ],
    ['circle', { cx: 33.7, cy: 35.5, r: 4 }, '#1E2350'],
    ['circle', { cx: 35.1, cy: 34.1, r: 1.3 }, '#FFFFFF'],
  ],
  settings: [
    [
      'path',
      {
        d: 'M26.45 8.28L27.64 6.09Q28.5 4.51 30.16 5.2L32.94 6.35Q34.6 7.04 34.09 8.77L33.38 11.15Q32.99 12.5 33.98 13.48L34.52 14.02Q35.5 15.01 36.85 14.62L39.23 13.91Q40.96 13.4 41.65 15.06L42.8 17.84Q43.49 19.5 41.91 20.36L39.72 21.55Q38.49 22.22 38.49 23.62L38.49 24.38Q38.49 25.78 39.72 26.45L41.91 27.64Q43.49 28.5 42.8 30.16L41.65 32.94Q40.96 34.6 39.23 34.09L36.85 33.38Q35.5 32.99 34.52 33.98L33.98 34.52Q32.99 35.5 33.38 36.85L34.09 39.23Q34.6 40.96 32.94 41.65L30.16 42.8Q28.5 43.49 27.64 41.91L26.45 39.72Q25.78 38.49 24.38 38.49L23.62 38.49Q22.22 38.49 21.55 39.72L20.36 41.91Q19.5 43.49 17.84 42.8L15.06 41.65Q13.4 40.96 13.91 39.23L14.62 36.85Q15.01 35.5 14.02 34.52L13.48 33.98Q12.5 32.99 11.15 33.38L8.77 34.09Q7.04 34.6 6.35 32.94L5.2 30.16Q4.51 28.5 6.09 27.64L8.28 26.45Q9.51 25.78 9.51 24.38L9.51 23.62Q9.51 22.22 8.28 21.55L6.09 20.36Q4.51 19.5 5.2 17.84L6.35 15.06Q7.04 13.4 8.77 13.91L11.15 14.62Q12.5 15.01 13.48 14.02L14.02 13.48Q15.01 12.5 14.62 11.15L13.91 8.77Q13.4 7.04 15.06 6.35L17.84 5.2Q19.5 4.51 20.36 6.09L21.55 8.28Q22.22 9.51 23.62 9.51L24.38 9.51Q25.78 9.51 26.45 8.28z',
      },
      '#7C8DB5',
    ],
    ['circle', { cx: 24, cy: 24, r: 8.6 }, '#E9EDF7'],
    ['circle', { cx: 24, cy: 24, r: 4.2 }, '#46557A'],
    ['path', { d: 'M15.5 15.2A12.6 12.6 0 0 1 21 12.1' }, '#B8C3DD', 'thick'],
  ],
  'settings-schedule': [
    ['circle', { cx: 24, cy: 24, r: 19 }, '#FF8A1F'],
    ['circle', { cx: 24, cy: 24, r: 14 }, '#FFF4E8'],
    [
      'path',
      { d: 'M24 12.8v2.2M35.2 24H33M24 35.2V33M12.8 24H15' },
      '#FFB070',
      'line',
    ],
    ['path', { d: 'M24 24v-7.2M24 24l5.2 3.4' }, '#252A57', 'thick'],
    ['circle', { cx: 24, cy: 24, r: 2.3 }, '#FF8A1F'],
  ],
  'share-nodes': [
    ['path', { d: 'M34.5 11.5L13.5 24L34.5 36.5' }, '#8FD3FF', 'thick'],
    ['circle', { cx: 34.5, cy: 11.5, r: 7 }, '#4D7CFF'],
    ['circle', { cx: 13.5, cy: 24, r: 7 }, '#4D7CFF'],
    ['circle', { cx: 34.5, cy: 36.5, r: 7 }, '#4D7CFF'],
    ['circle', { cx: 32.3, cy: 9.3, r: 1.8 }, '#FFFFFF'],
    ['circle', { cx: 11.3, cy: 21.8, r: 1.8 }, '#FFFFFF'],
    ['circle', { cx: 32.3, cy: 34.3, r: 1.8 }, '#FFFFFF'],
  ],
  'shield-check': [
    [
      'path',
      {
        d: 'M24 4C24.6 4 25.1 4.1 25.6 4.3L38.4 9.6C39.6 10.1 40.4 11.3 40.4 12.6C40.4 26.5 34.4 37.6 25.4 43.1C24.5 43.6 23.5 43.6 22.6 43.1C13.6 37.6 7.6 26.5 7.6 12.6C7.6 11.3 8.4 10.1 9.6 9.6L22.4 4.3C22.9 4.1 23.4 4 24 4Z',
      },
      '#21C45D',
    ],
    [
      'rect',
      {
        x: 12.26,
        y: 22.55,
        width: 13.38,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 18.95 25.35)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 16.51,
        y: 19.75,
        width: 20.88,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(-46.59 26.95 22.55)',
      },
      '#FFFFFF',
    ],
  ],
  'shield-exclamation': [
    [
      'path',
      {
        d: 'M24 4C24.6 4 25.1 4.1 25.6 4.3L38.4 9.6C39.6 10.1 40.4 11.3 40.4 12.6C40.4 26.5 34.4 37.6 25.4 43.1C24.5 43.6 23.5 43.6 22.6 43.1C13.6 37.6 7.6 26.5 7.6 12.6C7.6 11.3 8.4 10.1 9.6 9.6L22.4 4.3C22.9 4.1 23.4 4 24 4Z',
      },
      '#FF4D5E',
    ],
    ['rect', { x: 21.2, y: 9.8, width: 5.6, height: 15.8, rx: 2.8 }, '#FFFFFF'],
    ['circle', { cx: 24, cy: 31.9, r: 3.2 }, '#FFFFFF'],
  ],
  'shield-halved': [
    [
      'path',
      {
        d: 'M24 4C24.6 4 25.1 4.1 25.6 4.3L38.4 9.6C39.6 10.1 40.4 11.3 40.4 12.6C40.4 26.5 34.4 37.6 25.4 43.1C24.5 43.6 23.5 43.6 22.6 43.1C13.6 37.6 7.6 26.5 7.6 12.6C7.6 11.3 8.4 10.1 9.6 9.6L22.4 4.3C22.9 4.1 23.4 4 24 4Z',
      },
      '#8FD3FF',
    ],
    [
      'path',
      {
        d: 'M24 4C23.4 4 22.9 4.1 22.4 4.3L9.6 9.6C8.4 10.1 7.6 11.3 7.6 12.6C7.6 26.5 13.6 37.6 22.6 43.1C23.05 43.35 23.53 43.48 24 43.48Z',
      },
      '#4D7CFF',
    ],
  ],
  sidebar: [
    ['rect', { x: 4.5, y: 8, width: 39, height: 32, rx: 7.5 }, '#6C63FF'],
    ['rect', { x: 18.5, y: 12, width: 21, height: 24, rx: 4 }, '#FFFFFF'],
    ['rect', { x: 9, y: 14, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 9, y: 20.3, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 9, y: 26.6, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 22.5, y: 17, width: 12, height: 3.6, rx: 1.8 }, '#FF6FAE'],
    ['rect', { x: 22.5, y: 24, width: 8, height: 3.6, rx: 1.8 }, '#FFC23D'],
  ],
  'sidebar-flip': [
    ['rect', { x: 4.5, y: 8, width: 39, height: 32, rx: 7.5 }, '#6C63FF'],
    ['rect', { x: 8.5, y: 12, width: 21, height: 24, rx: 4 }, '#FFFFFF'],
    ['rect', { x: 33.5, y: 14, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 33.5, y: 20.3, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 33.5, y: 26.6, width: 5.5, height: 3.4, rx: 1.7 }, '#C9C2FF'],
    ['rect', { x: 13.5, y: 17, width: 12, height: 3.6, rx: 1.8 }, '#FF6FAE'],
    ['rect', { x: 17.5, y: 24, width: 8, height: 3.6, rx: 1.8 }, '#FFC23D'],
  ],
  'signal-stream': [
    [
      'path',
      {
        d: 'M21.2 4h11.6c1.1 0 1.8 1.1 1.4 2.1L29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16H11.4c-1.2 0-2-1.2-1.5-2.3L19.4 5.2c.3-.7 1-1.2 1.8-1.2z',
      },
      '#FFB020',
    ],
    [
      'path',
      {
        d: 'M29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16z',
      },
      '#FF7A1A',
    ],
    ['path', { d: 'M22.4 8.6l-5 11.2' }, '#FFFFFF', 'line'],
    ['circle', { cx: 41, cy: 8.5, r: 2.4 }, '#FFD966'],
    ['circle', { cx: 7.5, cy: 37, r: 2 }, '#FFD966'],
  ],
  signature: [
    ['path', { d: 'M7 40H41' }, ['--cl-base', '#2D2A5C'], 'thick'],
    [
      'path',
      {
        d: 'M7 33C11 32 17 27 20 20C22 15 21 9.5 17 9.5C13 9.5 12 16 13 23C13.8 28.5 15.5 33 19 31.5C21.5 30.4 23.5 26 26 26C28.5 26 28 30.5 30.5 30.5C33 30.5 34 26.5 36.5 26.5C38.5 26.5 39.5 28 41 29',
      },
      '#FF6FAE',
      'thick',
    ],
  ],
  sitemap: [
    [
      'path',
      {
        d: 'M25 24h3.2M33 10.5h-1.7a2.8 2.8 0 0 0-2.8 2.8v21.4a2.8 2.8 0 0 0 2.8 2.8H33',
      },
      '#A8238F',
      'line',
    ],
    ['rect', { x: 31.5, y: 4.5, width: 12, height: 12, rx: 3.8 }, '#A6DC2C'],
    ['rect', { x: 31.5, y: 31.5, width: 12, height: 12, rx: 3.8 }, '#A6DC2C'],
    ['circle', { cx: 14.5, cy: 24, r: 10.5 }, '#E23DBE'],
    [
      'path',
      {
        d: 'M11.6 19.3c0-1.5 1.6-2.4 2.9-1.6l7.3 4.6c1.2.8 1.2 2.6 0 3.4l-7.3 4.6c-1.3.8-2.9-.1-2.9-1.6z',
      },
      '#FFFFFF',
    ],
  ],
  skill: [
    ['path', { d: 'M8.5 40.5L24.5 24.5' }, '#F03A55', 'thick'],
    ['path', { d: 'M11.6 34.2l3.2 3.2' }, '#FFFFFF', 'line'],
    [
      'path',
      {
        d: 'M31.26 5.75Q32.72 3.47 33.4 6.09L34.81 11.48Q35.17 12.83 36.52 13.19L41.91 14.6Q44.53 15.28 42.25 16.74L37.56 19.74Q36.38 20.5 36.46 21.89L36.79 27.46Q36.95 30.15 34.86 28.44L30.55 24.91Q29.47 24.02 28.16 24.53L22.97 26.56Q20.46 27.54 21.44 25.03L23.47 19.84Q23.98 18.53 23.09 17.45L19.56 13.14Q17.85 11.05 20.54 11.21L26.11 11.54Q27.5 11.62 28.26 10.44Z',
      },
      '#FFC63A',
    ],
    ['circle', { cx: 27.2, cy: 14, r: 2 }, '#FFFFFF'],
    [
      'path',
      {
        d: 'M10.5 7.9l1.93 2.67 2.67 1.93-2.67 1.93-1.93 2.67-1.93-2.67-2.67-1.93 2.67-1.93z',
      },
      '#FF8A9B',
    ],
    ['circle', { cx: 41.5, cy: 38.5, r: 2.2 }, '#FF8A9B'],
  ],
  teams: [
    ['circle', { cx: 36, cy: 12, r: 5 }, '#5059C9'],
    ['path', { d: 'M32 21h11v12a6 6 0 0 1-11 3z' }, '#5059C9'],
    ['circle', { cx: 25, cy: 10, r: 7 }, '#7B83EB'],
    ['path', { d: 'M16 20h19v15a10 10 0 0 1-19 0z' }, '#7B83EB'],
    ['rect', { x: 3, y: 17, width: 23, height: 23, rx: 2 }, '#6264A7'],
    ['path', { d: 'M8 23h13v3h-5v11h-3V26H8z' }, '#FFFFFF'],
  ],
  slack: [
    [
      'path',
      {
        d: 'M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z',
        transform: 'translate(3 3) scale(0.342)',
      },
      '#E01E5A',
    ],
    [
      'path',
      {
        d: 'M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z',
        transform: 'translate(3 3) scale(0.342)',
      },
      '#36C5F0',
    ],
    [
      'path',
      {
        d: 'M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z',
        transform: 'translate(3 3) scale(0.342)',
      },
      '#2EB67D',
    ],
    [
      'path',
      {
        d: 'M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z',
        transform: 'translate(3 3) scale(0.342)',
      },
      '#ECB22E',
    ],
  ],
  sliders: [
    ['rect', { x: 5, y: 7.6, width: 38, height: 4.8, rx: 2.4 }, '#4D7CFF'],
    ['rect', { x: 5, y: 21.6, width: 38, height: 4.8, rx: 2.4 }, '#4D7CFF'],
    ['rect', { x: 5, y: 35.6, width: 38, height: 4.8, rx: 2.4 }, '#4D7CFF'],
    ['circle', { cx: 17, cy: 10, r: 6 }, '#FF6FAE'],
    ['circle', { cx: 31, cy: 24, r: 6 }, '#FFC23D'],
    ['circle', { cx: 21, cy: 38, r: 6 }, '#21C45D'],
    ['circle', { cx: 15, cy: 8, r: 1.5, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 29, cy: 22, r: 1.5, opacity: 0.55 }, '#FFFFFF'],
    ['circle', { cx: 19, cy: 36, r: 1.5, opacity: 0.55 }, '#FFFFFF'],
  ],
  sparkles: [
    [
      'path',
      {
        d: 'M19.45 15.55Q21.7 25.3 31.45 27.55Q34 28 31.45 28.45Q21.7 30.7 19.45 40.45Q19 43 18.55 40.45Q16.3 30.7 6.55 28.45Q4 28 6.55 27.55Q16.3 25.3 18.55 15.55Q19 13 19.45 15.55Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M36.24 5.36Q37.44 10.56 42.64 11.76Q44 12 42.64 12.24Q37.44 13.44 36.24 18.64Q36 20 35.76 18.64Q34.56 13.44 29.36 12.24Q28 12 29.36 11.76Q34.56 10.56 35.76 5.36Q36 4 36.24 5.36Z',
      },
      '#FF6FAE',
    ],
    [
      'path',
      {
        d: 'M39.14 30.02Q39.86 33.14 42.98 33.86Q43.8 34 42.98 34.14Q39.86 34.86 39.14 37.98Q39 38.8 38.86 37.98Q38.14 34.86 35.02 34.14Q34.2 34 35.02 33.86Q38.14 33.14 38.86 30.02Q39 29.2 39.14 30.02Z',
      },
      '#FFC23D',
    ],
    ['circle', { cx: 16.4, cy: 24.6, r: 1.7 }, '#FFFFFF'],
  ],
  'spinner-third': [
    ['circle', { cx: 24, cy: 24, r: 17.5, opacity: 0.45 }, '#A893FF', 'thick'],
    ['path', { d: 'M24 6.5A17.5 17.5 0 0 1 39.16 32.75' }, '#7C5CFF', 'thick'],
    ['circle', { cx: 39.16, cy: 32.75, r: 3.8 }, '#FF6FAE'],
  ],
  square: [
    ['rect', { x: 9, y: 9, width: 30, height: 30, rx: 8 }, '#FF4D5E'],
    [
      'path',
      { d: 'M13.6 20V17.6A4 4 0 0 1 17.6 13.6H20', opacity: 0.6 },
      '#FFFFFF',
      'line',
    ],
  ],
  'square-check': [
    ['rect', { x: 5, y: 5, width: 38, height: 38, rx: 10 }, '#21C45D'],
    [
      'path',
      { d: 'M10.5 19.5c0-4.4 2.6-8 7-9', opacity: 0.55 },
      '#FFFFFF',
      'line',
    ],
    [
      'rect',
      {
        x: 10.57,
        y: 24.4,
        width: 14.65,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(45 17.9 27.2)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 15.63,
        y: 21.3,
        width: 23.14,
        height: 5.6,
        rx: 2.8,
        transform: 'rotate(-45.92 27.2 24.1)',
      },
      '#FFFFFF',
    ],
  ],
  star: [
    [
      'path',
      {
        d: 'M22.28 6.2A1.9 1.9 0 0 1 25.72 6.2L30.31 16.05A1.4 1.4 0 0 0 31.41 16.85L42.21 18.17A1.9 1.9 0 0 1 43.27 21.45L35.32 28.86A1.4 1.4 0 0 0 34.9 30.15L36.97 40.83A1.9 1.9 0 0 1 34.19 42.85L24.68 37.58A1.4 1.4 0 0 0 23.32 37.58L13.81 42.85A1.9 1.9 0 0 1 11.03 40.83L13.1 30.15A1.4 1.4 0 0 0 12.68 28.86L4.73 21.45A1.9 1.9 0 0 1 5.79 18.17L16.59 16.85A1.4 1.4 0 0 0 17.69 16.05z',
      },
      '#FFC23D',
    ],
    ['circle', { cx: 20.6, cy: 21, r: 2 }, '#FFFFFF'],
  ],
  // The favourite, rated or selected star: a deeper gold.
  'star-solid': [
    [
      'path',
      {
        d: 'M22.28 6.2A1.9 1.9 0 0 1 25.72 6.2L30.31 16.05A1.4 1.4 0 0 0 31.41 16.85L42.21 18.17A1.9 1.9 0 0 1 43.27 21.45L35.32 28.86A1.4 1.4 0 0 0 34.9 30.15L36.97 40.83A1.9 1.9 0 0 1 34.19 42.85L24.68 37.58A1.4 1.4 0 0 0 23.32 37.58L13.81 42.85A1.9 1.9 0 0 1 11.03 40.83L13.1 30.15A1.4 1.4 0 0 0 12.68 28.86L4.73 21.45A1.9 1.9 0 0 1 5.79 18.17L16.59 16.85A1.4 1.4 0 0 0 17.69 16.05z',
      },
      '#F5A623',
    ],
    ['circle', { cx: 20.6, cy: 21, r: 2 }, '#FFFFFF'],
  ],
  stars: [
    [
      'path',
      {
        d: 'M19.45 15.55Q21.7 25.3 31.45 27.55Q34 28 31.45 28.45Q21.7 30.7 19.45 40.45Q19 43 18.55 40.45Q16.3 30.7 6.55 28.45Q4 28 6.55 27.55Q16.3 25.3 18.55 15.55Q19 13 19.45 15.55Z',
      },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M36.24 5.36Q37.44 10.56 42.64 11.76Q44 12 42.64 12.24Q37.44 13.44 36.24 18.64Q36 20 35.76 18.64Q34.56 13.44 29.36 12.24Q28 12 29.36 11.76Q34.56 10.56 35.76 5.36Q36 4 36.24 5.36Z',
      },
      '#FF6FAE',
    ],
    [
      'path',
      {
        d: 'M39.14 30.02Q39.86 33.14 42.98 33.86Q43.8 34 42.98 34.14Q39.86 34.86 39.14 37.98Q39 38.8 38.86 37.98Q38.14 34.86 35.02 34.14Q34.2 34 35.02 33.86Q38.14 33.14 38.86 30.02Q39 29.2 39.14 30.02Z',
      },
      '#FFC23D',
    ],
    ['circle', { cx: 16.4, cy: 24.6, r: 1.7 }, '#FFFFFF'],
  ],
  stop: [
    ['rect', { x: 9, y: 9, width: 30, height: 30, rx: 8 }, '#FF4D5E'],
    [
      'path',
      { d: 'M13.6 20V17.6A4 4 0 0 1 17.6 13.6H20', opacity: 0.6 },
      '#FFFFFF',
      'line',
    ],
  ],
  stopwatch: [
    ['rect', { x: 21.8, y: 6, width: 4.4, height: 7 }, '#FF8A1F'],
    [
      'rect',
      {
        x: 34.6,
        y: 9.2,
        width: 4.6,
        height: 7,
        rx: 2.3,
        transform: 'rotate(45 36.9 12.7)',
      },
      ['--cl-base', '#2D2A5C'],
    ],
    [
      'rect',
      { x: 18.5, y: 3, width: 11, height: 5, rx: 2.5 },
      ['--cl-base', '#2D2A5C'],
    ],
    ['circle', { cx: 24, cy: 27.5, r: 16.5 }, '#FF8A1F'],
    ['circle', { cx: 24, cy: 27.5, r: 12 }, '#FFF4E8'],
    ['path', { d: 'M24 27.5V17.5A10 10 0 0 1 31.07 20.43Z' }, '#FFC79A'],
    ['path', { d: 'M24 27.5L29.8 21.7' }, '#2D2A5C', 'thick'],
    ['circle', { cx: 24, cy: 27.5, r: 2.2 }, '#FF8A1F'],
  ],
  store: [
    ['rect', { x: 7, y: 14, width: 34, height: 29, rx: 4 }, '#FFB3C0'],
    [
      'path',
      {
        d: 'M8 5h32a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0 4 4 0 0 1-8 0 4 4 0 0 1-8 0 4 4 0 0 1-8 0 4 4 0 0 1-8 0V9a4 4 0 0 1 4-4z',
      },
      '#FF4D5E',
    ],
    [
      'path',
      { d: 'M12 8.5h8V16a4 4 0 0 1-8 0zM28 8.5h8V16a4 4 0 0 1-8 0z' },
      '#FFFFFF',
    ],
    [
      'path',
      { d: 'M19.5 43V31.5a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3V43z' },
      '#FF4D5E',
    ],
  ],
  sun: [
    [
      'path',
      {
        d: 'M38.2 24L42.6 24M34.04 34.04L37.15 37.15M24 38.2L24 42.6M13.96 34.04L10.85 37.15M9.8 24L5.4 24M13.96 13.96L10.85 10.85M24 9.8L24 5.4M34.04 13.96L37.15 10.85',
      },
      '#FFB547',
      'thick',
    ],
    ['circle', { cx: 24, cy: 24, r: 9.8 }, '#FFC23D'],
    ['circle', { cx: 20.6, cy: 20.6, r: 2.1 }, '#FFFFFF'],
  ],
  'sync-crm': [
    [
      'path',
      {
        d: 'M4 9.6V31.4A13 5.2 0 0 0 23.44 35.92A11.8 11.8 0 0 1 30 24.41V9.6Z',
      },
      '#7C5CFF',
    ],
    [
      'path',
      { d: 'M4 18.6A13 5.2 0 0 0 30 18.6v3.4A13 5.2 0 0 1 4 22Z' },
      '#A893FF',
    ],
    ['ellipse', { cx: 17, cy: 9.6, rx: 13, ry: 5.2 }, '#A893FF'],
    ['rect', { x: 31, y: 27.8, width: 12.6, height: 4.4, rx: 2.2 }, '#2F80FF'],
    [
      'path',
      {
        d: 'M27.8 31.1Q26.6 30 27.8 28.9L32.2 25.1Q34 23.6 34 26V34Q34 36.4 32.2 34.9Z',
      },
      '#2F80FF',
    ],
    [
      'rect',
      { x: 26.8, y: 37.8, width: 12.6, height: 4.4, rx: 2.2 },
      '#2F80FF',
    ],
    [
      'path',
      {
        d: 'M42.6 38.9Q43.8 40 42.6 41.1L38.2 44.9Q36.4 46.4 36.4 44V36Q36.4 33.6 38.2 35.1Z',
      },
      '#2F80FF',
    ],
  ],
  t: [
    ['rect', { x: 20.8, y: 6.3, width: 6.4, height: 35.9, rx: 3.2 }, '#6C63FF'],
    ['rect', { x: 6.8, y: 6.3, width: 34.4, height: 6.4, rx: 3.2 }, '#FF6FAE'],
    ['path', { d: 'M10.5 8.4H15.5', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  table: [
    ['rect', { x: 5, y: 7, width: 38, height: 34, rx: 7 }, '#4D7CFF'],
    ['rect', { x: 8.5, y: 17, width: 31, height: 20.5, rx: 3 }, '#FFFFFF'],
    ['rect', { x: 19, y: 17, width: 2.8, height: 20.5 }, '#4D7CFF'],
    ['rect', { x: 8.5, y: 25.9, width: 31, height: 2.8 }, '#4D7CFF'],
    ['circle', { cx: 12, cy: 12, r: 2.2 }, '#FFC23D'],
    ['rect', { x: 16.5, y: 10.5, width: 13, height: 3, rx: 1.5 }, '#FFFFFF'],
    ['rect', { x: 11, y: 19.9, width: 5.5, height: 3.2, rx: 1.6 }, '#FF6FAE'],
    ['rect', { x: 11, y: 31.5, width: 5.5, height: 3.2, rx: 1.6 }, '#FFC23D'],
    ['rect', { x: 24.5, y: 19.9, width: 12, height: 3.2, rx: 1.6 }, '#BFD3FF'],
    ['rect', { x: 24.5, y: 31.5, width: 8.5, height: 3.2, rx: 1.6 }, '#BFD3FF'],
  ],
  'table-columns': [
    ['rect', { x: 4.8, y: 5.8, width: 38.4, height: 36.4, rx: 8.2 }, '#4D7CFF'],
    ['rect', { x: 9.2, y: 20.2, width: 12.6, height: 17.6, rx: 3 }, '#FFFFFF'],
    ['rect', { x: 26.2, y: 20.2, width: 12.6, height: 17.6, rx: 3 }, '#FFC23D'],
    ['path', { d: 'M10.5 11.5H15.5', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  tag: [
    [
      'path',
      {
        d: 'M6 10.5A4.5 4.5 0 0 1 10.5 6L23.26 6A3 3 0 0 1 25.38 6.88L40.03 21.53A3.5 3.5 0 0 1 40.03 26.47L26.47 40.03A3.5 3.5 0 0 1 21.53 40.03L6.88 25.38A3 3 0 0 1 6 23.26z',
        transform: 'translate(24 24) scale(1.08) translate(-23.6 -23.6)',
      },
      '#4D7CFF',
    ],
    [
      'circle',
      {
        cx: 14.6,
        cy: 14.6,
        r: 3.8,
        transform: 'translate(24 24) scale(1.08) translate(-23.6 -23.6)',
      },
      '#FFFFFF',
    ],
  ],
  templates: [
    ['rect', { x: 6, y: 5, width: 36, height: 38, rx: 7 }, '#12B07B'],
    ['rect', { x: 11, y: 10.5, width: 26, height: 7, rx: 2.6 }, '#FFFFFF'],
    ['rect', { x: 11, y: 21.5, width: 12, height: 16, rx: 2.6 }, '#FFD13F'],
    [
      'rect',
      { x: 26.5, y: 21.5, width: 10.5, height: 3.4, rx: 1.7 },
      '#FFFFFF',
    ],
    [
      'rect',
      { x: 26.5, y: 27.8, width: 10.5, height: 3.4, rx: 1.7 },
      '#FFFFFF',
    ],
    ['rect', { x: 26.5, y: 34.1, width: 7, height: 3.4, rx: 1.7 }, '#FFFFFF'],
  ],
  'test-run': [
    [
      'path',
      {
        d: 'M19.5 7h9v11.4l10.7 19.4c1.5 2.7-.5 6.2-3.6 6.2H12.4c-3.1 0-5.1-3.5-3.6-6.2L19.5 18.4z',
      },
      '#C6F07F',
    ],
    [
      'path',
      {
        d: 'M14.3 28h19.4l5.5 9.8c1.5 2.7-.5 6.2-3.6 6.2H12.4c-3.1 0-5.1-3.5-3.6-6.2z',
      },
      '#7CC71E',
    ],
    ['rect', { x: 16, y: 4, width: 16, height: 5, rx: 2.5 }, '#3F9416'],
    ['circle', { cx: 20, cy: 36.5, r: 2.4 }, '#FFFFFF'],
    ['circle', { cx: 27.5, cy: 32.5, r: 1.6 }, '#FFFFFF'],
    ['circle', { cx: 25.5, cy: 21.5, r: 1.7 }, '#7CC71E'],
    ['circle', { cx: 22.5, cy: 14.5, r: 1.2 }, '#7CC71E'],
  ],
  text: [
    ['rect', { x: 21, y: 10.5, width: 6, height: 27 }, '#6C63FF'],
    ['rect', { x: 14.5, y: 34.5, width: 19, height: 6, rx: 3 }, '#6C63FF'],
    [
      'path',
      {
        d: 'M10.5 7.5H37.5A3 3 0 0 1 40.5 10.5V16.5A3 3 0 0 1 34.5 16.5V13.5H13.5V16.5A3 3 0 0 1 7.5 16.5V10.5A3 3 0 0 1 10.5 7.5Z',
      },
      '#FF6FAE',
    ],
    ['path', { d: 'M15 9.4H20', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  'thumbs-down': [
    ['rect', { x: 4.5, y: 5, width: 9, height: 23, rx: 3 }, '#E23A50'],
    [
      'rect',
      {
        x: 18,
        y: 21.5,
        width: 11,
        height: 23,
        rx: 5.5,
        transform: 'rotate(-12 23.5 33)',
      },
      '#FF4D5E',
    ],
    ['rect', { x: 16, y: 5, width: 27.5, height: 24.5, rx: 6.5 }, '#FF4D5E'],
    ['path', { d: 'M31.5 23.4H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 17.3H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 11.2H41' }, '#FFFFFF', 'line'],
    ['circle', { cx: 9, cy: 10.5, r: 1.7 }, '#FFB3C0'],
  ],
  'thumbs-up': [
    ['rect', { x: 4.5, y: 20, width: 9, height: 23, rx: 3 }, '#0A8A5F'],
    [
      'rect',
      {
        x: 18,
        y: 3.5,
        width: 11,
        height: 23,
        rx: 5.5,
        transform: 'rotate(12 23.5 15)',
      },
      '#14B87F',
    ],
    ['rect', { x: 16, y: 18.5, width: 27.5, height: 24.5, rx: 6.5 }, '#14B87F'],
    ['path', { d: 'M31.5 24.6H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 30.7H41' }, '#FFFFFF', 'line'],
    ['path', { d: 'M31.5 36.8H41' }, '#FFFFFF', 'line'],
    ['circle', { cx: 9, cy: 37.5, r: 1.7 }, '#8EEBC6'],
  ],
  thumbtack: [
    [
      'path',
      { d: 'M24 34V43.5', transform: 'translate(0 -0.9)' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    [
      'path',
      {
        d: 'M16.5 4H31.5a3.5 3.5 0 0 1 0 7H30.6L31.4 21.6C36.4 24.2 39.6 28 40 31.6a2 2 0 0 1-2 2.2H10a2 2 0 0 1-2-2.2C8.4 28 11.6 24.2 16.6 21.6L17.4 11H16.5a3.5 3.5 0 0 1 0-7z',
        transform: 'translate(0 -0.9)',
      },
      '#FF4D5E',
    ],
    [
      'path',
      { d: 'M17 7.5H21', transform: 'translate(0 -0.9)' },
      '#FFFFFF',
      'line',
    ],
  ],
  'thumbtack-slash': [
    [
      'path',
      { d: 'M24 34V43.5', transform: 'translate(0 -0.9)' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
    [
      'path',
      {
        d: 'M27.86 33.8 10 33.8 9.87 33.8 9.73 33.78 9.6 33.76 9.47 33.73 9.34 33.69 9.22 33.64 9.09 33.59 8.98 33.52 8.86 33.45 8.76 33.37 8.65 33.29 8.56 33.19 8.47 33.1 8.38 32.99 8.31 32.88 8.24 32.76 8.18 32.64 8.12 32.52 8.08 32.39 8.04 32.27 8.02 32.13 8 32 7.99 31.87 7.99 31.73 8 31.6 8.06 31.15 8.16 30.7 8.28 30.24 8.43 29.79 8.61 29.34 8.81 28.88 9.04 28.43 9.3 27.99 9.58 27.54 9.89 27.1 10.22 26.66 10.57 26.22 10.96 25.8 11.36 25.37 11.79 24.96 12.24 24.55 12.71 24.15 13.2 23.75 13.72 23.37 14.25 22.99 14.81 22.63 15.39 22.27 15.98 21.93z',
        transform: 'translate(0 -0.9)',
      },
      '#FF4D5E',
    ],
    [
      'path',
      {
        d: 'M16.5 4 31.5 4 31.73 4.01 31.96 4.03 32.18 4.07 32.41 4.12 32.63 4.19 32.84 4.27 33.05 4.36 33.25 4.47 33.44 4.59 33.63 4.72 33.81 4.87 33.97 5.03 34.13 5.19 34.28 5.37 34.41 5.56 34.53 5.75 34.64 5.95 34.73 6.16 34.81 6.37 34.88 6.59 34.93 6.82 34.97 7.04 34.99 7.27 35 7.5 34.99 7.73 34.97 7.96 34.93 8.18 34.88 8.41 34.81 8.63 34.73 8.84 34.64 9.05 34.53 9.25 34.41 9.44 34.28 9.63 34.13 9.81 33.97 9.97 33.81 10.13 33.63 10.28 33.44 10.41 33.25 10.53 33.05 10.64 32.84 10.73 32.63 10.81 32.41 10.88 32.18 10.93 31.96 10.97 31.73 10.99 31.5 11 30.6 11 31.4 21.6 32.02 21.93 32.61 22.27 33.19 22.63 33.75 22.99 34.28 23.37 34.8 23.75 35.29 24.15 35.76 24.55 36.21 24.96 36.64 25.37 37.04 25.8 37.42 26.22 37.78 26.66 38.11 27.1 38.42 27.54 38.7 27.99 38.96 28.43 39.19 28.88 39.39 29.34 39.57 29.79 39.72 30.24 39.84 30.7 39.94 31.15 40 31.6 40.01 31.73 40.01 31.87 40 32 39.98 32.13 39.96 32.27 39.92 32.39 39.88 32.52 39.82 32.64 39.76 32.76 39.69 32.88 39.62 32.99 39.53 33.1 39.44 33.19 39.35 33.29 17.37 11.43 17.4 11 16.94 11 13.03 7.09 13.07 6.82 13.12 6.59 13.19 6.37 13.27 6.16 13.36 5.95 13.47 5.75 13.59 5.56 13.72 5.37 13.87 5.19 14.03 5.03 14.19 4.87 14.37 4.72 14.56 4.59 14.75 4.47 14.95 4.36 15.16 4.27 15.37 4.19 15.59 4.12 15.82 4.07 16.04 4.03 16.27 4.01z',
        transform: 'translate(0 -0.9)',
      },
      '#FF4D5E',
    ],
    [
      'path',
      { d: 'M17 7.5H21', transform: 'translate(0 -0.9)' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      { d: 'M7 7L41 41', transform: 'translate(0 -0.9)' },
      ['--cl-base', '#2D2A5C'],
      'thick',
    ],
  ],
  ticket: [
    [
      'path',
      {
        d: 'M7 10H31.5V38H7a4 4 0 0 1-4-4V28.5a4.5 4.5 0 0 0 0-9V14a4 4 0 0 1 4-4z',
      },
      '#FFB547',
    ],
    [
      'path',
      {
        d: 'M34.5 10H41a4 4 0 0 1 4 4v5.5a4.5 4.5 0 0 0 0 9V34a4 4 0 0 1-4 4h-6.5z',
      },
      '#FF8A3D',
    ],
    [
      'path',
      {
        d: 'M17.5 16.6l2.2 4.5 4.9.7-3.6 3.5.9 4.9-4.4-2.3-4.4 2.3.9-4.9-3.6-3.5 4.9-.7z',
      },
      '#FFFFFF',
    ],
  ],
  'toggle-on': [
    ['rect', { x: 2.5, y: 9, width: 43, height: 30, rx: 15 }, '#21C45D'],
    ['circle', { cx: 30.5, cy: 24, r: 11 }, '#FFFFFF'],
  ],
  trash: [
    ['rect', { x: 18, y: 3.5, width: 12, height: 8, rx: 3 }, '#E0344A'],
    ['rect', { x: 6, y: 9, width: 36, height: 6.6, rx: 3.3 }, '#E0344A'],
    [
      'path',
      {
        d: 'M9.68 20.09Q9.5 18.5 11.1 18.5L36.9 18.5Q38.5 18.5 38.32 20.09L36.11 39.53Q35.6 44 31.1 44L16.9 44Q12.4 44 11.89 39.53Z',
      },
      '#FF4D5E',
    ],
    ['rect', { x: 17.8, y: 23.5, width: 3.6, height: 15, rx: 1.8 }, '#FFFFFF'],
    ['rect', { x: 26.6, y: 23.5, width: 3.6, height: 15, rx: 1.8 }, '#FFFFFF'],
  ],
  'triangle-exclamation': [
    [
      'path',
      {
        d: 'M19.46 8.17A5.2 5.2 0 0 1 28.54 8.17L43.5 35.07A5.2 5.2 0 0 1 38.96 42.8L9.04 42.8A5.2 5.2 0 0 1 4.5 35.07Z',
      },
      '#FFB547',
    ],
    ['rect', { x: 21.3, y: 16.1, width: 5.4, height: 15, rx: 2.7 }, '#6B3A00'],
    ['circle', { cx: 24, cy: 36.8, r: 3.1 }, '#6B3A00'],
  ],
  trigger: [
    [
      'path',
      {
        d: 'M21.2 4h11.6c1.1 0 1.8 1.1 1.4 2.1L29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16H11.4c-1.2 0-2-1.2-1.5-2.3L19.4 5.2c.3-.7 1-1.2 1.8-1.2z',
      },
      '#FFB020',
    ],
    [
      'path',
      {
        d: 'M29.6 17h8.2c1.3 0 2 1.5 1.2 2.5L19.4 43.6c-.8 1-2.4.2-2.1-1l3.7-16z',
      },
      '#FF7A1A',
    ],
    ['path', { d: 'M22.4 8.6l-5 11.2' }, '#FFFFFF', 'line'],
    ['circle', { cx: 41, cy: 8.5, r: 2.4 }, '#FFD966'],
    ['circle', { cx: 7.5, cy: 37, r: 2 }, '#FFD966'],
  ],
  'unknown-step': [
    ['rect', { x: 7, y: 7, width: 34, height: 34, rx: 10 }, '#8792A8'],
    [
      'path',
      { d: 'M18.8 19.6a5.4 5.4 0 1 1 8.1 4.7c-1.7 1-2.9 2.1-2.9 3.9v.6' },
      '#FFFFFF',
      'thick',
    ],
    ['circle', { cx: 24, cy: 34.6, r: 2.6 }, '#FFFFFF'],
  ],
  'up-right-and-down-left-from-center': [
    ['path', { d: 'M37.5 10.5L30 18' }, '#A893FF', 'thick'],
    ['path', { d: 'M10.5 37.5L18 30' }, '#A893FF', 'thick'],
    ['path', { d: 'M29.33 8H37.5Q40 8 40 10.5V18.67' }, '#6C63FF', 'thick'],
    ['path', { d: 'M18.67 40H10.5Q8 40 8 37.5V29.33' }, '#6C63FF', 'thick'],
    ['circle', { cx: 24, cy: 24, r: 3 }, '#FFC23D'],
  ],
  upload: [
    [
      'path',
      { d: 'M7 29V34A6 6 0 0 0 13 40H35A6 6 0 0 0 41 34V29' },
      '#6C63FF',
      'thick',
    ],
    ['path', { d: 'M24 30V11.5' }, '#21C45D', 'thick'],
    [
      'path',
      { d: 'M15 18L22.23 10.77Q24 9 25.77 10.77L33 18' },
      '#16A34A',
      'thick',
    ],
  ],
  user: [
    [
      'path',
      {
        d: 'M7.9 40.7A16.1 14.37 0 0 1 40.1 40.7A2.3 2.3 0 0 1 37.8 43L10.2 43A2.3 2.3 0 0 1 7.9 40.7Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 24, cy: 13.68, r: 8.62 }, '#A893FF'],
  ],
  'user-check': [
    [
      'path',
      {
        d: 'M5 40.5A14 12.5 0 0 1 33 40.5A2 2 0 0 1 31 42.5L7 42.5A2 2 0 0 1 5 40.5Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 19, cy: 17.0, r: 7.5 }, '#A893FF'],
    ['path', { d: 'M31.4 16.4l4 4 8-8.6' }, '#21C45D', 'thick'],
  ],
  'user-gear': [
    [
      'path',
      {
        d: 'M5 40.5A14 12.5 0 0 1 33 40.5A2 2 0 0 1 31 42.5L7 42.5A2 2 0 0 1 5 40.5Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 19, cy: 17.0, r: 7.5 }, '#A893FF'],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(0 37 16.2)',
      },
      '#7C8DB5',
    ],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(60 37 16.2)',
      },
      '#7C8DB5',
    ],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(120 37 16.2)',
      },
      '#7C8DB5',
    ],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(180 37 16.2)',
      },
      '#7C8DB5',
    ],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(240 37 16.2)',
      },
      '#7C8DB5',
    ],
    [
      'rect',
      {
        x: 34.7,
        y: 7.5,
        width: 4.6,
        height: 5,
        rx: 1.3,
        transform: 'rotate(300 37 16.2)',
      },
      '#7C8DB5',
    ],
    ['circle', { cx: 37, cy: 16.2, r: 6.5 }, '#7C8DB5'],
    ['circle', { cx: 37, cy: 16.2, r: 2.8 }, '#E9EDF7'],
    ['circle', { cx: 37.2, cy: 16, r: 1.6 }, '#46557A'],
  ],
  'user-group': [
    [
      'path',
      {
        d: 'M29.76 28.59A11.76 10.5 0 0 1 44.16 38.82A1.68 1.68 0 0 1 42.48 40.5L33.49 40.5A4.2 4.2 0 0 0 34 38.5A16.2 14.7 0 0 0 29.76 28.59Z',
      },
      '#A893FF',
    ],
    [
      'path',
      { d: 'M27.5 15.12A6.3 6.3 0 1 1 26.16 19.92A9.7 9.7 0 0 0 27.5 15.12Z' },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M3.8 38.5A14 12.5 0 0 1 31.8 38.5A2 2 0 0 1 29.8 40.5L5.8 40.5A2 2 0 0 1 3.8 38.5Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 17.8, cy: 15.0, r: 7.5 }, '#A893FF'],
  ],
  'user-magnifying-glass': [
    [
      'path',
      {
        d: 'M7 40.5c0-7.4 6-12.5 14-12.5s14 5.1 14 12.5a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 21, cy: 17, r: 7.5 }, '#A893FF'],
    ['path', { d: 'M39.6 36.6L43.6 40.6' }, ['--cl-base', '#252A57'], 'thick'],
    ['circle', { cx: 34, cy: 31, r: 8 }, ['--cl-base', '#252A57']],
    ['circle', { cx: 34, cy: 31, r: 5.6 }, '#7DF1E5'],
    ['circle', { cx: 32, cy: 29, r: 1.6 }, '#FFFFFF'],
  ],
  'user-minus': [
    [
      'path',
      {
        d: 'M5 40.5A14 12.5 0 0 1 33 40.5A2 2 0 0 1 31 42.5L7 42.5A2 2 0 0 1 5 40.5Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 19, cy: 17.0, r: 7.5 }, '#A893FF'],
    ['path', { d: 'M31 16h12' }, '#FF4D5E', 'thick'],
  ],
  'user-plus': [
    [
      'path',
      {
        d: 'M5 40.5c0-7.4 6-12.5 14-12.5s14 5.1 14 12.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z',
      },
      '#FF7A2A',
    ],
    ['circle', { cx: 19, cy: 17, r: 7.5 }, '#FFB070'],
    ['path', { d: 'M37 10v12M31 16h12' }, '#FF4D94', 'thick'],
  ],
  'user-slash': [
    [
      'path',
      {
        d: 'M37.06 43L10.2 43A2.3 2.3 0 0 1 7.9 40.7A16.1 14.37 0 0 1 20.69 26.63L37.06 43Z',
      },
      '#7C5CFF',
    ],
    [
      'path',
      { d: 'M16.12 10.18A8.62 8.62 0 1 1 27.5 21.56L16.12 10.18Z' },
      '#A893FF',
    ],
    ['path', { d: 'M7.5 7.5L40.5 40.5' }, '#7C5CFF', 'thick'],
  ],
  users: [
    [
      'path',
      {
        d: 'M9.63 39.2L3.6 39.2A1.5 1.5 0 0 1 2.1 37.7A10.5 9.38 0 0 1 13.07 28.33A14.8 13.45 0 0 0 9.2 37.4A4 4 0 0 0 9.63 39.2Z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M17.65 22.56A5.62 5.62 0 1 1 15.13 15.05A8.95 8.95 0 0 0 17.65 22.56Z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M34.93 28.33A10.5 9.38 0 0 1 45.9 37.7A1.5 1.5 0 0 1 44.4 39.2L38.37 39.2A4 4 0 0 0 38.8 37.4A14.8 13.45 0 0 0 34.93 28.33Z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M32.87 15.05A5.62 5.62 0 1 1 30.35 22.56A8.95 8.95 0 0 0 32.87 15.05Z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M11.4 37.4A12.6 11.25 0 1 1 36.6 37.4A1.8 1.8 0 0 1 34.8 39.2L13.2 39.2A1.8 1.8 0 0 1 11.4 37.4Z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 24, cy: 16.25, r: 6.75 }, '#A893FF'],
  ],
  wait: [
    [
      'path',
      {
        d: 'M13 9h22c0 8.5-7.5 11.5-7.5 15S35 30.5 35 39H13c0-8.5 7.5-11.5 7.5-15S13 17.5 13 9z',
      },
      '#D9B3FF',
    ],
    [
      'path',
      { d: 'M17.6 14h12.8c-1.2 4-4.6 5.6-6.4 8.4-1.8-2.8-5.2-4.4-6.4-8.4z' },
      '#FFC23D',
    ],
    ['rect', { x: 23.1, y: 22, width: 1.8, height: 11, rx: 0.9 }, '#FFC23D'],
    ['path', { d: 'M15.6 39c.8-4.6 4.6-7 8.4-7s7.6 2.4 8.4 7z' }, '#FFC23D'],
    ['rect', { x: 9, y: 4, width: 30, height: 6.5, rx: 3.25 }, '#A33BD8'],
    ['rect', { x: 9, y: 37.5, width: 30, height: 6.5, rx: 3.25 }, '#A33BD8'],
  ],
  wallet: [
    [
      'rect',
      {
        x: 9,
        y: 6,
        width: 24,
        height: 14,
        rx: 3,
        transform: 'rotate(-8 21 13)',
      },
      '#FFC23D',
    ],
    ['rect', { x: 4, y: 13, width: 37, height: 30, rx: 6 }, '#21C45D'],
    [
      'path',
      { d: 'M28 22h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H28a6 6 0 0 1 0-12z' },
      '#16A34A',
    ],
    ['circle', { cx: 29, cy: 28, r: 2.8 }, '#FFC23D'],
  ],
  'wand-magic-sparkles': [
    ['path', { d: 'M8.5 40.5L24.5 24.5' }, '#F03A55', 'thick'],
    ['path', { d: 'M11.6 34.2l3.2 3.2' }, '#FFFFFF', 'line'],
    [
      'path',
      {
        d: 'M31.26 5.75Q32.72 3.47 33.4 6.09L34.81 11.48Q35.17 12.83 36.52 13.19L41.91 14.6Q44.53 15.28 42.25 16.74L37.56 19.74Q36.38 20.5 36.46 21.89L36.79 27.46Q36.95 30.15 34.86 28.44L30.55 24.91Q29.47 24.02 28.16 24.53L22.97 26.56Q20.46 27.54 21.44 25.03L23.47 19.84Q23.98 18.53 23.09 17.45L19.56 13.14Q17.85 11.05 20.54 11.21L26.11 11.54Q27.5 11.62 28.26 10.44Z',
      },
      '#FFC63A',
    ],
    ['circle', { cx: 27.2, cy: 14, r: 2 }, '#FFFFFF'],
    [
      'path',
      {
        d: 'M10.5 7.9l1.93 2.67 2.67 1.93-2.67 1.93-1.93 2.67-1.93-2.67-2.67-1.93 2.67-1.93z',
      },
      '#FF8A9B',
    ],
    ['circle', { cx: 41.5, cy: 38.5, r: 2.2 }, '#FF8A9B'],
  ],
  warehouse: [
    [
      'path',
      {
        d: 'M4 20.6L22 10a4 4 0 0 1 4 0l18 10.6V40a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z',
      },
      '#FFB547',
    ],
    ['path', { d: 'M4.5 19.4L24 8l19.5 11.4' }, '#E58A00', 'thick'],
    ['rect', { x: 12, y: 25, width: 24, height: 18, rx: 2 }, '#E58A00'],
    ['rect', { x: 14.5, y: 27.5, width: 19, height: 3, rx: 1.5 }, '#FFE3BF'],
    ['rect', { x: 14.5, y: 33, width: 19, height: 3, rx: 1.5 }, '#FFE3BF'],
    ['rect', { x: 14.5, y: 38.5, width: 19, height: 3, rx: 1.5 }, '#FFE3BF'],
  ],
  'wave-pulse': [
    ['rect', { x: 5, y: 7, width: 38, height: 34, rx: 9 }, '#FF6FAE'],
    [
      'path',
      { d: 'M10 25h5.5l3.5-9 6 18 4-12.5 2.5 3.5H38' },
      '#FFFFFF',
      'thick',
    ],
  ],
  webhook: [
    ['path', { d: 'M9.6 27.2a8 8 0 1 0 12.4 6.8h12' }, '#C9184A', 'thick'],
    [
      'path',
      { d: 'M30.2 41a8 8 0 1 0 3.8-15c-1.4 0-2.8.4-4 1l-6-11' },
      ['--cl-base', '#252A57'],
      'thick',
    ],
    [
      'path',
      { d: 'M32 16a8 8 0 1 0-16 0c0 3 1.5 5.6 4 7l-6 11' },
      '#FF4D6D',
      'thick',
    ],
  ],
  'wifi-slash': [
    ['path', { d: 'M8.4 22.4A22 22 0 0 1 39.6 22.4' }, '#8FD3FF', 'thick'],
    ['path', { d: 'M14.1 28.1A14 14 0 0 1 33.9 28.1' }, '#4D7CFF', 'thick'],
    ['circle', { cx: 24, cy: 36, r: 3.6 }, '#2F80FF'],
    ['path', { d: 'M9 9L39 39' }, '#FF4D5E', 'thick'],
  ],
  'window-restore': [
    [
      'path',
      {
        d: 'M19.8 5L39 5Q44 5 44 10L44 26.2Q44 31 39.2 31L36.8 31Q34.4 31 34.4 28.6L34.4 19.1Q34.4 12.6 27.9 12.6L18.4 12.6Q16 12.6 16 10.2L16 8.8Q16 5 19.8 5Z',
      },
      '#8FD3FF',
    ],
    ['rect', { x: 4, y: 15, width: 28, height: 28, rx: 5 }, '#2F80FF'],
    ['rect', { x: 8, y: 23, width: 20, height: 16, rx: 2.5 }, '#FFFFFF'],
  ],
  'x-twitter': [
    ['path', { d: 'M8.5 8h10.2l20.8 32H29.3z' }, 'currentColor'],
    ['path', { d: 'M38.6 8L9.4 40' }, 'currentColor', 'thick'],
  ],
  xmark: [
    [
      'rect',
      {
        x: 3.53,
        y: 20.5,
        width: 40.94,
        height: 7,
        rx: 3.5,
        transform: 'rotate(45 24 24)',
      },
      '#FF4D5E',
    ],
    [
      'rect',
      {
        x: 3.53,
        y: 20.5,
        width: 40.94,
        height: 7,
        rx: 3.5,
        transform: 'rotate(135 24 24)',
      },
      '#FF4D5E',
    ],
    ['path', { d: 'M12.2 14.5L15.5 17.8', opacity: 0.6 }, '#FFFFFF', 'line'],
  ],
  // Find People and Find Companies filter rows (search overhaul, 2026-10-30)
  'headline-card': [
    ['rect', { x: 3, y: 8, width: 42, height: 32, rx: 7 }, '#6FB6FF'],
    ['rect', { x: 8, y: 14, width: 32, height: 7.5, rx: 3.75 }, '#FFD23F'],
    ['rect', { x: 8, y: 26, width: 24, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['rect', { x: 8, y: 32, width: 15, height: 3.4, rx: 1.7 }, '#FFFFFF'],
    ['circle', { cx: 37, cy: 33.2, r: 3.6 }, '#3A7BF0'],
  ],
  'summary-doc': [
    [
      'path',
      {
        d: 'M9 9Q9 4 14 4L26.4 4Q28 4 29.13 5.13L37.87 13.87Q39 15 39 16.6L39 39Q39 44 34 44L14 44Q9 44 9 39Z',
      },
      '#7C5CFF',
    ],
    [
      'path',
      {
        d: 'M12 9Q12 7 14 7L26.26 7Q26.76 7 27.11 7.35L35.65 15.89Q36 16.24 36 16.74L36 39Q36 41 34 41L14 41Q12 41 12 39Z',
      },
      '#F3EEFF',
    ],
    [
      'path',
      { d: 'M26.76 6L26.76 13.64Q26.76 16.24 29.36 16.24L37 16.24Z' },
      '#7C5CFF',
    ],
    ['rect', { x: 15, y: 20, width: 18, height: 3.4, rx: 1.7 }, '#FF6FAE'],
    ['rect', { x: 15, y: 26.6, width: 18, height: 3.4, rx: 1.7 }, '#A893FF'],
    ['rect', { x: 15, y: 33.2, width: 11, height: 3.4, rx: 1.7 }, '#FFC23D'],
  ],
  'x-handle': [
    ['rect', { x: 4, y: 4, width: 38, height: 38, rx: 9 }, '#1F2433'],
    ['path', { d: 'M14 13.5L31.5 33.5M31.5 13.5L14 33.5' }, '#FFFFFF', 'thick'],
    ['circle', { cx: 37.5, cy: 37.5, r: 9.5 }, '#FFFFFF'],
    ['circle', { cx: 37.5, cy: 37.5, r: 7.8 }, '#1D9BF0'],
    ['circle', { cx: 37.5, cy: 37.5, r: 2 }, '#FFFFFF', 'line'],
    [
      'path',
      { d: 'M39.5 36.5v1.6a1.3 1.3 0 0 0 2.6 0v-.6a4.6 4.6 0 1 0 -2.2 3.9' },
      '#FFFFFF',
      'line',
    ],
  ],
  hourglass: [
    [
      'path',
      {
        d: 'M13 9h22c0 7-5 11-8.5 15C30 28 35 32 35 39H13c0-7 5-11 8.5-15C18 20 13 16 13 9z',
      },
      '#D9F1FF',
    ],
    [
      'path',
      { d: 'M16.5 13h15c-1 3.5-4 6-7.5 8.5c-3.5-2.5-6.5-5-7.5-8.5z' },
      '#FFC23D',
    ],
    ['rect', { x: 23.2, y: 21, width: 1.6, height: 9, rx: 0.8 }, '#FFC23D'],
    [
      'path',
      { d: 'M15.4 37c.9-4 4.4-6.8 8.6-8.5c4.2 1.7 7.7 4.5 8.6 8.5z' },
      '#FF8A1F',
    ],
    ['rect', { x: 9, y: 4, width: 30, height: 6, rx: 3 }, '#7C5CFF'],
    ['rect', { x: 9, y: 38, width: 30, height: 6, rx: 3 }, '#7C5CFF'],
  ],
  'study-book': [
    [
      'path',
      { d: 'M3.5 12.5c6.5-2.5 13.5-2 20.5 1.5v27c-7-3.5-14-4-20.5-1.5z' },
      '#4D7CFF',
    ],
    [
      'path',
      { d: 'M44.5 12.5c-6.5-2.5-13.5-2-20.5 1.5v27c7-3.5 14-4 20.5-1.5z' },
      '#7C5CFF',
    ],
    [
      'path',
      { d: 'M8.5 19.5c3.3-.7 6.6-.4 10 .9M8.5 26c3.3-.7 6.6-.4 10 .9' },
      '#FFFFFF',
      'line',
    ],
    [
      'path',
      { d: 'M39.5 19.5c-3.3-.7-6.6-.4-10 .9M39.5 26c-3.3-.7-6.6-.4-10 .9' },
      '#FFFFFF',
      'line',
    ],
    ['path', { d: 'M31 7h6v12l-3-2.4-3 2.4z' }, '#FF6FAE'],
  ],
  connections: [
    [
      'path',
      { d: 'M13 14.5L35 14.5M13 14.5L24 34M35 14.5L24 34' },
      '#C9CCE0',
      'thick',
    ],
    ['circle', { cx: 13, cy: 14.5, r: 8 }, '#7C5CFF'],
    ['circle', { cx: 35, cy: 14.5, r: 8 }, '#FF6FAE'],
    ['circle', { cx: 24, cy: 34, r: 9 }, '#19B6EC'],
    ['circle', { cx: 13, cy: 12.6, r: 2.6 }, '#FFFFFF'],
    [
      'path',
      { d: 'M8.6 19c.8-2.4 2.4-3.6 4.4-3.6s3.6 1.2 4.4 3.6' },
      '#FFFFFF',
    ],
    ['circle', { cx: 35, cy: 12.6, r: 2.6 }, '#FFFFFF'],
    [
      'path',
      { d: 'M30.6 19c.8-2.4 2.4-3.6 4.4-3.6s3.6 1.2 4.4 3.6' },
      '#FFFFFF',
    ],
    ['circle', { cx: 24, cy: 31.8, r: 3 }, '#FFFFFF'],
    ['path', { d: 'M19 39c.9-2.8 2.7-4.2 5-4.2s4.1 1.4 5 4.2' }, '#FFFFFF'],
  ],
  'company-linkedin': [
    [
      'rect',
      {
        x: 7,
        y: 5,
        width: 19,
        height: 37,
        rx: 4,
        transform: 'translate(0 0) scale(0.82)',
      },
      '#B44CE6',
    ],
    [
      'rect',
      {
        x: 23,
        y: 17,
        width: 18,
        height: 25,
        rx: 4,
        transform: 'translate(0 0) scale(0.82)',
      },
      '#F27BC9',
    ],
    [
      'path',
      {
        d: 'M12 11.5h6M12 17.5h6M12 23.5h6M12 29.5h6',
        transform: 'translate(0 0) scale(0.82)',
      },
      ['--cl-oncard', '#FFFFFF'],
      'line',
    ],
    [
      'path',
      {
        d: 'M28.3 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM28.3 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1z',
        transform: 'translate(0 0) scale(0.82)',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
    [
      'rect',
      {
        x: 29.5,
        y: 35,
        width: 5,
        height: 7,
        rx: 1.6,
        transform: 'translate(0 0) scale(0.82)',
      },
      '#FFD23F',
    ],
    ['rect', { x: 26, y: 26, width: 19, height: 19, rx: 5.5 }, '#FFFFFF'],
    ['rect', { x: 27.5, y: 27.5, width: 16, height: 16, rx: 4.5 }, '#0A66C2'],
    ['circle', { cx: 32.2, cy: 32, r: 1.5 }, '#FFFFFF'],
    ['path', { d: 'M32.2 35.4v4.4' }, '#FFFFFF', 'line'],
    [
      'path',
      { d: 'M35.8 39.8v-4.4m0 2c0-1.3 1-2.1 2.1-2.1s2.1.8 2.1 2.1v2.4' },
      '#FFFFFF',
      'line',
    ],
  ],
  'naics-code': [
    ['rect', { x: 7, y: 5, width: 15, height: 9, rx: 3 }, '#0E8F84'],
    ['rect', { x: 4, y: 9, width: 40, height: 32, rx: 7 }, '#14B8A6'],
    [
      'path',
      { d: 'M19.5 15.5l-2.6 19M29.5 15.5l-2.6 19M13 22h20M12 28h20' },
      '#FFFFFF',
      'thick',
    ],
    ['circle', { cx: 38, cy: 35, r: 3.6 }, '#FFD23F'],
  ],
  'stock-candles': [
    [
      'rect',
      { x: 5, y: 40, width: 38, height: 3, rx: 1.5 },
      ['--cl-base', '#252A57'],
    ],
    ['path', { d: 'M13 9v28' }, '#16A34A', 'line'],
    ['path', { d: 'M24 14v22' }, '#E03445', 'line'],
    ['path', { d: 'M35 5v26' }, '#16A34A', 'line'],
    ['rect', { x: 9, y: 15, width: 8, height: 16, rx: 2.4 }, '#21C45D'],
    ['rect', { x: 20, y: 19, width: 8, height: 11, rx: 2.4 }, '#FF4D5E'],
    ['rect', { x: 31, y: 9, width: 8, height: 17, rx: 2.4 }, '#21C45D'],
  ],
  'past-company': [
    [
      'rect',
      {
        x: 7,
        y: 5,
        width: 19,
        height: 37,
        rx: 4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#B44CE6',
    ],
    [
      'rect',
      {
        x: 23,
        y: 17,
        width: 18,
        height: 25,
        rx: 4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#F27BC9',
    ],
    [
      'path',
      {
        d: 'M12 11.5h6M12 17.5h6M12 23.5h6M12 29.5h6',
        transform: 'translate(0 0) scale(0.78)',
      },
      ['--cl-oncard', '#FFFFFF'],
      'line',
    ],
    [
      'path',
      {
        d: 'M28.3 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM28.3 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1z',
        transform: 'translate(0 0) scale(0.78)',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
    [
      'rect',
      {
        x: 29.5,
        y: 35,
        width: 5,
        height: 7,
        rx: 1.6,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFD23F',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF8A1F'],
    [
      'path',
      {
        d: 'M37 32.6v4.6l3.1 2',
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'past-title': [
    [
      'path',
      {
        d: 'M17 15v-4.5a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4V15',
        transform: 'translate(0 0) scale(0.78)',
      },
      '#8E5526',
      'thick',
    ],
    [
      'rect',
      {
        x: 4,
        y: 14,
        width: 40,
        height: 28,
        rx: 6,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#D08A45',
    ],
    [
      'rect',
      {
        x: 4,
        y: 25.5,
        width: 40,
        height: 3.4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#B8743A',
    ],
    [
      'rect',
      {
        x: 19.5,
        y: 22.7,
        width: 9,
        height: 9,
        rx: 2.4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFE3BF',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF8A1F'],
    [
      'path',
      {
        d: 'M37 32.6v4.6l3.1 2',
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'past-seniority': [
    [
      'rect',
      {
        x: 11.25,
        y: 3.65,
        width: 8.5,
        height: 23.7,
        rx: 2.4,
        transform: 'translate(0 0) scale(0.78) rotate(-27.6 15.5 15.5)',
      },
      '#7C5CFF',
    ],
    [
      'rect',
      {
        x: 28.25,
        y: 3.65,
        width: 8.5,
        height: 23.7,
        rx: 2.4,
        transform: 'translate(0 0) scale(0.78) rotate(27.6 32.5 15.5)',
      },
      '#A893FF',
    ],
    [
      'circle',
      { cx: 24, cy: 32, r: 12, transform: 'translate(0 0) scale(0.78)' },
      '#FFC23D',
    ],
    [
      'path',
      {
        d: 'M24 26.4L25.47 29.98L29.33 30.27L26.38 32.77L27.29 36.53L24 34.5L20.71 36.53L21.62 32.77L18.67 30.27L22.53 29.98Z',
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFFFFF',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF8A1F'],
    [
      'path',
      {
        d: 'M37 32.6v4.6l3.1 2',
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'past-department': [
    [
      'path',
      {
        d: 'M25 24h3.2M33 10.5h-1.7a2.8 2.8 0 0 0-2.8 2.8v21.4a2.8 2.8 0 0 0 2.8 2.8H33',
        transform: 'translate(0 0) scale(0.78)',
      },
      '#A8238F',
      'line',
    ],
    [
      'rect',
      {
        x: 31.5,
        y: 4.5,
        width: 12,
        height: 12,
        rx: 3.8,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#A6DC2C',
    ],
    [
      'rect',
      {
        x: 31.5,
        y: 31.5,
        width: 12,
        height: 12,
        rx: 3.8,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#A6DC2C',
    ],
    [
      'circle',
      { cx: 14.5, cy: 24, r: 10.5, transform: 'translate(0 0) scale(0.78)' },
      '#E23DBE',
    ],
    [
      'path',
      {
        d: 'M11.6 19.3c0-1.5 1.6-2.4 2.9-1.6l7.3 4.6c1.2.8 1.2 2.6 0 3.4l-7.3 4.6c-1.3.8-2.9-.1-2.9-1.6z',
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFFFFF',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF8A1F'],
    [
      'path',
      {
        d: 'M37 32.6v4.6l3.1 2',
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'hq-region': [
    [
      'circle',
      { cx: 24, cy: 24, r: 19.5, transform: 'translate(0 0) scale(0.78)' },
      '#4D7CFF',
    ],
    [
      'ellipse',
      {
        cx: 24,
        cy: 24,
        rx: 7.5,
        ry: 17,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFFFFF',
      'thick',
    ],
    [
      'path',
      { d: 'M7.5 24H40.5', transform: 'translate(0 0) scale(0.78)' },
      '#FFFFFF',
      'thick',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#B44CE6'],
    [
      'rect',
      {
        x: 34,
        y: 32.5,
        width: 6,
        height: 9,
        rx: 1.4,
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
    ],
    [
      'rect',
      {
        x: 35.6,
        y: 34.4,
        width: 2.8,
        height: 1.6,
        rx: 0.6,
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#B44CE6',
    ],
    [
      'rect',
      {
        x: 35.6,
        y: 37.4,
        width: 2.8,
        height: 1.6,
        rx: 0.6,
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#B44CE6',
    ],
  ],
  'title-ban': [
    [
      'path',
      {
        d: 'M17 15v-4.5a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4V15',
        transform: 'translate(0 0) scale(0.78)',
      },
      '#8E5526',
      'thick',
    ],
    [
      'rect',
      {
        x: 4,
        y: 14,
        width: 40,
        height: 28,
        rx: 6,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#D08A45',
    ],
    [
      'rect',
      {
        x: 4,
        y: 25.5,
        width: 40,
        height: 3.4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#B8743A',
    ],
    [
      'rect',
      {
        x: 19.5,
        y: 22.7,
        width: 9,
        height: 9,
        rx: 2.4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFE3BF',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF4D5E'],
    [
      'rect',
      {
        x: 32.4,
        y: 35.5,
        width: 9.2,
        height: 3,
        rx: 1.5,
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
    ],
  ],
  'company-ban': [
    [
      'rect',
      {
        x: 7,
        y: 5,
        width: 19,
        height: 37,
        rx: 4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#B44CE6',
    ],
    [
      'rect',
      {
        x: 23,
        y: 17,
        width: 18,
        height: 25,
        rx: 4,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#F27BC9',
    ],
    [
      'path',
      {
        d: 'M12 11.5h6M12 17.5h6M12 23.5h6M12 29.5h6',
        transform: 'translate(0 0) scale(0.78)',
      },
      ['--cl-oncard', '#FFFFFF'],
      'line',
    ],
    [
      'path',
      {
        d: 'M28.3 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 22.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM28.3 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1zM33.7 28.5h1.4a1.1 1.1 0 0 1 1.1 1.1v1.4a1.1 1.1 0 0 1 -1.1 1.1h-1.4a1.1 1.1 0 0 1 -1.1 -1.1v-1.4a1.1 1.1 0 0 1 1.1 -1.1z',
        transform: 'translate(0 0) scale(0.78)',
      },
      ['--cl-oncard', '#FFFFFF'],
    ],
    [
      'rect',
      {
        x: 29.5,
        y: 35,
        width: 5,
        height: 7,
        rx: 1.6,
        transform: 'translate(0 0) scale(0.78)',
      },
      '#FFD23F',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#FF4D5E'],
    [
      'rect',
      {
        x: 32.4,
        y: 35.5,
        width: 9.2,
        height: 3,
        rx: 1.5,
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
    ],
  ],
  'headcount-growth': [
    [
      'path',
      {
        d: 'M9.63 39.2L3.6 39.2A1.5 1.5 0 0 1 2.1 37.7A10.5 9.38 0 0 1 13.07 28.33A14.8 13.45 0 0 0 9.2 37.4A4 4 0 0 0 9.63 39.2Z',
        transform: 'translate(2 3) scale(0.76)',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M17.65 22.56A5.62 5.62 0 1 1 15.13 15.05A8.95 8.95 0 0 0 17.65 22.56Z',
        transform: 'translate(2 3) scale(0.76)',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M34.93 28.33A10.5 9.38 0 0 1 45.9 37.7A1.5 1.5 0 0 1 44.4 39.2L38.37 39.2A4 4 0 0 0 38.8 37.4A14.8 13.45 0 0 0 34.93 28.33Z',
        transform: 'translate(2 3) scale(0.76)',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M32.87 15.05A5.62 5.62 0 1 1 30.35 22.56A8.95 8.95 0 0 0 32.87 15.05Z',
        transform: 'translate(2 3) scale(0.76)',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M11.4 37.4A12.6 11.25 0 1 1 36.6 37.4A1.8 1.8 0 0 1 34.8 39.2L13.2 39.2A1.8 1.8 0 0 1 11.4 37.4Z',
        transform: 'translate(2 3) scale(0.76)',
      },
      '#7C5CFF',
    ],
    [
      'circle',
      { cx: 24, cy: 16.25, r: 6.75, transform: 'translate(2 3) scale(0.76)' },
      '#A893FF',
    ],
    ['circle', { cx: 36.5, cy: 36.5, r: 9.2 }, '#FFFFFF'],
    ['circle', { cx: 36.5, cy: 36.5, r: 7.6 }, '#21C45D'],
    [
      'path',
      {
        d: 'M37 41v-7.4M33.8 36.6l3.2-3.2 3.2 3.2',
        transform: 'translate(36.5 36.5) scale(0.9) translate(-37 -37)',
      },
      '#FFFFFF',
      'line',
    ],
  ],
  'box-check': [
    [
      'path',
      { d: 'M8.5 24.5l5.2-5.2 7 7L34.3 12.7l5.2 5.2-18.8 18.8z' },
      '#FFFFFF',
    ],
  ],
  'box-minus': [
    ['rect', { x: 10, y: 21, width: 28, height: 6, rx: 3 }, '#FFFFFF'],
  ],
  'salesforce-cloud': [
    ['circle', { cx: 15, cy: 27, r: 9 }, '#00A1E0'],
    ['circle', { cx: 24, cy: 19.5, r: 10.5 }, '#00A1E0'],
    ['circle', { cx: 34, cy: 24, r: 9.5 }, '#00A1E0'],
    ['rect', { x: 8, y: 25, width: 34, height: 12, rx: 6 }, '#00A1E0'],
    ['path', { d: 'M17.5 15.5a8 8 0 0 1 8-3.4' }, '#7FD8FF', 'line'],
  ],
  'hubspot-sprocket': [
    [
      'path',
      { d: 'M29 21V11.5M21 33.5L12.5 40M21.5 23L13.5 15.5' },
      '#FF7A59',
      'thick',
    ],
    ['circle', { cx: 29, cy: 9.5, r: 4 }, '#FF7A59'],
    ['circle', { cx: 11.5, cy: 41, r: 3.6 }, '#FF7A59'],
    ['circle', { cx: 12, cy: 14, r: 3.6 }, '#FF7A59'],
    ['circle', { cx: 29, cy: 30, r: 10 }, '#FF7A59'],
    ['circle', { cx: 29, cy: 30, r: 4.6 }, '#FFE0D6'],
  ],
  'spark-plain': [
    [
      'path',
      {
        d: 'M24 5c1.2 8.6 5.4 12.8 14 14c-8.6 1.2-12.8 5.4-14 14c-1.2-8.6-5.4-12.8-14-14c8.6-1.2 12.8-5.4 14-14z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M37 29c.6 4.2 2.6 6.2 6.8 6.8c-4.2.6-6.2 2.6-6.8 6.8c-.6-4.2-2.6-6.2-6.8-6.8c4.2-.6 6.2-2.6 6.8-6.8z',
      },
      '#FFC23D',
    ],
  ],
  'team-size': [
    ['circle', { cx: 12.5, cy: 13, r: 5.6 }, '#A893FF'],
    ['circle', { cx: 35.5, cy: 13, r: 5.6 }, '#A893FF'],
    [
      'path',
      {
        d: 'M3 33.5c0-6.3 4.3-10.5 9.5-10.5c3 0 5.6 1.3 7.3 3.5c-3 2.4-4.8 6-4.8 10.5v1H5a2 2 0 0 1-2-2z',
      },
      '#A893FF',
    ],
    [
      'path',
      {
        d: 'M45 33.5c0-6.3-4.3-10.5-9.5-10.5c-3 0-5.6 1.3-7.3 3.5c3 2.4 4.8 6 4.8 10.5v1H43a2 2 0 0 0 2-2z',
      },
      '#A893FF',
    ],
    ['circle', { cx: 24, cy: 17, r: 7.4 }, '#7C5CFF'],
    [
      'path',
      {
        d: 'M11.5 41.5c0-8 5.5-13.5 12.5-13.5s12.5 5.5 12.5 13.5a2 2 0 0 1-2 2h-21a2 2 0 0 1-2-2z',
      },
      '#7C5CFF',
    ],
    ['circle', { cx: 24, cy: 15.4, r: 2.2 }, '#FFFFFF', 'line'],
  ],
  investor: [
    ['circle', { cx: 19, cy: 13, r: 8.5 }, '#4D7CFF'],
    [
      'path',
      {
        d: 'M3.5 40c0-9 6.9-15.5 15.5-15.5S34.5 31 34.5 40a3 3 0 0 1-3 3h-25a3 3 0 0 1-3-3z',
      },
      '#4D7CFF',
    ],
    ['path', { d: 'M13 9.5a8 8 0 0 1 6-2.4' }, '#9DBBFF', 'line'],
    ['circle', { cx: 36, cy: 35.5, r: 10.5 }, '#FFFFFF'],
    ['circle', { cx: 36, cy: 37, r: 8.6 }, '#0E9F55'],
    ['circle', { cx: 36, cy: 35, r: 8.6 }, '#2BD46F'],
    ['circle', { cx: 36, cy: 35, r: 5.6 }, '#9DF0BF'],
    ['path', { d: 'M36 31.7v6.6' }, '#0E9F55', 'line'],
  ],
};

// Sequences' sidebar icon: Plain draws Clu with the plane, Candy its plane.
CANDY_ART['paper-plane-clu'] = CANDY_ART['paper-plane-top'];

// Filter rows with their own Plain glyph keep the Candy drawing they had.
CANDY_ART['city'] = CANDY_ART['house'];
CANDY_ART['state-province'] = CANDY_ART['globe'];
CANDY_ART['hq-location'] = CANDY_ART['building'];
CANDY_ART['hq-city'] = CANDY_ART['building'];
CANDY_ART['revenue'] = CANDY_ART['sack-dollar'];
CANDY_ART['job-change'] = CANDY_ART['clock-rotate-left'];
CANDY_ART['profile-refreshed'] = CANDY_ART['clock-rotate-left'];
CANDY_ART['team-mix'] = CANDY_ART['chart-user'];
